<template>
   <div class="client-dashboard-page">
      <!-- ── Top Organisation Card ── -->
      <div class="org-header-card card border-0 shadow-sm mb-4 p-4">
         <div class="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <!-- Left: Avatar, Name, Badges, Subtitle -->
            <div class="d-flex align-items-center gap-3">
               <div class="org-avatar-box d-flex align-items-center justify-content-center">
                  <i class="fas fa-building"></i>
               </div>
               <div>
                  <div class="d-flex align-items-center flex-wrap gap-2 mb-1">
                     <h2 class="org-header-title mb-0">{{ profileData.name || 'Loading...' }}</h2>
                     <div class="badge-container d-inline-flex align-items-center gap-1">
                        <span v-for="c in profileData.classifications" :key="c" class="org-pill-badge" :class="'org-pill-badge--' + c.toLowerCase().replace(/\s+/g, '-')">
                           {{ c }}
                        </span>
                        <span v-if="getVertexLeadName()" class="org-pill-badge org-pill-badge--owner" :title="'Vertex Lead: ' + getVertexLeadName()">
                           <i class="fas fa-user-tie me-1"></i> Vertex Lead: {{ getVertexLeadName() }}
                        </span>
                        <span v-if="profileData.is_merged" class="org-pill-badge org-pill-badge--merged" :title="'Merged into: ' + (profileData.merged_into_name || 'Primary Organisation')">
                           <i class="fas fa-code-branch me-1"></i> Merged to {{ profileData.merged_into_name || 'Primary Organisation' }}
                        </span>
                        <span v-if="profileData.isDeactivated" class="org-badge badge-deactivated org-pill-badge org-pill-badge--deactivated" style="background: #FEE2E2; color: #DC2626; border: 1px solid #FCA5A5;">
                           <i class="fas fa-ban me-1"></i> DEACTIVATED
                        </span>
                        <span v-if="profileData.isTestData" class="org-pill-badge org-pill-badge--test">Test</span>
                        <span v-if="profileData.isAuthSynced || profileData.hasAuthOrganisation == 1 || profileData.isAuthDeleted || profileData.isDeactivated || profileData.is_inactive"
                              :class="['org-pill-badge', (profileData.isDeactivated || profileData.is_inactive) ? 'org-pill-badge--synced-inactive' : (profileData.isAuthDeleted ? 'org-pill-badge--synced-deleted' : 'org-pill-badge--synced')]"
                              :title="(profileData.isDeactivated || profileData.is_inactive) ? 'Organisation is inactive in Auth' : (profileData.isAuthDeleted ? 'Deleted Org Data (Organisation not found on Auth)' : (isPastClient ? 'Linked to Auth Portal (Sync disabled for Past Clients)' : 'Synced with Auth Portal'))">
                           <i class="fas fa-sync-alt me-1"></i> Auth Synced
                        </span>
                        <span v-else-if="profileData.hasAuthOrganisation == 2" class="org-pill-badge org-pill-badge--marked" title="Marked for creation in Auth">
                           <i class="fas fa-clock me-1"></i> Marked for Creation
                        </span>
                     </div>
                  </div>
                  <div class="org-header-subtitle text-muted small d-flex align-items-center gap-2 flex-wrap">
                     <span>{{ profileData.abn ? profileData.abn : (profileData.phone ? profileData.phone : ('No ' + businessNumberShortLabel + ' Provided')) }}</span>
                     <span v-if="getDisplayFormerNames()" class="text-secondary d-inline-flex align-items-center">
                        <span class="text-muted me-1">&bull; <i class="fa-solid fa-history me-1"></i>Formerly:</span>
                        <strong class="text-dark">{{ getDisplayFormerNames() }}</strong>
                     </span>
                  </div>
               </div>
            </div>

            <!-- Right: Search Organisation Bar with Total Count & Back Button -->
            <div class="org-switcher-box">
               <label class="org-switcher-title mb-1">Switch Organisation ({{ totalOrganisationsCount || leads.length }})</label>
               <div class="d-flex align-items-center gap-2">
                  <router-link 
                     to="/clients/view-clients" 
                     class="btn btn-outline-secondary back-to-all-btn d-inline-flex align-items-center justify-content-center flex-shrink-0" 
                     title="Back to All Organisations"
                     style="border-radius: 8px; border: 1.5px solid #cbd5e1; height: 38px; width: 38px; color: #475569; font-weight: 600; padding: 0; background-color: #fff; text-decoration: none;"
                  >
                     <i class="fas fa-arrow-left"></i>
                  </router-link>
                  <div class="header-search-container m-0">
                     <div class="header-search-input-wrapper">
                        <i class="fas fa-search search-icon"></i>
                        <input 
                           type="text" 
                           v-model="searchQuery" 
                           class="header-search-input" 
                           placeholder="Search across all organisations..." 
                           @focus="showSearchDropdown = true"
                           @input="onSearchInput"
                        />
                        <button v-if="searchQuery" class="clear-search-btn" @click="clearSearchQuery" type="button">
                           <i class="fas fa-times"></i>
                        </button>
                     </div>
                     
                     <!-- Suggestions Dropdown overlay -->
                     <div v-if="showSearchDropdown && searchQuery && searchDropdownList.length > 0" class="header-search-dropdown">
                        <div 
                           v-for="org in searchDropdownList" 
                           :key="org.id || org.uuid" 
                           class="header-search-dropdown-item"
                           @mousedown="selectOrgFromDropdown(org)"
                        >
                           <div class="d-flex align-items-center justify-content-between">
                              <div>
                                 <span class="org-name-text">{{ org.organisation_name }}</span>
                                 <div v-if="org.former_names && org.former_names.length" class="text-muted small fst-italic" style="font-size: 0.72rem;">
                                    FKA: {{ org.former_names.join(', ') }}
                                 </div>
                              </div>
                              <div class="d-flex align-items-center gap-1">
                                 <span v-if="org.is_merged" class="org-pill-badge org-pill-badge--merged" style="font-size: 0.65rem; padding: 1px 6px;" :title="'Merged into: ' + (org.merged_into_name || 'Primary Organisation')">
                                    <i class="fas fa-code-branch me-1"></i> Merged to {{ org.merged_into_name || 'Primary Organisation' }}
                                 </span>
                                 <span v-if="org.is_deactivated || org.isDeactivated" class="org-badge badge-deactivated org-pill-badge org-pill-badge--deactivated" style="font-size: 0.65rem; padding: 1px 6px; background: #FEE2E2; color: #DC2626; border: 1px solid #FCA5A5;">
                                    <i class="fas fa-ban me-1"></i> Deactivated
                                 </span>
                                 <span v-if="org.is_test_data || org.isTestData" class="org-pill-badge org-pill-badge--test" style="font-size: 0.65rem; padding: 1px 6px;">Test</span>
                                 <span v-if="isPartner(org) && getOrgClassification(org) !== 'Partner'" class="org-pill-badge org-pill-badge--partner" style="font-size: 0.65rem; padding: 1px 6px;">Partner</span>
                                 <span v-if="isSupplier(org) && getOrgClassification(org) !== 'Supplier'" class="org-pill-badge org-pill-badge--supplier" style="font-size: 0.65rem; padding: 1px 6px;">Supplier</span>
                                 <span class="org-type-badge" :class="'org-type-badge--' + (getOrgClassification(org) || '').toLowerCase().replace(/\s+/g, '-')">{{ getOrgClassification(org) }}</span>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </div>

      <!-- ── Merged Organisation Alert Banner ── -->
      <div v-if="profileData.is_merged" class="alert d-flex align-items-center justify-content-between p-3 mb-4 rounded-3 shadow-sm" style="background-color: #f0fdfa; border: 1px solid #99f6e4; border-left: 5px solid #00768a;">
         <div class="d-flex align-items-center gap-3">
            <div class="d-inline-flex align-items-center justify-content-center rounded-circle" style="width: 44px; height: 44px; background-color: rgba(0, 118, 138, 0.12); color: #00768a; flex-shrink: 0;">
               <i class="fas fa-code-branch fa-lg"></i>
            </div>
            <div>
               <h6 class="alert-heading mb-1 fw-bold text-dark">This Organisation Has Been Merged</h6>
               <p class="mb-0 text-muted small">
                  Merged into
                  <a href="javascript:void(0)" @click="navigateToOrganisation(profileData.merged_into_uuid)" class="fw-bold text-decoration-underline" style="color: #00768a; cursor: pointer;">
                     {{ profileData.merged_into_name || 'Primary Organisation' }}
                  </a>
                  <span v-if="profileData.merged_at"> on {{ formatDate(profileData.merged_at) }}</span>
                  <span v-if="profileData.merged_by_name"> by {{ profileData.merged_by_name }}</span>.
                  <span v-if="profileData.merge_reason"><br/><strong>Reason:</strong> {{ profileData.merge_reason }}</span>
               </p>
            </div>
         </div>
         <div class="d-flex align-items-center gap-2">
            <button type="button" class="btn btn-sm btn-vertex-primary" @click="navigateToOrganisation(profileData.merged_into_uuid)">
               <i class="fas fa-external-link-alt me-1"></i> View Primary Organisation
            </button>
            <button type="button" class="btn btn-sm btn-vertex-outline" @click="openUnmergeModal(profileData)">
               <i class="fas fa-undo me-1"></i> Unmerge
            </button>
         </div>
      </div>

      <!-- ── Deactivated Organisation Alert Banner ── -->
      <div v-if="profileData.isDeactivated || profileData.is_deactivated" class="alert d-flex align-items-center justify-content-between p-3 mb-4 rounded-3 shadow-sm" style="background-color: #fef2f2; border: 1px solid #fecaca; border-left: 5px solid #ef4444;">
         <div class="d-flex align-items-center gap-3">
            <div class="d-inline-flex align-items-center justify-content-center rounded-circle" style="width: 44px; height: 44px; background-color: rgba(239, 68, 68, 0.12); color: #ef4444; flex-shrink: 0;">
               <i class="fas fa-ban fa-lg"></i>
            </div>
            <div>
               <h6 class="alert-heading mb-1 fw-bold text-dark">Deactivated Organisation</h6>
               <p class="mb-0 text-muted small">
                  Deactivated Organisation: This organisation has been deactivated and is hidden from active lists.
               </p>
            </div>
         </div>
         <div class="d-flex align-items-center gap-2">
            <button 
               type="button" 
               class="btn btn-sm text-white px-3 fw-semibold d-flex align-items-center gap-1" 
               style="background-color: #10b981; border-color: #10b981;" 
               :disabled="isRestoringOrg" 
               @click="confirmRestoreOrganisation"
            >
               <i class="fas" :class="isRestoringOrg ? 'fa-spinner fa-spin' : 'fa-undo'"></i>
               <span>{{ isRestoringOrg ? 'Restoring...' : 'Restore Organisation' }}</span>
            </button>
         </div>
      </div>

      <LoadingPage v-if="isLoading"></LoadingPage>

      <!-- ── Organisations Tabs Card (Matching All Organisations) ── -->
      <div class="ms-card mb-4">
         <div class="org-tabs-container">
            <div class="org-tabs-nav">
               <button 
                  @click="activeTab = 'profile'" 
                  class="org-tab-btn" 
                  :class="{ active: activeTab === 'profile' }"
               >
                  Profile
               </button>
               <button 
                  @click="activeTab = 'contacts'" 
                  class="org-tab-btn" 
                  :class="{ active: activeTab === 'contacts' }"
               >
                  Contacts
                  <span v-if="orgContacts && orgContacts.length" class="org-tab-counter-badge">
                     {{ orgContacts.length }}
                  </span>
               </button>
               <button 
                  v-if="canShowJobsBoard"
                  @click="activeTab = 'jobs_board'" 
                  class="org-tab-btn" 
                  :class="{ active: activeTab === 'jobs_board' }"
               >
                  Jobs Board
                  <span v-if="orgActiveJobs && orgActiveJobs.length" class="org-tab-counter-badge">
                     {{ orgActiveJobs.length }}
                  </span>
               </button>
               <!-- Merged Organisations tab hidden for the meantime -->
               <button 
                  v-if="false"
                  @click="activeTab = 'merged_organisations'" 
                  class="org-tab-btn" 
                  :class="{ active: activeTab === 'merged_organisations' }"
               >
                  Merged Organisations
                  <span v-if="mergedOrganisations && mergedOrganisations.length" class="org-tab-counter-badge">
                     {{ mergedOrganisations.length }}
                  </span>
               </button>
               <button 
                  @click="activeTab = 'timeline'" 
                  class="org-tab-btn" 
                  :class="{ active: activeTab === 'timeline' }"
               >
                  Activity Timeline
               </button>
            </div>

            <!-- Sub-header Meta Row on the right side of the tab bar -->
            <div class="d-flex align-items-center gap-3 flex-wrap py-2 text-muted small" style="font-size: 0.82rem;">
               <div>
                  <span class="text-secondary fw-semibold">Date Created:</span> {{ profileData.firstContactDate || 'Not Available' }}
               </div>
               <span class="text-muted opacity-50">•</span>
               <div>
                  <span class="text-secondary fw-semibold">Last synced:</span> {{ profileData.syncedAt ? formatSyncedDate(profileData.syncedAt) : 'Never' }}
               </div>
               <button 
                  v-if="!profileData.is_merged" 
                  type="button" 
                  class="btn btn-sm btn-vertex-outline d-flex align-items-center gap-1 ms-2" 
                  style="border-radius: 6px; font-size: 0.78rem; font-weight: 600; padding: 4px 10px;"
                  @click="openMergeModal" 
                  title="Merge this organisation into another"
               >
                  <i class="fas fa-code-branch"></i> Merge To...
               </button>
            </div>
         </div>
      </div>

      <!-- ── Tab Content ── -->
      <div class="tab-content-container">
         <!-- PROFILE TAB -->
         <div v-if="activeTab === 'profile'" class="profile-tab-content">
            <!-- 1. Organisation Details Card -->
            <div class="accordion-card card border-0 shadow-sm mb-3">
               <div class="accordion-header d-flex justify-content-between align-items-center p-3" @click="toggleSection('organisationDetails')">
                  <div class="d-flex align-items-center gap-2">
                     <i class="fas fa-building text-info"></i>
                     <span class="accordion-title">Organisation Details</span>
                  </div>

                  <div class="d-flex align-items-center gap-2" @click.stop>
                     <!-- Platform Action Icons -->
                     <button type="button" 
                             class="btn-accordion-icon btn-acc-auth" 
                             :class="{ 'is-linked': profileData.hasAuthOrganisation == 1 }" 
                             @click.stop="openInAuth"
                             :title="profileData.hasAuthOrganisation == 1 ? 'Open Organisation in Auth' : 'Please link to an Auth organisation first'">
                        <i class="fas fa-shield-alt"></i>
                     </button>

                     <button type="button" 
                             class="btn-accordion-icon btn-acc-xero" 
                             :class="{ 'is-linked': profileData.hasAuthOrganisation == 1 }" 
                             @click.stop="openInXero"
                             :title="profileData.hasAuthOrganisation == 1 ? 'Open Organisation in Xero' : 'Please link to an Auth organisation first to enable Xero'">
                        <i class="fas fa-file-invoice-dollar"></i>
                     </button>

                     <button
                        type="button"
                        class="btn-accordion-icon btn-acc-sync"
                        :class="{ 'is-linked': profileData.hasAuthOrganisation == 1 && !isPastClient && !profileData.isAuthDeleted && !profileData.is_merged, 'is-syncing': isSyncing }"
                        @click.stop="manualSyncFromAuth"
                        :disabled="isSyncing || profileData.hasAuthOrganisation != 1 || isPastClient || profileData.isAuthDeleted || profileData.is_merged"
                        :title="
                           profileData.isAuthDeleted
                              ? 'Cannot sync deleted organisation from Auth'
                              : profileData.is_merged
                                ? 'Cannot sync merged organisation'
                                : isPastClient
                                  ? 'Auth sync is disabled for Past Clients'
                                  : profileData.hasAuthOrganisation == 1
                                    ? isSyncing
                                       ? 'Syncing with Auth...'
                                       : 'Sync organisation from Auth'
                                    : 'Please link to an Auth organisation first to enable sync'
                        "
                     >
                        <i class="fas" :class="isSyncing ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
                     </button>

                     <!-- Expand/Collapse Chevron -->
                     <button type="button" class="btn-accordion-chevron ms-1" @click.stop="toggleSection('organisationDetails')">
                        <i class="fas" :class="sectionOpen.organisationDetails ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                     </button>
                  </div>
               </div>

               <div v-show="sectionOpen.organisationDetails" class="accordion-body p-4 pt-3 border-top">
                  <div class="row g-4">
                     <!-- Left Column -->
                     <div class="col-12 col-lg-5">
                        <div class="mb-3">
                           <label class="form-label">Organisation Name</label>
                           <div class="field-save-wrapper">
                              <input type="text" class="form-control" v-model="profileData.name" @input="debounceSave('Organisation Name', 'name')" :disabled="isLinkedToAuthOrg" />
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.name === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.name === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.name === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                            <small v-if="getDisplayFormerNames()" class="text-muted fst-italic d-block mt-1">
                               <i class="fa-solid fa-history me-1"></i>Formerly: <strong>{{ getDisplayFormerNames() }}</strong>
                            </small>
                        </div>

                        <div class="mb-3">
                           <label class="form-label">Organisation {{ businessNumberShortLabel }}</label>
                           <div class="field-save-wrapper">
                              <input type="text" class="form-control" v-model="profileData.abn" @input="debounceSave('Organisation ' + businessNumberShortLabel, 'abn')" :placeholder="'Enter ' + businessNumberShortLabel" :disabled="isLinkedToAuthOrg" />
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.abn === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.abn === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.abn === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>

                        <div class="mb-0">
                           <label class="form-label">Client Type</label>
                           <div class="d-flex align-items-center flex-wrap gap-3">
                              <select class="form-select form-select-sm" style="max-width: 180px;" v-model="primaryClassification" @change="saveClassificationsImmediate">
                                 <option value="Lead">Lead</option>
                                 <option value="Prospect">Prospect</option>
                                 <option value="Client">Client</option>
                                 <option value="Past Client">Past Client</option>
                              </select>

                              <div class="d-flex align-items-center gap-3">
                                 <label class="checkbox-label mb-0" for="class_Partner" @click.stop>
                                    <input type="checkbox" id="class_Partner" v-model="isPartnerClassification" @change="saveClassificationsImmediate" class="styled-checkbox" />
                                    <span class="checkbox-custom"></span>
                                    <span class="checkbox-text">Partner</span>
                                 </label>
                                 
                                 <label class="checkbox-label mb-0" for="class_Supplier" @click.stop>
                                    <input type="checkbox" id="class_Supplier" v-model="isSupplierClassification" @change="saveClassificationsImmediate" class="styled-checkbox" />
                                    <span class="checkbox-custom"></span>
                                    <span class="checkbox-text">Supplier</span>
                                 </label>

                                 <label class="checkbox-label mb-0" for="class_PastClient" @click.stop>
                                    <input type="checkbox" id="class_PastClient" :checked="primaryClassification === 'Past Client'" @change="togglePastClientClassification($event)" class="styled-checkbox" />
                                    <span class="checkbox-custom"></span>
                                    <span class="checkbox-text">Past Client</span>
                                 </label>

                                 <label class="checkbox-label mb-0" for="profileIsTestData" @click.stop>
                                    <input type="checkbox" id="profileIsTestData" v-model="profileData.isTestData" @change="saveClassificationsImmediate" class="styled-checkbox" />
                                    <span class="checkbox-custom"></span>
                                    <span class="checkbox-text">Test</span>
                                 </label>
                              </div>

                              <div class="field-status-indicator-static ms-1">
                                 <span v-if="fieldStates.classifications === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.classifications === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>
                     </div>

                     <!-- Right Column -->
                     <div class="col-12 col-lg-7 d-flex flex-column">
                        <div class="mb-0 d-flex flex-column flex-grow-1">
                           <label class="form-label">Company Background or Purpose</label>
                           <div class="field-save-wrapper d-flex flex-column flex-grow-1">
                              <div
                                 class="form-control company-background-display flex-grow-1"
                                 tabindex="0"
                                 role="button"
                                 title="Click to edit company background"
                                 @click="openCompanyBackgroundModal"
                                 @keydown.enter.prevent="openCompanyBackgroundModal"
                              >
                                 <div
                                    v-if="profileData.background && profileData.background.trim()"
                                    v-html="formatBackgroundToHtml(profileData.background)"
                                 ></div>
                                 <span v-else class="text-muted fst-italic">Click to add company background or purpose...</span>
                              </div>
                              <div class="field-status-indicator field-status-indicator--textarea">
                                 <span v-if="fieldStates.background === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.background === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.background === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            <!-- 2. Contact Information Card -->
            <div class="accordion-card card border-0 shadow-sm mb-3">
               <div class="accordion-header d-flex justify-content-between align-items-center p-3" @click="toggleSection('contactInformation')">
                  <div class="d-flex align-items-center gap-2">
                     <i class="fas fa-address-book text-info"></i>
                     <span class="accordion-title">Contact Information</span>
                  </div>

                  <button type="button" class="btn-accordion-chevron" @click.stop="toggleSection('contactInformation')">
                     <i class="fas" :class="sectionOpen.contactInformation ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                  </button>
               </div>

               <div v-show="sectionOpen.contactInformation" class="accordion-body p-4 pt-3 border-top">
                  <div class="row g-4">
                     <div class="col-12 col-lg-6">
                        <div class="mb-3">
                           <label class="form-label">Organisation Lead Name</label>
                           <div class="field-save-wrapper">
                              <input type="text" class="form-control" v-model="profileData.leadName" @input="debounceSave('Organisation Lead Name', 'leadName')" :disabled="isLinkedToAuthOrg" placeholder="Not Provided" />
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.leadName === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.leadName === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.leadName === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>

                        <div class="mb-3">
                           <label class="form-label">Organisation Lead Email</label>
                           <div class="field-save-wrapper">
                              <input type="email" class="form-control" v-model="profileData.org_lead_email" @input="debounceSave('Organisation Lead Email', 'org_lead_email')" :disabled="isLinkedToAuthOrg" placeholder="Not Provided" />
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.org_lead_email === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.org_lead_email === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.org_lead_email === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>
                     </div>

                     <div class="col-12 col-lg-6">
                        <div class="mb-3">
                           <label class="form-label">Organisation Phone Number</label>
                           <div class="field-save-wrapper">
                              <input type="text" class="form-control" v-model="profileData.phone" @input="debounceSave('Organisation Phone Number', 'phone')" :disabled="isLinkedToAuthOrg" placeholder="Not Provided" />
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.phone === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.phone === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.phone === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>

                     </div>
                  </div>

                  <!-- Organisation Addresses Section -->
                  <div class="mt-4 pt-3 border-top">
                     <div class="d-flex align-items-center gap-2 mb-3">
                        <i class="fas fa-map-marker-alt text-info"></i>
                        <h6 class="mb-0 fw-bold text-dark" style="font-size: 14.5px;">Organisation Addresses</h6>
                     </div>

                     <div class="row g-4">
                        <!-- 1. Organisation Address (Primary) -->
                        <div class="col-12 col-lg-6">
                           <div class="mb-3">
                              <label class="form-label d-flex align-items-center gap-1">
                                 <i class="fas fa-building text-muted"></i>
                                 <span>Organisation Address <small class="text-muted">(Primary)</small></span>
                              </label>
                              <div class="field-save-wrapper">
                                 <input type="text" class="form-control" v-model="combinedAddress" @input="debounceSave('Organisation Address', 'address')" :disabled="isLinkedToAuthOrg" placeholder="Not Provided" />
                                 <div class="field-status-indicator">
                                    <span v-if="fieldStates.address === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.address === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.address === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                              </div>
                           </div>
                        </div>

                        <!-- 2. Delivery Address -->
                        <div class="col-12 col-lg-6">
                           <div class="mb-3">
                              <label class="form-label d-flex align-items-center gap-1">
                                 <i class="fas fa-truck text-muted"></i>
                                 <span>Delivery Address</span>
                              </label>
                              <div class="field-save-wrapper">
                                 <input type="text" class="form-control" v-model="combinedDeliveryAddress" @input="debounceSave('Delivery Address', 'delivery_address')" :disabled="isLinkedToAuthOrg" placeholder="Not Provided" />
                                 <div class="field-status-indicator">
                                    <span v-if="fieldStates.delivery_address === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.delivery_address === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.delivery_address === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                              </div>
                           </div>
                        </div>

                        <!-- 3. Billing Address & Billing Email -->
                        <div class="col-12 col-lg-6">
                           <div class="mb-3">
                              <label class="form-label d-flex align-items-center gap-1">
                                 <i class="fas fa-file-invoice-dollar text-muted"></i>
                                 <span>Billing Address</span>
                              </label>
                              <div class="field-save-wrapper">
                                 <input type="text" class="form-control" v-model="combinedBillingAddress" @input="debounceSave('Billing Address', 'billing_address')" :disabled="isLinkedToAuthOrg" placeholder="Not Provided" />
                                 <div class="field-status-indicator">
                                    <span v-if="fieldStates.billing_address === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.billing_address === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.billing_address === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                              </div>
                           </div>
                           <div class="mb-3">
                              <label class="form-label d-flex align-items-center gap-1">
                                 <i class="fas fa-envelope-open-dollar text-muted"></i>
                                 <span>Billing Email</span>
                              </label>
                              <div class="field-save-wrapper">
                                 <input type="email" class="form-control" v-model="profileData.billing_email" @input="debounceSave('Billing Email', 'billing_email')" :disabled="isLinkedToAuthOrg" placeholder="Not Provided" />
                                 <div class="field-status-indicator">
                                    <span v-if="fieldStates.billing_email === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.billing_email === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.billing_email === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                              </div>
                           </div>
                        </div>

                        <!-- 4. Address for Service of Notices & Service Email -->
                        <div class="col-12 col-lg-6">
                           <div class="mb-3">
                              <label class="form-label d-flex align-items-center gap-1">
                                 <i class="fas fa-envelope-open-text text-muted"></i>
                                 <span>Address for Service of Notices</span>
                              </label>
                              <div class="field-save-wrapper">
                                 <input type="text" class="form-control" v-model="combinedServiceAddress" @input="debounceSave('Address for Service of Notices', 'service_address')" :disabled="isLinkedToAuthOrg" placeholder="Not Provided" />
                                 <div class="field-status-indicator">
                                    <span v-if="fieldStates.service_address === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.service_address === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.service_address === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                              </div>
                           </div>
                           <div class="mb-3">
                              <label class="form-label d-flex align-items-center gap-1">
                                 <i class="fas fa-envelope text-muted"></i>
                                 <span>Service of Notices Email</span>
                              </label>
                              <div class="field-save-wrapper">
                                 <input type="email" class="form-control" v-model="profileData.service_email" @input="debounceSave('Service of Notices Email', 'service_email')" :disabled="isLinkedToAuthOrg" placeholder="Not Provided" />
                                 <div class="field-status-indicator">
                                    <span v-if="fieldStates.service_email === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.service_email === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.service_email === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>

                  <!-- Key Stakeholders & Contacts Preview Section -->
                  <div class="mt-4 pt-3 border-top">
                     <div class="d-flex justify-content-between align-items-center mb-3">
                        <div class="d-flex align-items-center gap-2">
                           <i class="fas fa-users text-info"></i>
                           <h6 class="mb-0 fw-bold text-dark" style="font-size: 14.5px;">Key Stakeholders & Contacts</h6>
                           <span v-if="orgContacts.length" class="badge bg-light text-secondary rounded-pill border px-2 py-1" style="font-size: 11px;">
                              {{ orgContacts.length }}
                           </span>
                        </div>
                        <button
                           v-if="orgContacts.length > 0"
                           type="button"
                           class="btn btn-sm btn-link text-info p-0 text-decoration-none fw-semibold d-flex align-items-center gap-1"
                           @click="activeTab = 'contacts'"
                        >
                           <span>View all {{ orgContacts.length }} in Contacts tab</span>
                           <i class="fas fa-arrow-right small"></i>
                        </button>
                     </div>

                     <!-- Mini-grid preview of top contacts -->
                     <div v-if="isContactsLoading" class="text-center py-3 text-muted small">
                        <i class="fas fa-spinner fa-spin me-1 text-info"></i> Loading contacts...
                     </div>
                     <div v-else-if="orgContacts.length === 0" class="p-3 rounded bg-light border text-center text-muted small">
                        <i class="fas fa-user-slash me-1"></i> No contacts found. Link this organisation with Auth or switch to the Contacts tab to sync.
                     </div>
                     <div v-else class="row g-3">
                        <div
                           v-for="contact in previewContacts"
                           :key="contact.id || contact.uuid || contact.email"
                           class="col-12 col-md-6 col-xl-4"
                        >
                           <div class="p-3 rounded bg-light border border-light h-100 contact-preview-mini-card">
                              <div class="d-flex align-items-start justify-content-between gap-2 mb-2">
                                 <div class="d-flex align-items-center gap-2 text-truncate" style="min-width: 0;">
                                    <div class="contact-mini-avatar" :class="'avatar-' + (contact.contact_type || 'default').toLowerCase()">
                                       {{ getContactInitials(contact.first_name, contact.last_name, contact.email) }}
                                    </div>
                                    <div class="text-truncate">
                                       <div class="fw-bold text-dark text-truncate" style="font-size: 13.5px;">
                                          {{ (contact.first_name || '') + ' ' + (contact.last_name || '') }}
                                       </div>
                                       <div class="text-muted small text-truncate" style="font-size: 11.5px;">
                                          {{ contact.role || 'Contact' }}
                                       </div>
                                    </div>
                                 </div>
                                  <div class="d-flex align-items-center flex-shrink-0 ms-2" style="gap: 6px;">
                                     <span
                                        v-if="contact.contact_type"
                                        class="badge rounded-pill text-uppercase px-2 py-1 contact-badge-item"
                                        :class="getContactTypeBadgeClass(contact.contact_type)"
                                        style="font-size: 9.5px; font-weight: 700;"
                                     >
                                        {{ contact.contact_type }}
                                     </span>
                                     <span
                                        v-if="contact.is_primary && (!contact.contact_type || contact.contact_type.toLowerCase() !== 'primary')"
                                        class="badge rounded-pill px-2 py-1 contact-badge-item contact-type-badge-primary"
                                        style="font-size: 9px; font-weight: 600;"
                                     >
                                        ★ Primary
                                     </span>
                                  </div>
                              </div>
                              <div class="small text-muted d-flex flex-column gap-1 pt-2 border-top" style="font-size: 12px;">
                                 <div v-if="contact.email" class="text-truncate d-flex align-items-center justify-content-between">
                                    <a :href="'mailto:' + contact.email" class="text-secondary text-decoration-none text-truncate" :title="contact.email">
                                       <i class="fas fa-envelope text-info me-1"></i>{{ contact.email }}
                                    </a>
                                    <button
                                       type="button"
                                       class="btn btn-sm btn-link text-muted p-0 ms-1 copy-email-btn"
                                       @click.stop="copyContactEmail(contact.email)"
                                       :title="copiedContactEmail === contact.email ? 'Copied!' : 'Copy Email'"
                                    >
                                       <i :class="copiedContactEmail === contact.email ? 'fas fa-check text-success' : 'far fa-copy'"></i>
                                    </button>
                                 </div>
                                 <div v-if="contact.phone_number" class="text-truncate">
                                    <a :href="'tel:' + contact.phone_number" class="text-secondary text-decoration-none">
                                       <i class="fas fa-phone text-info me-1"></i>{{ contact.phone_number }}
                                    </a>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            <!-- 3. Relationships Card -->
            <div class="accordion-card card border-0 shadow-sm mb-3">
               <div class="accordion-header d-flex justify-content-between align-items-center p-3" @click="toggleSection('relationships')">
                  <div class="d-flex align-items-center gap-2">
                     <i class="fas fa-network-wired text-info"></i>
                     <span class="accordion-title">Relationships</span>
                  </div>

                  <div class="d-flex align-items-center gap-2" @click.stop>
                     <span v-if="profileData.isMspManaged && profileData.mspUuid" class="rel-header-badge rel-badge-msp">
                        MSP - {{ getMspName(profileData.mspUuid) }}
                     </span>
                     <span v-if="profileData.isPartnerReferred && profileData.partnerUuid" class="rel-header-badge rel-badge-partner">
                        Partner Referred - {{ getPartnerName(profileData.partnerUuid) }}
                     </span>
                     <span v-if="profileData.isEcosystem && profileData.ecosystemUuid" class="rel-header-badge rel-badge-ecosystem">
                        Ecosystem - {{ getEcosystemName(profileData.ecosystemUuid) }}
                     </span>

                     <button type="button" class="btn-accordion-chevron ms-1" @click.stop="toggleSection('relationships')">
                        <i class="fas" :class="sectionOpen.relationships ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                     </button>
                  </div>
               </div>

               <div v-show="sectionOpen.relationships" class="accordion-body p-4 pt-3 border-top">
                  <div class="row g-3">
                     <!-- MSP Managed Section -->
                     <div class="col-12 col-lg-4">
                        <div class="p-3 rounded bg-light border border-light h-100">
                           <div class="d-flex justify-content-between align-items-center mb-2">
                              <span class="form-label mb-0">
                                 <i class="fas fa-network-wired me-2 text-primary"></i>
                                 <strong>MSP Managed</strong>
                              </span>
                              <div class="d-flex align-items-center gap-2">
                                 <div class="field-status-indicator position-static">
                                    <span v-if="fieldStates.isMspManaged === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.isMspManaged === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.isMspManaged === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                                 <label class="switch-toggle mb-0">
                                    <input type="checkbox" v-model="profileData.isMspManaged" @change="debounceSave('MSP Managed Status', 'isMspManaged')" />
                                    <span class="switch-slider"></span>
                                 </label>
                              </div>
                           </div>
                           <div v-if="profileData.isMspManaged" class="ps-2 mt-2 border-start border-primary border-2 animate__animated animate__fadeIn">
                              <label class="form-label text-muted small mb-1">Select MSP</label>
                              <div class="field-save-wrapper">
                                 <select class="form-select form-select-sm" v-model="profileData.mspUuid" @change="debounceSave('MSP Selection', 'mspUuid')">
                                    <option value="">- Select MSP -</option>
                                    <option v-for="opt in mspOptions" :value="opt.id" :key="opt.id">{{ opt.display_name }}</option>
                                 </select>
                                 <div class="field-status-indicator">
                                    <span v-if="fieldStates.mspUuid === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.mspUuid === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.mspUuid === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>

                     <!-- Partner Referred Section -->
                     <div class="col-12 col-lg-4">
                        <div class="p-3 rounded bg-light border border-light h-100">
                           <div class="d-flex justify-content-between align-items-center mb-2">
                              <span class="form-label mb-0">
                                 <i class="fas fa-handshake me-2 text-success"></i>
                                 <strong>Partner Referred</strong>
                              </span>
                              <div class="d-flex align-items-center gap-2">
                                 <div class="field-status-indicator position-static">
                                    <span v-if="fieldStates.isPartnerReferred === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.isPartnerReferred === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.isPartnerReferred === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                                 <label class="switch-toggle mb-0">
                                    <input type="checkbox" v-model="profileData.isPartnerReferred" @change="debounceSave('Partner Referred Status', 'isPartnerReferred')" />
                                    <span class="switch-slider"></span>
                                 </label>
                              </div>
                           </div>
                           <div v-if="profileData.isPartnerReferred" class="ps-2 mt-2 border-start border-success border-2 animate__animated animate__fadeIn">
                              <label class="form-label text-muted small mb-1">Select Partner</label>
                              <div class="field-save-wrapper">
                                 <select class="form-select form-select-sm" v-model="profileData.partnerUuid" @change="debounceSave('Partner Selection', 'partnerUuid')">
                                    <option value="">- Select Partner -</option>
                                    <option v-for="opt in partnerOptions" :value="opt.id" :key="opt.id">{{ opt.display_name }}</option>
                                 </select>
                                 <div class="field-status-indicator">
                                    <span v-if="fieldStates.partnerUuid === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.partnerUuid === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.partnerUuid === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>

                     <!-- Ecosystem Section -->
                     <div class="col-12 col-lg-4">
                        <div class="p-3 rounded bg-light border border-light h-100">
                           <div class="d-flex justify-content-between align-items-center mb-2">
                              <span class="form-label mb-0">
                                 <i class="fas fa-globe me-2 text-info"></i>
                                 <strong>Ecosystem</strong>
                              </span>
                              <div class="d-flex align-items-center gap-2">
                                 <div class="field-status-indicator position-static">
                                    <span v-if="fieldStates.isEcosystem === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.isEcosystem === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.isEcosystem === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                                 <label class="switch-toggle mb-0">
                                    <input type="checkbox" v-model="profileData.isEcosystem" @change="debounceSave('Ecosystem Status', 'isEcosystem')" />
                                    <span class="switch-slider"></span>
                                 </label>
                              </div>
                           </div>
                           <div v-if="profileData.isEcosystem" class="ps-2 mt-2 border-start border-info border-2 animate__animated animate__fadeIn">
                              <label class="form-label text-muted small mb-1">Select Ecosystem</label>
                              <div class="field-save-wrapper">
                                 <select class="form-select form-select-sm" v-model="profileData.ecosystemUuid" @change="debounceSave('Ecosystem Selection', 'ecosystemUuid')">
                                    <option value="">- Select Ecosystem -</option>
                                    <option v-for="opt in ecosystemOptions" :value="opt.id" :key="opt.id">{{ opt.display_name }}</option>
                                 </select>
                                 <div class="field-status-indicator">
                                    <span v-if="fieldStates.ecosystemUuid === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.ecosystemUuid === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.ecosystemUuid === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            <!-- 4. Account Information Card -->
            <div class="accordion-card card border-0 shadow-sm mb-3">
               <div class="accordion-header d-flex justify-content-between align-items-center p-3" @click="toggleSection('accountInformation')">
                  <div class="d-flex align-items-center gap-2">
                     <i class="fas fa-id-card text-info"></i>
                     <span class="accordion-title">Account Information</span>
                  </div>

                  <button type="button" class="btn-accordion-chevron" @click.stop="toggleSection('accountInformation')">
                     <i class="fas" :class="sectionOpen.accountInformation ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                  </button>
               </div>

               <div v-show="sectionOpen.accountInformation" class="accordion-body p-4 pt-3 border-top">
                  <div class="row g-4">
                     <div class="col-12 col-lg-6">
                        <div class="mb-3">
                           <label class="form-label">Vertex BDM</label>
                           <div class="field-save-wrapper">
                              <select class="form-select" v-model="profileData.assigned_user_id" @change="debounceSave('Vertex BDM', 'assigned_user_id')">
                                 <option :value="0">- No BDM Assigned -</option>
                                 <option v-for="bdm in bdms" :value="bdm.auth_user_id" :key="bdm.db_id">
                                     {{ bdm.name || (bdm.first_name + ' ' + bdm.last_name).trim() || bdm.email }}{{ bdm.email && (bdm.name || bdm.first_name || bdm.last_name) ? ` (${bdm.email})` : '' }}
                                 </option>
                              </select>
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.assigned_user_id === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.assigned_user_id === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.assigned_user_id === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>

                        <div class="mb-3" v-if="primaryClassification === 'Client' || primaryClassification === 'Past Client'">
                           <label class="form-label">Vertex Organisation Lead</label>
                           <div class="field-save-wrapper">
                              <select class="form-select" v-model="profileData.vertex_org_manager_id" @change="debounceSave('Vertex Organisation Lead', 'vertex_org_manager_id')">
                                 <option :value="0">- No Lead Assigned -</option>
                                 <option v-for="user in vertexUsers" :value="user.id" :key="'lead_' + user.id">
                                    {{ user.name || (user.first_name + ' ' + user.last_name).trim() || user.email }}{{ user.email && (user.name || user.first_name || user.last_name) ? ` (${user.email})` : '' }}
                                 </option>
                              </select>
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.vertex_org_manager_id === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.vertex_org_manager_id === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.vertex_org_manager_id === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>

                        <div class="mb-3">
                           <label class="form-label">Organisation Currency</label>
                           <div class="field-save-wrapper">
                              <input type="text" class="form-control" v-model="profileData.currency" @input="debounceSave('Organisation Currency', 'currency')" />
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.currency === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.currency === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.currency === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>
                     </div>

                     <div class="col-12 col-lg-6">
                        <!-- Organisation Management -->
                        <div class="mb-3">
                           <label class="form-label">Organisation Management</label>
                           <div class="p-3 bg-light rounded border border-light">
                              <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                                  <div class="d-flex align-items-center gap-2">
                                     <span class="text-muted small fw-semibold">Auth Status:</span>
                                     <span v-if="profileData.isAuthSynced || profileData.hasAuthOrganisation == 1 || profileData.isAuthDeleted"
                                           :class="['badge text-white', profileData.isAuthDeleted ? 'bg-danger' : 'bg-success']"
                                           :title="profileData.isAuthDeleted ? 'Deleted Org Data (Organisation not found on Auth)' : (isPastClient ? 'Linked to Auth Portal (Sync disabled for Past Clients)' : 'Synced with Auth Portal')">
                                        <i class="fas fa-sync-alt me-1"></i> Auth Synced
                                     </span>
                                     <span v-else-if="profileData.hasAuthOrganisation == 2" class="badge bg-primary text-white">
                                        <i class="fas fa-clock me-1"></i> Marked for Creation
                                     </span>
                                     <span v-else class="badge bg-secondary text-white">
                                        Unlinked
                                     </span>
                                  </div>

                                  <div>
                                     <button v-if="!isPastClient && (profileData.hasAuthOrganisation == 0 || profileData.hasAuthOrganisation == null)"
                                             class="btn btn-sm btn-outline-primary"
                                             title="Mark for Auth Creation"
                                             @click="openMarkAuthModal">
                                        <i class="fas fa-clock me-1"></i> Mark for Creation
                                     </button>
                                     <button v-else-if="profileData.hasAuthOrganisation == 2"
                                             class="btn btn-sm btn-outline-danger"
                                             title="Unmark from Auth Creation"
                                             @click="openUnmarkAuthModal">
                                        <i class="fas fa-times me-1"></i> Unmark from Auth Creation
                                     </button>
                                  </div>
                              </div>

                              <div class="d-flex gap-2">
                                 <button v-if="profileData.isTestData && !isDirty('isTestData')" type="button" class="btn btn-danger btn-sm px-3 d-flex align-items-center gap-2" @click="openDeleteOrgModal" title="Delete Organisation">
                                    <i class="fas fa-trash-alt"></i> Delete Organisation
                                 </button>
                                 <button v-else-if="profileData.isDeactivated || profileData.is_deactivated" type="button" class="btn btn-success btn-sm px-3 d-flex align-items-center gap-2 text-white btn-restore-org" :disabled="isRestoringOrg" @click="confirmRestoreOrganisation" title="Restore Organisation" style="background-color: #10b981; border-color: #10b981;">
                                    <i class="fas" :class="isRestoringOrg ? 'fa-spinner fa-spin' : 'fa-undo'"></i> {{ isRestoringOrg ? 'Restoring...' : 'Restore Organisation' }}
                                 </button>
                                 <button v-else-if="!profileData.isTestData && !isDirty('isTestData')" type="button" class="btn btn-warning btn-sm px-3 d-flex align-items-center gap-2 text-white btn-delete-org" @click="confirmDeleteOrganisation" title="Deactivate Organisation" style="background-color: #f59e0b; border-color: #f59e0b;">
                                    <i class="fas fa-ban"></i> Deactivate Organisation
                                 </button>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            <!-- 5. Supplier & Partner Governance & Assurance Card -->
            <div v-if="isSupplierOrPartner" class="accordion-card card border-0 shadow-sm mb-3">
               <div class="accordion-header d-flex justify-content-between align-items-center p-3" @click="toggleSection('supplierGovernance')">
                  <div class="d-flex align-items-center gap-2">
                     <i class="fas fa-shield-alt text-info"></i>
                     <span class="accordion-title">Supplier & Partner Governance & Assurance</span>
                     <span class="badge rounded-pill bg-light text-primary border ms-2 px-3 py-1" style="font-size: 0.72rem; font-weight: 600;">
                        {{ isSupplierClassification && isPartnerClassification ? 'Supplier & Partner' : (isSupplierClassification ? 'Supplier' : 'Partner') }}
                     </span>
                  </div>

                  <button type="button" class="btn-accordion-chevron" @click.stop="toggleSection('supplierGovernance')">
                     <i class="fas" :class="sectionOpen.supplierGovernance ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                  </button>
               </div>

               <div v-show="sectionOpen.supplierGovernance" class="accordion-body p-4 pt-3 border-top">
                  <div class="row g-4">
                     <!-- Section 1: Self-Declaration & Code of Conduct (Left Column) -->
                     <div class="col-12 col-lg-6">
                        <div class="mb-3">
                           <label class="form-label mb-1">Supplier and Partner Diversity Self-Declaration Form Completed</label>
                           <div class="field-save-wrapper">
                              <VueMultiselect
                                 v-model="diversityFormCompletedSelected"
                                 :options="booleanYesNoOptions"
                                 label="label"
                                 track-by="value"
                                 :searchable="false"
                                 :allow-empty="false"
                                 :show-labels="false"
                                 :close-on-select="true"
                                 placeholder="Select"
                                 class="vertex-comm-multiselect"
                                 @select="opt => { diversityFormCompletedSelected = opt; }"
                                 @update:model-value="opt => { diversityFormCompletedSelected = opt; }"
                              >
                              </VueMultiselect>
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.diversity_form_completed === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.diversity_form_completed === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.diversity_form_completed === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>

                        <div class="mb-3">
                           <label class="form-label mb-1">Supplier Code of Conduct - Date Accepted</label>
                           <div class="field-save-wrapper">
                              <input type="date" class="form-control" v-model="profileData.code_of_conduct_accepted_date" :max="todayDateString" @change="onCodeOfConductDateChange" />
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.code_of_conduct_accepted_date === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.code_of_conduct_accepted_date === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.code_of_conduct_accepted_date === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>

                        <div class="mb-3">
                           <label class="form-label mb-1">Independent Private Business</label>
                           <div class="field-save-wrapper">
                              <VueMultiselect
                                 v-model="isIndependentPrivateBusinessSelected"
                                 :options="booleanYesNoOptions"
                                 label="label"
                                 track-by="value"
                                 :searchable="false"
                                 :allow-empty="false"
                                 :show-labels="false"
                                 :close-on-select="true"
                                 placeholder="Select"
                                 class="vertex-comm-multiselect"
                                 @select="opt => { isIndependentPrivateBusinessSelected = opt; }"
                                 @update:model-value="opt => { isIndependentPrivateBusinessSelected = opt; }"
                              >
                              </VueMultiselect>
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.is_independent_private_business === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.is_independent_private_business === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.is_independent_private_business === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>
                     </div>

                     <!-- Section 2: Ratings & Other Certifications (Right Column) -->
                     <div class="col-12 col-lg-6">
                        <div class="mb-3">
                           <label class="form-label d-flex align-items-center gap-2 mb-1">
                              <span>Security Risk Rating</span>
                              <span v-if="profileData.security_risk_rating === 'Low'" class="badge rounded-pill py-1 px-2.5" style="font-size: 0.72rem; font-weight: 600; background-color: #dcfce7; color: #15803d; border: 1px solid #bbf7d0;">
                                 <i class="fas fa-shield-alt me-1"></i> Low (Optimal)
                              </span>
                              <span v-else-if="profileData.security_risk_rating === 'Medium'" class="badge rounded-pill py-1 px-2.5" style="font-size: 0.72rem; font-weight: 600; background-color: #fef3c7; color: #b45309; border: 1px solid #fde68a;">
                                 <i class="fas fa-exclamation-triangle me-1"></i> Medium
                              </span>
                              <span v-else-if="profileData.security_risk_rating === 'High'" class="badge rounded-pill py-1 px-2.5" style="font-size: 0.72rem; font-weight: 600; background-color: #fee2e2; color: #b91c1c; border: 1px solid #fecaca;">
                                 <i class="fas fa-radiation me-1"></i> High
                              </span>
                           </label>
                           <div class="field-save-wrapper">
                              <VueMultiselect
                                 v-model="profileData.security_risk_rating"
                                 :options="ratingOptions"
                                 :searchable="false"
                                 :allow-empty="true"
                                 :show-labels="false"
                                 :close-on-select="true"
                                 placeholder="- Select Rating -"
                                 class="vertex-comm-multiselect"
                                 @select="val => { profileData.security_risk_rating = val; debounceSave('Security Risk Rating', 'security_risk_rating'); }"
                                 @remove="() => { profileData.security_risk_rating = ''; debounceSave('Security Risk Rating', 'security_risk_rating'); }"
                                 @update:model-value="val => { profileData.security_risk_rating = val || ''; debounceSave('Security Risk Rating', 'security_risk_rating'); }"
                              >
                              </VueMultiselect>
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.security_risk_rating === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.security_risk_rating === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.security_risk_rating === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>

                        <div class="mb-3">
                           <label class="form-label d-flex align-items-center gap-2 mb-1">
                              <span>ESG Practices Rating</span>
                              <span v-if="profileData.esg_practices_rating === 'High'" class="badge rounded-pill py-1 px-2.5" style="font-size: 0.72rem; font-weight: 600; background-color: #dcfce7; color: #15803d; border: 1px solid #bbf7d0;">
                                 <i class="fas fa-leaf me-1"></i> High (Optimal)
                              </span>
                              <span v-else-if="profileData.esg_practices_rating === 'Medium'" class="badge rounded-pill py-1 px-2.5" style="font-size: 0.72rem; font-weight: 600; background-color: #fef3c7; color: #b45309; border: 1px solid #fde68a;">
                                 <i class="fas fa-leaf me-1"></i> Medium
                              </span>
                              <span v-else-if="profileData.esg_practices_rating === 'Low'" class="badge rounded-pill py-1 px-2.5" style="font-size: 0.72rem; font-weight: 600; background-color: #f1f5f9; color: #475569; border: 1px solid #cbd5e1;">
                                 <i class="fas fa-leaf me-1"></i> Low
                              </span>
                           </label>
                           <div class="field-save-wrapper">
                              <VueMultiselect
                                 v-model="profileData.esg_practices_rating"
                                 :options="ratingOptions"
                                 :searchable="false"
                                 :allow-empty="true"
                                 :show-labels="false"
                                 :close-on-select="true"
                                 placeholder="- Select Rating -"
                                 class="vertex-comm-multiselect"
                                 @select="val => { profileData.esg_practices_rating = val; debounceSave('ESG Practices Rating', 'esg_practices_rating'); }"
                                 @remove="() => { profileData.esg_practices_rating = ''; debounceSave('ESG Practices Rating', 'esg_practices_rating'); }"
                                 @update:model-value="val => { profileData.esg_practices_rating = val || ''; debounceSave('ESG Practices Rating', 'esg_practices_rating'); }"
                              >
                              </VueMultiselect>
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.esg_practices_rating === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.esg_practices_rating === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.esg_practices_rating === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>

                        <!-- Other Certifications in top section -->
                        <div class="mb-3">
                           <label class="form-label mb-1">Other Certifications</label>
                           <div class="field-save-wrapper">
                              <input type="text" class="form-control" placeholder="e.g. ISO 22301, SOC 1, industry-specific accreditations..." v-model="profileData.certifications_other" @input="debounceSave('Other Certifications', 'certifications_other')" />
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.certifications_other === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.certifications_other === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.certifications_other === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>
                     </div>

                     <!-- Divider -->
                     <div class="col-12"><hr class="my-2 text-muted opacity-25" /></div>

                     <!-- Section 3: Formal Written Policies -->
                     <div class="col-12 col-lg-6">
                        <div class="mb-3">
                           <div class="d-flex justify-content-between align-items-center mb-2">
                              <label class="form-label mb-0 fw-semibold">
                                 <i class="fas fa-file-contract text-primary me-1"></i> Formal Written Policies
                              </label>
                              <div class="field-status-indicator-static">
                                 <span v-if="fieldStates.formal_written_policies === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.formal_written_policies === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.formal_written_policies === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                           <small class="text-muted d-block mb-3">Select all formal policies adopted by the organisation:</small>

                           <div class="d-flex flex-column gap-2 p-3 bg-light rounded border border-light">
                              <label v-for="(policy, idx) in formalPolicyOptions" :key="policy" class="checkbox-label mb-0 py-1" :for="'formal_policy_' + idx" @click.stop>
                                 <input type="checkbox" :id="'formal_policy_' + idx" :checked="profileData.formal_written_policies && profileData.formal_written_policies.includes(policy)" @change="toggleFormalPolicy(policy)" class="styled-checkbox" />
                                 <span class="checkbox-custom"></span>
                                 <span class="checkbox-text" :class="{ 'fw-bold text-dark': policy === 'None of the above' }">{{ policy }}</span>
                              </label>
                           </div>
                        </div>
                     </div>

                     <!-- Section 4: Positive Practices -->
                     <div class="col-12 col-lg-6">
                        <div class="mb-3">
                           <div class="d-flex justify-content-between align-items-center mb-2">
                              <label class="form-label mb-0 fw-semibold">
                                 <i class="fas fa-hand-holding-heart text-success me-1"></i> Positive Practices
                              </label>
                              <div class="field-status-indicator-static">
                                 <span v-if="fieldStates.positive_practices === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.positive_practices === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.positive_practices === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                           <small class="text-muted d-block mb-3">Select all verified positive practices implemented:</small>

                           <div class="d-flex flex-column gap-2 p-3 bg-light rounded border border-light">
                              <label v-for="(practice, idx) in positivePracticeOptions" :key="practice.key" class="checkbox-label align-items-start mb-0 py-1" :for="'pos_practice_' + idx" @click.stop>
                                 <input type="checkbox" :id="'pos_practice_' + idx" :checked="profileData.positive_practices && profileData.positive_practices.includes(practice.key)" @change="togglePositivePractice(practice.key)" class="styled-checkbox" />
                                 <span class="checkbox-custom"></span>
                                 <span class="checkbox-text" :class="{ 'fw-bold text-dark': practice.key === 'None of the above' }">
                                    <strong>{{ practice.title }}</strong><span v-if="practice.desc" class="text-muted"> &mdash; {{ practice.desc }}</span>
                                 </span>
                              </label>
                           </div>
                        </div>
                     </div>

                     <!-- Divider -->
                     <div class="col-12"><hr class="my-2 text-muted opacity-25" /></div>

                     <!-- Section 5: 3rd Party Certifications -->
                     <div class="col-12">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                           <label class="form-label mb-0 fw-semibold" style="font-size: 1.05rem;">
                              <i class="fas fa-certificate text-warning me-1"></i> 3rd Party Certifications
                           </label>
                           <div class="field-status-indicator-static">
                              <span v-if="fieldStates.certifications === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                 <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                              </span>
                              <span v-else-if="fieldStates.certifications === 'saving'" class="status-text saving" title="Saving changes...">
                                 <i class="fas fa-spinner fa-spin text-info"></i>
                              </span>
                              <span v-else-if="fieldStates.certifications === 'saved'" class="status-text saved" title="Saved!">
                                 <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                              </span>
                           </div>
                        </div>
                        <small class="text-muted d-block mb-3">Select all active 3rd party accreditations held by the supplier/partner:</small>

                        <div class="row g-3">
                           <!-- Category A: Information Security & Data Privacy -->
                           <div class="col-12 col-md-6 col-xl-3">
                              <div class="p-3 bg-light rounded border border-light h-100">
                                 <h6 class="fw-bold text-primary mb-3" style="font-size: 0.85rem;">
                                    <i class="fas fa-lock me-1"></i> InfoSec & Data Privacy
                                 </h6>
                                 <div class="d-flex flex-column gap-2">
                                    <label v-for="(cert, idx) in certCategories.infoSec" :key="cert" class="checkbox-label mb-0 py-1" :for="'cert_infosec_' + idx" @click.stop>
                                       <input type="checkbox" :id="'cert_infosec_' + idx" :checked="profileData.certifications && profileData.certifications.includes(cert)" @change="toggleCertification(cert)" class="styled-checkbox" />
                                       <span class="checkbox-custom"></span>
                                       <span class="checkbox-text">{{ cert }}</span>
                                    </label>
                                 </div>
                              </div>
                           </div>

                           <!-- Category B: ESG, Sustainability & Ethical Sourcing -->
                           <div class="col-12 col-md-6 col-xl-3">
                              <div class="p-3 bg-light rounded border border-light h-100">
                                 <h6 class="fw-bold text-success mb-3" style="font-size: 0.85rem;">
                                    <i class="fas fa-globe-americas me-1"></i> ESG & Ethical Sourcing
                                 </h6>
                                 <div class="d-flex flex-column gap-2">
                                    <label v-for="(cert, idx) in certCategories.esg" :key="cert" class="checkbox-label mb-0 py-1" :for="'cert_esg_' + idx" @click.stop>
                                       <input type="checkbox" :id="'cert_esg_' + idx" :checked="profileData.certifications && profileData.certifications.includes(cert)" @change="toggleCertification(cert)" class="styled-checkbox" />
                                       <span class="checkbox-custom"></span>
                                       <span class="checkbox-text">{{ cert }}</span>
                                    </label>
                                 </div>
                              </div>
                           </div>

                           <!-- Category C: Supplier Diversity & Social Procurement -->
                           <div class="col-12 col-md-6 col-xl-3">
                              <div class="p-3 bg-light rounded border border-light h-100">
                                 <h6 class="fw-bold text-info mb-3" style="font-size: 0.85rem;">
                                    <i class="fas fa-users me-1"></i> Diversity & Social Procurement
                                 </h6>
                                 <div class="d-flex flex-column gap-2">
                                    <label v-for="(cert, idx) in certCategories.diversity" :key="cert" class="checkbox-label mb-0 py-1" :for="'cert_div_' + idx" @click.stop>
                                       <input type="checkbox" :id="'cert_div_' + idx" :checked="profileData.certifications && profileData.certifications.includes(cert)" @change="toggleCertification(cert)" class="styled-checkbox" />
                                       <span class="checkbox-custom"></span>
                                       <span class="checkbox-text">{{ cert }}</span>
                                    </label>
                                 </div>
                              </div>
                           </div>

                           <!-- Category D: Quality, Health, & Safety -->
                           <div class="col-12 col-md-6 col-xl-3">
                              <div class="p-3 bg-light rounded border border-light h-100">
                                 <h6 class="fw-bold text-warning mb-3" style="font-size: 0.85rem;">
                                    <i class="fas fa-medkit me-1"></i> Quality, Health & Safety
                                 </h6>
                                 <div class="d-flex flex-column gap-2">
                                    <label v-for="(cert, idx) in certCategories.quality" :key="cert.name" class="checkbox-label align-items-start mb-0 py-1" :for="'cert_qual_' + idx" @click.stop :title="cert.desc">
                                       <input type="checkbox" :id="'cert_qual_' + idx" :checked="profileData.certifications && profileData.certifications.includes(cert.name)" @change="toggleCertification(cert.name)" class="styled-checkbox" />
                                       <span class="checkbox-custom"></span>
                                       <span class="checkbox-text">
                                          <strong class="d-block text-dark">{{ cert.name }}</strong>
                                          <small class="d-block text-muted mt-1" style="font-size: 0.75rem; line-height: 1.25;">{{ cert.summary }}</small>
                                       </span>
                                    </label>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            <!-- 6. Social Purpose Card -->
            <div class="accordion-card card border-0 shadow-sm mb-3">
               <div class="accordion-header d-flex justify-content-between align-items-center p-3" @click="toggleSection('socialPurpose')">
                  <div class="d-flex align-items-center gap-2">
                     <i class="fas fa-hand-holding-heart text-info"></i>
                     <span class="accordion-title">Social Purpose</span>
                     <span v-if="profileData.purpose_driven_type && profileData.purpose_driven_type !== 'None'" class="badge rounded-pill bg-light text-primary border ms-2 px-3 py-1" style="font-size: 0.72rem; font-weight: 600;">
                        {{ profileData.purpose_driven_type }}
                     </span>
                     <span v-if="profileData.is_local_supplier" class="badge rounded-pill bg-success-light text-success border border-success-subtle ms-1 px-2.5 py-1" style="font-size: 0.72rem; font-weight: 600;">
                        Local Supplier
                     </span>
                     <span v-if="profileData.is_women_owned" class="badge rounded-pill bg-light text-primary border ms-1 px-2.5 py-1" style="font-size: 0.72rem; font-weight: 600;">
                        Women Owned
                     </span>
                     <span v-if="profileData.is_minority_owned" class="badge rounded-pill bg-light text-primary border ms-1 px-2.5 py-1" style="font-size: 0.72rem; font-weight: 600;">
                        Minority Owned
                     </span>
                     <span v-if="profileData.is_indigenous_owned" class="badge rounded-pill bg-light text-primary border ms-1 px-2.5 py-1" style="font-size: 0.72rem; font-weight: 600;">
                        Indigenous Owned
                     </span>
                     <span v-if="profileData.is_disclosure_industry" class="badge rounded-pill bg-warning-light text-warning-emphasis border border-warning-subtle ms-1 px-2.5 py-1" style="font-size: 0.72rem; font-weight: 600;">
                        <i class="fas fa-exclamation-triangle me-1"></i> Disclosure Industry
                     </span>
                  </div>

                  <button type="button" class="btn-accordion-chevron" @click.stop="toggleSection('socialPurpose')">
                     <i class="fas" :class="sectionOpen.socialPurpose ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                  </button>
               </div>

               <div v-show="sectionOpen.socialPurpose" class="accordion-body p-4 pt-3 border-top">
                  <div class="row g-4">
                     <!-- Top Level Mandatory Fields -->
                     <div class="col-12 col-md-6">
                        <div class="mb-3">
                           <label class="form-label fw-bold mb-1">
                              Organisation Type <span class="text-danger">*</span>
                           </label>
                           <div class="field-save-wrapper">
                              <VueMultiselect
                                 v-model="profileData.organisation_type"
                                 :options="organisationTypeOptions"
                                 :searchable="false"
                                 :allow-empty="false"
                                 :show-labels="false"
                                 :close-on-select="true"
                                 placeholder="Select Organisation Type"
                                 class="vertex-comm-multiselect"
                                 @select="val => { profileData.organisation_type = val; debounceSave('Organisation Type', 'organisation_type'); }"
                                 @update:model-value="val => { if (val) { profileData.organisation_type = val; debounceSave('Organisation Type', 'organisation_type'); } }"
                              >
                              </VueMultiselect>
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.organisation_type === 'waiting'" class="status-text waiting"><i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i></span>
                                 <span v-else-if="fieldStates.organisation_type === 'saving'" class="status-text saving"><i class="fas fa-spinner fa-spin text-info"></i></span>
                                 <span v-else-if="fieldStates.organisation_type === 'saved'" class="status-text saved"><i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i></span>
                              </div>
                           </div>
                        </div>
                     </div>

                     <div class="col-12 col-md-6">
                        <div class="mb-3">
                           <label class="form-label fw-bold mb-1">
                              Purpose Driven <span class="text-danger">*</span>
                           </label>
                           <div class="field-save-wrapper">
                              <VueMultiselect
                                 v-model="profileData.purpose_driven_type"
                                 :options="purposeDrivenTypeOptions"
                                 :searchable="false"
                                 :allow-empty="false"
                                 :show-labels="false"
                                 :close-on-select="true"
                                 :disabled="(profileData.isCharity || profileData.purpose_driven_type === 'Registered Charity') && profileData.reporting_current"
                                 placeholder="Select Purpose Driven Type"
                                 class="vertex-comm-multiselect"
                                 @select="val => onPurposeDrivenTypeChange(val)"
                                 @update:model-value="val => onPurposeDrivenTypeChange(val)"
                              >
                              </VueMultiselect>
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.purpose_driven_type === 'waiting'" class="status-text waiting"><i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i></span>
                                 <span v-else-if="fieldStates.purpose_driven_type === 'saving'" class="status-text saving"><i class="fas fa-spinner fa-spin text-info"></i></span>
                                 <span v-else-if="fieldStates.purpose_driven_type === 'saved'" class="status-text saved"><i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i></span>
                              </div>
                           </div>
                        </div>
                     </div>

                     <!-- CASE 1: Registered Charity -->
                     <div v-if="profileData.purpose_driven_type === 'Registered Charity'" class="col-12">
                        <div class="card bg-light border p-3 rounded mb-3">
                           <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 pb-2 border-bottom">
                              <div class="d-flex align-items-center gap-2">
                                 <i class="fas fa-ribbon text-primary"></i>
                                 <h6 class="fw-bold mb-0 text-dark">ACNC Registered Charity Record</h6>
                              </div>
                              <div class="d-flex align-items-center gap-2">
                                 <button type="button" class="btn btn-sm btn-outline-primary" :disabled="isCheckingAcnc" @click="validateAcnc">
                                    <i class="fas" :class="isCheckingAcnc ? 'fa-spinner fa-spin' : 'fa-sync-alt me-1'"></i>
                                    {{ isCheckingAcnc ? 'Validating ABR/ACNC...' : 'Validate ACNC Record' }}
                                 </button>
                              </div>
                           </div>

                           <div v-if="acncValidationMessage" class="alert py-2 px-3 mb-3" :class="acncValidationStatus === 'success' ? 'alert-success' : 'alert-warning'" style="font-size: 0.85rem;">
                              <i class="fas" :class="acncValidationStatus === 'success' ? 'fa-check-circle me-1' : 'fa-exclamation-circle me-1'"></i>
                              {{ acncValidationMessage }}
                           </div>

                           <div class="row g-3">
                              <!-- Location from ACNC -->
                              <div class="col-12 col-md-6">
                                 <label class="form-label fw-bold small mb-1">Location</label>
                                 <div class="form-control d-flex align-items-center bg-white" style="min-height: 38px; height: 38px;">
                                    {{ formattedSocialPurposeLocation }}
                                 </div>
                              </div>

                              <!-- Reporting Current & PBI / HPC -->
                              <div class="col-12 col-md-6">
                                 <label class="form-label fw-bold small mb-1">Charity Status & Subtypes</label>
                                 <div class="form-control d-flex align-items-center gap-1 bg-white overflow-hidden" style="min-height: 38px; height: 38px;">
                                    <span class="badge" :class="profileData.reporting_current ? 'bg-success text-white' : 'bg-warning text-dark'" style="font-size: 0.72rem;">
                                       Reporting: {{ profileData.reporting_current ? 'Current' : 'No' }}
                                    </span>
                                    <span class="badge" :class="profileData.is_pbi ? 'bg-info text-white' : 'bg-light text-muted border'" style="font-size: 0.72rem;">
                                       PBI: {{ profileData.is_pbi ? 'Yes' : 'No' }}
                                    </span>
                                    <span class="badge" :class="profileData.is_hpc ? 'bg-info text-white' : 'bg-light text-muted border'" style="font-size: 0.72rem;">
                                       HPC: {{ profileData.is_hpc ? 'Yes' : 'No' }}
                                    </span>
                                 </div>
                              </div>

                              <!-- Organisational Purpose (Tags) -->
                              <div class="col-12 col-md-6">
                                 <label class="form-label fw-bold small mb-1">Organisational Purpose (from ACNC)</label>
                                 <div class="p-2 bg-white rounded border" style="min-height: 56px;">
                                    <div v-if="profileData.organisational_purpose && profileData.organisational_purpose.length > 0" class="d-flex flex-wrap gap-1">
                                       <span v-for="p in profileData.organisational_purpose" :key="p" class="badge bg-light text-primary border py-1 px-2" style="font-size: 0.78rem;">
                                          {{ p }}
                                       </span>
                                    </div>
                                    <span v-else class="text-muted small fst-italic">No specific purposes recorded. Click Validate ACNC to populate.</span>
                                 </div>
                              </div>

                              <!-- Population Served (Tags) -->
                              <div class="col-12 col-md-6">
                                 <label class="form-label fw-bold small mb-1">Population Served (from ACNC)</label>
                                 <div class="p-2 bg-white rounded border" style="min-height: 56px;">
                                    <div v-if="profileData.population_served && profileData.population_served.length > 0" class="d-flex flex-wrap gap-1">
                                       <span v-for="pop in profileData.population_served" :key="pop" class="badge bg-light text-info border py-1 px-2" style="font-size: 0.78rem;">
                                          {{ pop }}
                                       </span>
                                    </div>
                                    <span v-else class="text-muted small fst-italic">No specific populations recorded. Click Validate ACNC to populate.</span>
                                 </div>
                              </div>
                           </div>
                        </div>

                        <!-- Underserved Beneficiaries Section -->
                        <div class="border rounded p-3 bg-white">
                           <div class="d-flex align-items-center justify-content-between mb-2">
                              <label class="form-label fw-bold mb-0">Underserved Beneficiaries</label>
                              <div class="form-check form-switch m-0">
                                 <input class="form-check-input" type="checkbox" id="charityUnderservedToggle" v-model="profileData.underserved_beneficiaries" @change="debounceSave('Underserved Beneficiaries', 'underserved_beneficiaries')" />
                                 <label class="form-check-label fw-semibold" for="charityUnderservedToggle">
                                    {{ profileData.underserved_beneficiaries ? 'Yes' : 'No' }}
                                 </label>
                              </div>
                           </div>

                           <div v-if="profileData.underserved_beneficiaries" class="mt-3 pt-2 border-top">
                              <label class="form-label small text-muted mb-1">Underserved Type</label>
                              <VueMultiselect
                                 v-model="profileData.underserved_types"
                                 :options="underservedTypeOptions"
                                 :multiple="true"
                                 :close-on-select="false"
                                 :clear-on-select="false"
                                 :preserve-search="true"
                                 placeholder="Select Underserved Types"
                                 class="vertex-comm-multiselect"
                                 @select="debounceSave('Underserved Types', 'underserved_types')"
                                 @remove="debounceSave('Underserved Types', 'underserved_types')"
                                 @update:model-value="debounceSave('Underserved Types', 'underserved_types')"
                              >
                              </VueMultiselect>
                           </div>
                        </div>
                     </div>

                     <!-- CASE 2: BCorp, NFP, RSE, WOC -->
                     <div v-else-if="['Certified BCorp', 'Not For Profit Organisation', 'Registered Social Enterprise', 'Worker Owned Cooperative'].includes(profileData.purpose_driven_type)" class="col-12">
                        <div class="card bg-light border p-3 rounded mb-3">
                           <div class="row g-3">
                              <!-- Location -->
                              <div class="col-12">
                                 <label class="form-label fw-bold small mb-1">Location</label>
                                 <div class="form-control d-flex align-items-center bg-white" style="min-height: 38px; height: 38px;">
                                    {{ formattedSocialPurposeLocation }}
                                 </div>
                              </div>

                              <!-- Organisational Purpose User Input -->
                              <div class="col-12 col-md-6">
                                 <label class="form-label fw-bold small mb-1">Organisational Purpose</label>
                                 <VueMultiselect
                                    v-model="profileData.organisational_purpose"
                                    :options="purposeOptions"
                                    :multiple="true"
                                    :taggable="true"
                                    @tag="addPurposeTag"
                                    :close-on-select="false"
                                    placeholder="Select or type purpose"
                                    class="vertex-comm-multiselect"
                                    @select="debounceSave('Organisational Purpose', 'organisational_purpose')"
                                    @remove="debounceSave('Organisational Purpose', 'organisational_purpose')"
                                    @update:model-value="debounceSave('Organisational Purpose', 'organisational_purpose')"
                                 >
                                 </VueMultiselect>
                              </div>

                              <!-- Population Served User Input -->
                              <div class="col-12 col-md-6">
                                 <label class="form-label fw-bold small mb-1">Population Served</label>
                                 <VueMultiselect
                                    v-model="profileData.population_served"
                                    :options="populationOptions"
                                    :multiple="true"
                                    :taggable="true"
                                    @tag="addPopulationTag"
                                    :close-on-select="false"
                                    placeholder="Select or type population"
                                    class="vertex-comm-multiselect"
                                    @select="debounceSave('Population Served', 'population_served')"
                                    @remove="debounceSave('Population Served', 'population_served')"
                                    @update:model-value="debounceSave('Population Served', 'population_served')"
                                 >
                                 </VueMultiselect>
                              </div>
                           </div>
                        </div>

                        <!-- Underserved Beneficiaries Section -->
                        <div class="border rounded p-3 bg-white">
                           <div class="d-flex align-items-center justify-content-between mb-2">
                              <label class="form-label font-weight-bold mb-0">Underserved Beneficiaries</label>
                              <div class="form-check form-switch m-0">
                                 <input class="form-check-input" type="checkbox" id="bcorpUnderservedToggle" v-model="profileData.underserved_beneficiaries" @change="debounceSave('Underserved Beneficiaries', 'underserved_beneficiaries')" />
                                 <label class="form-check-label font-weight-bold" for="bcorpUnderservedToggle">
                                    {{ profileData.underserved_beneficiaries ? 'Yes' : 'No' }}
                                 </label>
                              </div>
                           </div>

                           <div v-if="profileData.underserved_beneficiaries" class="mt-3 pt-2 border-top">
                              <label class="form-label small text-muted mb-1">Underserved Type</label>
                              <VueMultiselect
                                 v-model="profileData.underserved_types"
                                 :options="underservedTypeOptions"
                                 :multiple="true"
                                 :close-on-select="false"
                                 :clear-on-select="false"
                                 :preserve-search="true"
                                 placeholder="Select Underserved Types"
                                 class="vertex-comm-multiselect"
                                 @select="debounceSave('Underserved Types', 'underserved_types')"
                                 @remove="debounceSave('Underserved Types', 'underserved_types')"
                                 @update:model-value="debounceSave('Underserved Types', 'underserved_types')"
                              >
                              </VueMultiselect>
                           </div>
                        </div>
                     </div>

                     <!-- CASE 3: Purpose Driven = None -->
                     <div v-else class="col-12">
                        <div class="row g-3">
                           <div class="col-12">
                              <label class="form-label fw-bold small mb-1">Location</label>
                              <div class="form-control d-flex align-items-center bg-white" style="min-height: 38px; height: 38px;">
                                 {{ formattedSocialPurposeLocation }}
                              </div>
                           </div>
                        </div>
                     </div>

                     <!-- Common Section: Business Ownership Diversity, Industry, and Disclosure Industry -->
                     <!-- Ownership Diversity Checkboxes -->
                     <div class="col-12">
                        <div class="p-3 bg-light rounded border">
                           <h6 class="fw-bold text-dark mb-3" style="font-size: 0.88rem;">
                              Business Ownership Diversity
                           </h6>
                           <div class="row g-3">
                              <div class="col-12 col-md-4">
                                 <label class="checkbox-label mb-0 py-1" for="womenOwnedCheck">
                                    <input type="checkbox" id="womenOwnedCheck" v-model="profileData.is_women_owned" @change="debounceSave('Women Owned', 'is_women_owned')" class="styled-checkbox" />
                                    <span class="checkbox-custom"></span>
                                    <span class="checkbox-text fw-bold">Women Owned</span>
                                 </label>
                              </div>
                              <div class="col-12 col-md-4">
                                 <label class="checkbox-label mb-0 py-1" for="minorityOwnedCheck">
                                    <input type="checkbox" id="minorityOwnedCheck" v-model="profileData.is_minority_owned" @change="debounceSave('Minority Owned', 'is_minority_owned')" class="styled-checkbox" />
                                    <span class="checkbox-custom"></span>
                                    <span class="checkbox-text fw-bold">Minority Owned</span>
                                 </label>
                              </div>
                              <div class="col-12 col-md-4">
                                 <label class="checkbox-label mb-0 py-1" for="indigenousOwnedCheck">
                                    <input type="checkbox" id="indigenousOwnedCheck" v-model="profileData.is_indigenous_owned" @change="debounceSave('Indigenous Owned', 'is_indigenous_owned')" class="styled-checkbox" />
                                    <span class="checkbox-custom"></span>
                                    <span class="checkbox-text fw-bold">Indigenous Owned</span>
                                 </label>
                              </div>
                           </div>
                        </div>
                     </div>

                     <!-- Industry Selection -->
                     <div class="col-12 col-md-6">
                        <label class="form-label fw-bold mb-1 d-flex align-items-center gap-2" style="min-height: 28px; height: 28px;">
                           <span>Industry</span>
                           <span v-if="profileData.is_disclosure_industry" class="badge rounded-pill py-1 px-2.5" style="font-size: 0.72rem; font-weight: 600; background-color: #fef3c7; color: #b45309; border: 1px solid #fde68a; line-height: 1;">
                              <i class="fas fa-exclamation-triangle me-1"></i> Disclosure Industry
                           </span>
                           <span v-else class="badge rounded-pill py-1 px-2.5 bg-light text-muted border" style="font-size: 0.72rem; font-weight: 600; line-height: 1;">
                              Non-Disclosure Industry
                           </span>
                        </label>
                        <div class="field-save-wrapper">
                           <VueMultiselect
                              v-model="profileData.industry"
                              :options="computedIndustryOptions"
                              :searchable="true"
                              :allow-empty="true"
                              :show-labels="false"
                              :close-on-select="true"
                              placeholder="- Select Industry -"
                              class="vertex-comm-multiselect"
                              @select="val => onIndustrySelect(val)"
                              @remove="() => onIndustrySelect(null)"
                              @update:model-value="val => onIndustrySelect(val)"
                           >
                           </VueMultiselect>
                           <div class="field-status-indicator">
                              <span v-if="fieldStates.industry === 'waiting'" class="status-text waiting"><i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i></span>
                              <span v-else-if="fieldStates.industry === 'saving'" class="status-text saving"><i class="fas fa-spinner fa-spin text-info"></i></span>
                              <span v-else-if="fieldStates.industry === 'saved'" class="status-text saved"><i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i></span>
                           </div>
                        </div>
                     </div>

                     <!-- Disclosure Industry -->
                     <div class="col-12 col-md-6">
                        <label class="form-label fw-bold mb-1 d-flex align-items-center" style="min-height: 28px; height: 28px;">
                           <span>Disclosure Industry</span>
                        </label>
                        <div class="form-control d-flex align-items-center bg-white" style="min-height: 38px; height: 38px; border-radius: 10px; border: 1.5px solid #cbd5e1; padding: 6px 12px;">
                           <div class="form-check form-switch m-0 d-flex align-items-center">
                              <input class="form-check-input" type="checkbox" id="disclosureIndustryToggle" v-model="profileData.is_disclosure_industry" @change="debounceSave('Disclosure Industry', 'is_disclosure_industry')" style="cursor: pointer;" />
                              <label class="form-check-label fw-semibold ms-2 mb-0" for="disclosureIndustryToggle" style="cursor: pointer;">
                                 {{ profileData.is_disclosure_industry ? 'Yes' : 'No' }}
                              </label>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            <!-- 7. Technology Stack Card -->
            <div class="accordion-card card border-0 shadow-sm mb-3">
               <div class="accordion-header d-flex justify-content-between align-items-center p-3" @click="toggleSection('technologyStack')">
                  <div class="d-flex align-items-center gap-2">
                     <i class="fas fa-layer-group text-info"></i>
                     <span class="accordion-title">Technology Stack</span>
                     <span v-if="profileData.technology_stack && profileData.technology_stack.length > 0" class="badge rounded-pill border ms-1 px-2.5 py-1" style="background-color: #e6f7fa; color: #008298; border-color: rgba(0, 160, 193, 0.25) !important; font-size: 0.72rem; font-weight: 600;">
                        {{ profileData.technology_stack.length }} Configured
                     </span>
                     <div class="d-flex align-items-center ml-2" v-if="!sectionOpen.technologyStack && profileData.technology_stack && profileData.technology_stack.length > 0">
                        <span v-for="(tech, index) in profileData.technology_stack.slice(0, 5)" :key="'header-'+index" class="d-inline-flex align-items-center justify-content-center bg-white border rounded-circle shadow-sm" style="width: 24px; height: 24px; margin-left: -6px; z-index: 1;" :title="tech">
                           <span v-if="getTechMeta(tech).badgeText" class="tech-monogram-badge-xs" :style="{ color: getTechMeta(tech).color }">{{ getTechMeta(tech).badgeText }}</span>
                           <svg v-else-if="getTechMeta(tech).svgPath" viewBox="0 0 24 24" width="14" height="14"><path :d="getTechMeta(tech).svgPath" :fill="getTechMeta(tech).color" /></svg>
                           <i v-else :class="getTechMeta(tech).icon || 'fas fa-cube'" :style="{ color: getTechMeta(tech).color, fontSize: '0.75rem' }"></i>
                        </span>
                        <span v-if="profileData.technology_stack.length > 5" class="badge bg-light text-muted border ml-1" style="font-size: 0.65rem;">+{{ profileData.technology_stack.length - 5 }}</span>
                     </div>
                     <span v-if="fieldStates.technology_stack === 'waiting'" class="badge bg-warning text-dark ml-2" style="font-size: 0.72rem;">
                        <i class="fas fa-clock animate__animated animate__pulse animate__infinite mr-1"></i> Saving...
                     </span>
                     <span v-else-if="fieldStates.technology_stack === 'saving'" class="badge bg-info text-white ml-2" style="font-size: 0.72rem;">
                        <i class="fas fa-spinner fa-spin mr-1"></i> Saving...
                     </span>
                     <span v-else-if="fieldStates.technology_stack === 'saved'" class="badge bg-success text-white ml-2" style="font-size: 0.72rem;">
                        <i class="fas fa-check-circle mr-1"></i> Saved
                     </span>
                  </div>

                  <button type="button" class="btn-accordion-chevron" @click.stop="toggleSection('technologyStack')">
                     <i class="fas" :class="sectionOpen.technologyStack ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                  </button>
               </div>

               <div v-show="sectionOpen.technologyStack" class="accordion-body p-4 pt-3 border-top">
                  <!-- Website Scanner Panel -->
                  <div class="tech-scanner-card p-3 mb-4 rounded-3 border bg-white shadow-sm">
                     <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                        <div class="d-flex align-items-center gap-2">
                           <div class="tech-scanner-icon" style="width: 36px; height: 36px; background: #e6f7fa; color: #00A0C1;">
                              <i class="fas fa-satellite-dish" style="color: #00A0C1;"></i>
                           </div>
                           <div>
                              <h6 class="mb-0 font-weight-bold text-dark">Website Tech Stack Scanner</h6>
                              <small class="text-muted">Analyze live website headers, cookies, meta tags, and DNS records to discover technologies</small>
                           </div>
                        </div>
                     </div>
                     <div class="d-flex align-items-center gap-2">
                        <div class="input-group flex-grow-1">
                           <span class="input-group-text bg-light text-muted border-end-0">
                              <i class="fas fa-globe"></i>
                           </span>
                           <input 
                              type="text" 
                              class="form-control border-start-0" 
                              placeholder="e.g. example.com or https://example.com" 
                              v-model="techScanUrl"
                              @keyup.enter="scanWebsiteTechStack"
                              :disabled="isScanningTechStack"
                           />
                           <button 
                              v-if="techScanUrl" 
                              type="button" 
                              class="btn btn-outline-secondary border-start-0" 
                              @click="techScanUrl = ''" 
                              :disabled="isScanningTechStack"
                              title="Clear URL"
                           >
                              <i class="fas fa-times"></i>
                           </button>
                        </div>
                        <button 
                           type="button" 
                           class="action-btn-circle action-btn-circle--vertex vertex-action-btn"
                           style="width: 38px; height: 38px; min-width: 38px; min-height: 38px;"
                           @click="scanWebsiteTechStack"
                           :disabled="isScanningTechStack || !techScanUrl.trim()"
                           :title="isScanningTechStack ? 'Scanning website...' : 'Scan Website'"
                           :aria-label="isScanningTechStack ? 'Scanning website...' : 'Scan Website'"
                        >
                           <i v-if="isScanningTechStack" class="fas fa-spinner fa-spin"></i>
                           <i v-else class="fas fa-search"></i>
                        </button>
                     </div>

                     <!-- Scanner Results Preview Panel -->
                     <div v-if="techScanResults" class="mt-3 pt-3 border-top" style="background: #f8fafc; border-color: rgba(0, 160, 193, 0.25) !important;">
                        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2 px-2 pt-2">
                           <div class="d-flex align-items-center gap-2">
                              <i class="fas fa-check-circle mr-2" style="color: #00A0C1;"></i>
                              <span class="font-weight-bold text-dark small text-uppercase" style="letter-spacing: 0.5px;">Scan Results</span>
                              <span class="badge ml-2" style="background-color: #00A0C1; color: #ffffff; font-size: 0.68rem;">{{ techScanResults.length }}</span>
                              <span class="small text-muted ml-1">
                                 Target: <strong>{{ techScanMeta?.domain || techScanMeta?.url }}</strong>
                              </span>
                           </div>
                           <div class="d-flex align-items-center gap-2">
                              <button 
                                 v-if="techScanResults.some(item => !isTechSelected(item.name))"
                                 type="button" 
                                 class="btn btn-xs vertex-tech-btn-outline"
                                 @click="toggleSelectAllNewScanResults"
                              >
                                 <i class="fas" :class="isAllScanItemsSelected ? 'fa-minus-square' : 'fa-check-square'"></i>
                                 {{ isAllScanItemsSelected ? 'Deselect All' : 'Select All New' }}
                              </button>
                              <button type="button" class="btn btn-sm btn-outline-secondary py-0" @click="dismissTechScanResults" title="Close scan results">
                                 <i class="fas fa-times mr-1"></i> Dismiss
                              </button>
                           </div>
                        </div>

                        <!-- If 0 detected -->
                        <div v-if="techScanResults.length === 0" class="alert alert-light border text-muted small mb-0 py-2 mx-2">
                           <i class="fas fa-info-circle mr-1"></i> No technologies could be automatically detected from this target.
                        </div>

                        <!-- Detected items table -->
                        <div v-else class="tech-scan-results-container table-responsive border rounded bg-white p-2 mb-3 mx-2" style="max-height: 280px; overflow-y: auto;">
                           <table class="table table-sm align-middle mb-0">
                              <thead class="table-light">
                                 <tr>
                                    <th style="width: 40px;" class="text-center">Import</th>
                                    <th>Technology</th>
                                    <th>Category</th>
                                    <th>Source</th>
                                    <th>Confidence</th>
                                    <th>Status</th>
                                 </tr>
                              </thead>
                              <tbody>
                                 <tr v-for="item in techScanResults" :key="item.name" :class="{ 'tech-scan-item-card-selected': !isTechSelected(item.name) && techScanSelected[item.name] }">
                                    <td class="text-center">
                                       <input 
                                          v-if="!isTechSelected(item.name)"
                                          type="checkbox" 
                                          class="form-check-input vertex-tech-checkbox" 
                                          :id="'scan-check-' + item.name"
                                          v-model="techScanSelected[item.name]"
                                       />
                                       <span v-else class="text-muted"><i class="fas fa-check" style="color: #00A0C1;"></i></span>
                                    </td>
                                    <td>
                                       <div class="d-flex align-items-center gap-2">
                                          <span class="tech-logo-wrapper d-inline-flex align-items-center justify-content-center" style="width: 24px; height: 24px;">
                                             <span v-if="getTechMeta(item.name).badgeText" class="tech-monogram-badge" :style="{ color: getTechMeta(item.name).color, backgroundColor: getTechMeta(item.name).bgTint || '#e6f7fa' }">{{ getTechMeta(item.name).badgeText }}</span>
                                             <svg v-else-if="getTechMeta(item.name).svgPath" viewBox="0 0 24 24" width="20" height="20"><path :d="getTechMeta(item.name).svgPath" :fill="getTechMeta(item.name).color" /></svg>
                                             <i v-else :class="getTechMeta(item.name).icon || 'fas fa-cube'" :style="{ color: getTechMeta(item.name).color, fontSize: '1.1rem' }"></i>
                                          </span>
                                          <span class="font-weight-bold text-dark">{{ item.name }}</span>
                                       </div>
                                    </td>
                                    <td>
                                       <span class="badge bg-light text-dark border">{{ item.category }}</span>
                                    </td>
                                    <td>
                                       <span class="small text-muted">{{ item.source }}</span>
                                       <div v-if="item.evidence" class="text-muted text-truncate" style="max-width: 240px; font-size: 0.72rem;" :title="item.evidence">
                                          {{ item.evidence }}
                                       </div>
                                    </td>
                                    <td>
                                       <span class="badge" :class="item.confidence === 'High' ? 'bg-success' : 'bg-secondary'">
                                          {{ item.confidence }}
                                       </span>
                                    </td>
                                    <td>
                                       <span v-if="isTechSelected(item.name)" class="badge border" style="background-color: #e6f7fa; color: #008298; border-color: rgba(0, 160, 193, 0.25) !important;">
                                          <i class="fas fa-check mr-1" style="color: #00A0C1;"></i> In Stack
                                       </span>
                                       <span v-else class="badge" style="font-size: 0.6rem; background-color: #e6f7fa; color: #008298; border: 1px solid rgba(0, 160, 193, 0.3);">
                                          New
                                       </span>
                                    </td>
                                 </tr>
                              </tbody>
                           </table>
                        </div>

                        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 px-2 pb-2">
                           <small class="text-muted">
                              <i class="fas fa-info-circle mr-1"></i>
                              {{ selectedScanItemsCount }} new item(s) selected for import
                           </small>
                           <div class="d-flex gap-2">
                              <button 
                                 type="button" 
                                 class="btn btn-xs vertex-tech-btn-primary"
                                 @click="applySelectedScanResults"
                                 :disabled="selectedScanItemsCount === 0"
                              >
                                 <i class="fas fa-plus-circle mr-1"></i> Add Selected ({{ selectedScanItemsCount }}) to Stack
                              </button>
                              <button type="button" class="btn btn-sm btn-outline-secondary" @click="dismissTechScanResults">
                                 Dismiss
                              </button>
                           </div>
                        </div>
                     </div>
                  </div>

                  <!-- Search Bar -->
                  <div class="mb-3">
                     <div class="input-group input-group-sm">
                        <span class="input-group-text bg-white border-end-0 text-muted">
                           <i class="fas fa-search"></i>
                        </span>
                        <input 
                           type="text" 
                           class="form-control form-control-sm border-start-0" 
                           placeholder="Filter catalog technologies..." 
                           v-model="techStackSearch"
                        />
                        <button 
                           v-if="techStackSearch" 
                           class="btn btn-outline-secondary border-start-0" 
                           type="button" 
                           @click="techStackSearch = ''"
                        >
                           <i class="fas fa-times"></i>
                        </button>
                     </div>
                  </div>

                  <!-- Active Technologies Pill Cloud -->
                  <div class="tech-active-section mb-4 p-3 bg-light rounded-3 border">
                     <div class="d-flex align-items-center justify-content-between mb-2">
                        <div class="d-flex align-items-center gap-2">
                           <i class="fas fa-layer-group mr-1" style="color: #00A0C1;"></i>
                           <span class="font-weight-bold text-dark small text-uppercase" style="letter-spacing: 0.5px;">Active Organisation Stack</span>
                           <span class="badge ml-2" style="background-color: #e6f7fa; color: #008298; border: 1px solid rgba(0, 160, 193, 0.25); font-size: 0.68rem;">
                              {{ profileData.technology_stack ? profileData.technology_stack.length : 0 }}
                           </span>
                        </div>
                        <div v-if="profileData.technology_stack && profileData.technology_stack.length > 0">
                           <button
                              type="button"
                              class="action-btn-circle action-btn-circle--delete"
                              @click="clearAllTechStack"
                              title="Clear all technologies from stack"
                              aria-label="Clear all technologies from stack"
                           >
                              <i class="fas fa-trash"></i>
                           </button>
                        </div>
                     </div>

                     <div v-if="!profileData.technology_stack || profileData.technology_stack.length === 0" class="text-muted small py-2">
                        <i class="fas fa-layer-group mr-1"></i> No technologies assigned yet. Click preset cards below or use the website scanner above.
                     </div>

                     <div v-else class="d-flex flex-wrap gap-2">
                        <div 
                           v-for="tech in profileData.technology_stack" 
                           :key="tech"
                           class="tech-active-pill d-flex align-items-center gap-2 px-2 py-1 bg-white rounded-pill border shadow-xs"
                        >
                           <span v-if="getTechMeta(tech).badgeText" class="tech-monogram-badge-sm" :style="{ color: getTechMeta(tech).color, backgroundColor: getTechMeta(tech).bgTint || '#e6f7fa' }">{{ getTechMeta(tech).badgeText }}</span>
                           <svg v-else-if="getTechMeta(tech).svgPath" viewBox="0 0 24 24" width="16" height="16"><path :d="getTechMeta(tech).svgPath" :fill="getTechMeta(tech).color" /></svg>
                           <i v-else :class="getTechMeta(tech).icon || 'fas fa-cube'" :style="{ color: getTechMeta(tech).color }"></i>
                           <span class="font-weight-bold small text-dark">{{ tech }}</span>
                           <button type="button" class="btn btn-link p-0 text-muted ml-1" @click.stop="removeTechStack(tech)" title="Remove from stack" style="font-size: 0.85rem;"><i class="fas fa-times"></i></button>
                        </div>
                     </div>
                  </div>

                  <!-- Categorized Catalog Grid -->
                  <div class="tech-categories-wrapper">
                     <div 
                        v-for="cat in filteredTechStackCategories" 
                        :key="cat.name" 
                        class="tech-category-group mb-4"
                     >
                        <div class="d-flex align-items-center gap-2 mb-2 pb-1 border-bottom">
                           <i :class="cat.icon" style="color: #00A0C1;"></i>
                           <h6 class="mb-0 font-weight-bold text-dark">{{ cat.name }}</h6>
                           <span class="badge ml-auto" style="font-size: 0.65rem; background-color: #e6f7fa; color: #008298; border: 1px solid rgba(0, 160, 193, 0.25);">
                              {{ cat.items.filter(i => isTechSelected(i.name)).length }} / {{ cat.items.length }}
                           </span>
                        </div>

                        <div class="tech-grid">
                           <div 
                              v-for="item in cat.items" 
                              :key="item.name"
                              class="tech-tile d-flex align-items-center gap-2 p-2 rounded-2 border position-relative"
                              :class="{ 'is-active': isTechSelected(item.name) }"
                              @click="toggleTechStack(item.name)"
                              role="button"
                              tabindex="0"
                              @keyup.space.prevent="toggleTechStack(item.name)"
                              @keyup.enter.prevent="toggleTechStack(item.name)"
                           >
                              <div class="tech-tile-icon d-flex align-items-center justify-content-center">
                                 <span v-if="item.badgeText" class="tech-monogram-badge" :style="{ color: item.color, backgroundColor: item.bgTint || '#e6f7fa' }">{{ item.badgeText }}</span>
                                 <svg v-else-if="item.svgPath" viewBox="0 0 24 24" width="20" height="20"><path :d="item.svgPath" :fill="item.color" /></svg>
                                 <i v-else :class="item.icon || 'fas fa-cube'" :style="{ color: item.color }"></i>
                              </div>
                              <div class="tech-tile-label flex-grow-1 text-truncate">
                                 <span class="font-weight-bold text-dark small">{{ item.name }}</span>
                              </div>
                              <div class="tech-tile-check">
                                 <i v-if="isTechSelected(item.name)" class="fas fa-check-circle vertex-tech-check-icon"></i>
                                 <i v-else class="far fa-circle text-muted" style="opacity: 0.35;"></i>
                              </div>
                           </div>
                        </div>
                     </div>

                     <div v-if="filteredTechStackCategories.length === 0" class="text-center py-4 text-muted">
                        <i class="fas fa-search fa-2x mb-2 d-block"></i>
                        <p class="mb-0">No preset technologies match "<strong>{{ techStackSearch }}</strong>".</p>
                     </div>
                  </div>
               </div>
            </div>
         </div>

         <!-- CONTACTS TAB -->
         <div v-if="activeTab === 'contacts'" class="contacts-tab-content card p-4 border-0 shadow-sm" style="border-radius: 12px;">
            <!-- Header Bar -->
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
               <div>
                  <div class="form-section-title mb-0 border-0 pb-0 d-flex align-items-center gap-2">
                     <i class="fas fa-address-book mr-2" style="color: #00A0C1;"></i>
                     <span>Contacts</span>
                     <span v-if="orgContacts.length" class="badge bg-light text-secondary rounded-pill border px-2 py-1" style="font-size: 13px;">
                        {{ orgContacts.length }}
                     </span>
                  </div>
               </div>

               <!-- Controls Toolbar (Sync + Create Comm Preference together + Search in one neat row) -->
               <div class="d-flex align-items-center gap-2 flex-nowrap" style="gap: 10px;">
                  <div class="d-flex align-items-center" style="gap: 8px;">
                     <!-- Sync from Auth circular icon button (Vertex design) -->
                     <button
                        type="button"
                        class="action-btn-circle action-btn-circle--vertex vertex-action-btn"
                        :class="{ 'is-linked': profileData.hasAuthOrganisation == 1 && !isPastClient, 'is-syncing': isContactsSyncing }"
                        @click="syncOrganisationContacts"
                        :disabled="isContactsSyncing || profileData.hasAuthOrganisation != 1 || isPastClient"
                        :title="isPastClient ? 'Auth sync is disabled for Past Clients' : (profileData.hasAuthOrganisation == 1 ? (isContactsSyncing ? 'Syncing contacts from Auth...' : 'Sync contacts from Auth') : 'Please link to an Auth organisation first to enable sync')"
                        :aria-label="isPastClient ? 'Auth sync is disabled for Past Clients' : (profileData.hasAuthOrganisation == 1 ? (isContactsSyncing ? 'Syncing contacts from Auth...' : 'Sync contacts from Auth') : 'Please link to an Auth organisation first to enable sync')"
                     >
                        <i class="fas" :class="isContactsSyncing ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
                     </button>

                     <!-- Create a Client Communication Preference circular icon button -->
                     <button
                        type="button"
                        class="action-btn-circle action-btn-circle--vertex vertex-action-btn"
                        @click="openCreateCommModal"
                        title="Create a client communication preference"
                     >
                        <i class="fas fa-comment-medical"></i>
                     </button>
                  </div>

                  <!-- Search Contacts -->
                  <div class="header-search-container m-0" style="width: 280px; min-width: 200px;">
                     <div class="header-search-input-wrapper">
                        <i class="fas fa-search search-icon"></i>
                        <input
                           type="text"
                           v-model="contactsSearchQuery"
                           class="header-search-input"
                           placeholder="Search contacts by name, role, email..."
                        />
                        <button
                           v-if="contactsSearchQuery"
                           type="button"
                           class="clear-search-btn"
                           @click="contactsSearchQuery = ''"
                           title="Clear search"
                        >
                           <i class="fas fa-times"></i>
                        </button>
                     </div>
                  </div>
               </div>
            </div>

            <!-- Loading State -->
            <div v-if="isContactsLoading" class="py-5 text-center text-muted">
               <i class="fas fa-spinner fa-spin fa-2x text-info mb-2"></i>
               <p class="mb-0 fw-semibold">Loading organisation contacts...</p>
            </div>

            <!-- Empty State -->
            <div v-else-if="filteredOrgContacts.length === 0" class="card border-0 bg-light py-5 text-center text-muted" style="border-radius: 12px;">
               <i class="fas fa-address-book fa-3x mb-3 text-secondary animate__animated animate__pulse animate__infinite" style="opacity: 0.4;"></i>
               <p class="mb-1 fw-bold">
                  {{ contactsSearchQuery || contactsRoleFilter.length > 0 ? 'No contacts match the selected filters.' : 'No contacts found for this organisation.' }}
               </p>
               <p class="small text-muted mb-3">
                  {{ contactsSearchQuery || contactsRoleFilter.length > 0 ? 'Try adjusting your search query or role filter.' : 'Link this organisation to Auth or click Sync to fetch stakeholders.' }}
               </p>
               <div v-if="contactsSearchQuery || contactsRoleFilter.length > 0">
                  <button type="button" class="btn btn-sm btn-outline-info" @click="contactsSearchQuery = ''; contactsRoleFilter = [];">
                     Clear Filters
                  </button>
               </div>
            </div>

            <!-- CARDS GRID VIEW -->
            <div v-else class="row g-4">
               <div
                  v-for="contact in filteredOrgContacts"
                  :key="contact.id || contact.uuid || contact.email"
                  class="col-12 col-md-6 col-xl-4"
               >
                  <div class="card h-100 contact-directory-card contact-card">
                     <div class="card-body d-flex flex-column h-100">
                        <!-- Top Meta & Avatar -->
                        <div class="d-flex justify-content-between align-items-start mb-3">
                           <div class="d-flex align-items-center gap-3 text-truncate" style="min-width: 0; gap: 12px;">
                              <div class="contact-card-avatar" :class="'avatar-' + (contact.contact_type || 'default').toLowerCase()" style="width: 42px; height: 42px; min-width: 42px;">
                                 {{ getContactInitials(contact.first_name, contact.last_name, contact.email) }}
                              </div>
                              <div class="text-truncate">
                                 <h6 class="mb-0 text-truncate" style="font-size: 0.98rem; font-weight: 700; color: #1e293b;">
                                    {{ (contact.first_name || '') + ' ' + (contact.last_name || '') }}
                                 </h6>
                                 <div class="text-muted small text-truncate" style="font-size: 12.5px;">
                                    {{ contact.role || 'Contact' }}
                                 </div>
                              </div>
                           </div>
                           <div class="d-flex align-items-center flex-shrink-0 ms-2" style="gap: 6px;">
                              <span
                                 v-if="contact.contact_type"
                                 class="badge rounded-pill text-uppercase px-2 py-1 contact-badge-item"
                                 :class="getContactTypeBadgeClass(contact.contact_type)"
                                 style="font-size: 10px; font-weight: 700; letter-spacing: 0.3px;"
                              >
                                 {{ contact.contact_type }}
                              </span>
                              <span
                                 v-if="contact.is_primary && (!contact.contact_type || contact.contact_type.toLowerCase() !== 'primary')"
                                 class="badge rounded-pill px-2 py-1 contact-badge-item contact-type-badge-primary"
                                 style="font-size: 9.5px; font-weight: 600;"
                              >
                                 ★ Primary
                              </span>
                           </div>
                        </div>

                        <!-- Contact Channels -->
                        <div class="contact-card-channels d-flex flex-column gap-2 pt-3 mb-2" style="border-top: 1px solid #f1f5f9; gap: 8px;">
                           <div v-if="contact.email" class="d-flex align-items-center justify-content-between gap-2 text-truncate">
                              <a :href="'mailto:' + contact.email" class="text-secondary text-decoration-none text-truncate d-flex align-items-center gap-2" :title="contact.email" style="font-size: 13px; gap: 8px;">
                                 <i class="fas fa-envelope text-info" style="width: 16px;"></i>
                                 <span class="text-truncate">{{ contact.email }}</span>
                              </a>
                              <button
                                 type="button"
                                 class="btn btn-sm btn-link text-muted p-0 flex-shrink-0 copy-email-btn"
                                 @click.stop="copyContactEmail(contact.email)"
                                 :title="copiedContactEmail === contact.email ? 'Copied!' : 'Copy Email'"
                              >
                                 <i :class="copiedContactEmail === contact.email ? 'fas fa-check text-success' : 'far fa-copy'"></i>
                              </button>
                           </div>
                           <div v-if="contact.phone_number" class="d-flex align-items-center gap-2 text-truncate">
                              <a :href="'tel:' + contact.phone_number" class="text-secondary text-decoration-none d-flex align-items-center gap-2" style="font-size: 13px; gap: 8px;">
                                 <i class="fas fa-phone text-info" style="width: 16px;"></i>
                                 <span>{{ contact.phone_number }}</span>
                              </a>
                           </div>
                           <div v-if="!contact.email && !contact.phone_number" class="text-muted small fst-italic">
                              No direct channels recorded.
                           </div>
                        </div>

                        <!-- Minimal Saved Communication Preferences Pill Strip -->
                        <div
                           v-if="getContactCommPref(contact)"
                           class="contact-comm-pill-strip"
                           @click="openContactCommDetailsModal(contact)"
                           title="Click to view communication preference details"
                        >
                           <template v-if="getContactCommPref(contact).client_pace || getContactCommPref(contact).cost_sensitivity || getContactCommPref(contact).technicality">
                              <span
                                 v-if="getContactCommPref(contact).client_pace"
                                 :class="['comm-badge', 'comm-badge--sm', getPaceBadgeClass(getContactCommPref(contact).client_pace)]"
                              >
                                 <i class="fas fa-stopwatch"></i>
                                 {{ getPaceBadgeText(getContactCommPref(contact).client_pace) }}
                              </span>
                              <span
                                 v-if="getContactCommPref(contact).cost_sensitivity"
                                 :class="['comm-badge', 'comm-badge--sm', getCostBadgeClass(getContactCommPref(contact).cost_sensitivity)]"
                              >
                                 <i class="fas fa-dollar-sign"></i>
                                 {{ getCostBadgeText(getContactCommPref(contact).cost_sensitivity) }}
                              </span>
                              <span
                                 v-if="getContactCommPref(contact).technicality"
                                 :class="['comm-badge', 'comm-badge--sm', getTechBadgeClass(getContactCommPref(contact).technicality)]"
                              >
                                 <i class="fas fa-code"></i>
                                 {{ getTechBadgeText(getContactCommPref(contact).technicality) }}
                              </span>
                           </template>
                           <span
                              v-else
                              class="comm-badge comm-badge--sm comm-badge--saved"
                           >
                              <i class="fas fa-comments"></i>
                              Preferences Saved
                           </span>
                        </div>

                        <!-- Bottom Actions & Meta -->
                        <div class="d-flex justify-content-between align-items-center" style="border-top: 1px solid #f1f5f9; padding-top: 14px; margin-top: auto;">
                           <span v-if="contact.auth_user_id || contact.auth_contact_id" class="badge bg-light text-secondary border d-inline-flex align-items-center gap-1" style="font-size: 10.5px; font-weight: 500; gap: 4px;">
                              <i class="fas fa-shield-alt text-info"></i> Auth Linked
                           </span>
                           <span v-else></span>

                           <div class="d-flex gap-2" style="gap: 8px;">
                              <button
                                 type="button"
                                 class="action-btn-circle action-btn-circle--vertex"
                                 :class="{ 'has-comm-pref': !!getContactCommPref(contact) }"
                                 @click="openContactCommModalForContact(contact)"
                                 :title="getContactCommPref(contact) ? 'Edit communication preferences' : 'Create communication preferences'"
                              >
                                 <i class="fas" :class="getContactCommPref(contact) ? 'fa-pen' : 'fa-comment-medical'"></i>
                                 <span v-if="getContactCommPref(contact)" class="action-btn-dot"></span>
                              </button>
                              <button
                                 type="button"
                                 class="action-btn-circle action-btn-circle--vertex"
                                 @click="createProposalForContact(contact)"
                                 title="Create proposal addressed to this contact"
                              >
                                 <i class="fas fa-file-invoice"></i>
                              </button>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </div>

         <!-- JOBS BOARD TAB -->
         <div v-if="activeTab === 'jobs_board' && canShowJobsBoard" class="jobs-board-tab-content card p-4 border-0 shadow-sm" style="border-radius: 12px;">
            <!-- Header & Top Action Bar -->
            <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
               <div class="d-flex align-items-center gap-2">
                  <div class="aj-board-header-icon">
                     <i class="fa-solid fa-bolt text-teal"></i>
                  </div>
                  <div>
                     <div class="form-section-title mb-0 border-0 pb-0 d-flex align-items-center gap-2">
                        <span>Active & Staged Jobs</span>
                        <span v-if="orgActiveJobs.length" class="badge bg-light text-secondary rounded-pill border px-2 py-1" style="font-size: 13px;">
                           {{ orgActiveJobs.length }}
                        </span>
                     </div>
                     <p class="text-muted small mb-0 mt-1">Operational delivery jobs and automated task workflows for {{ profileData.name || 'this client' }}</p>
                  </div>
               </div>
               <div class="d-flex align-items-center gap-2">
                  <button
                     type="button"
                     class="action-btn-circle action-btn-circle--vertex vertex-action-btn"
                     @click="fetchOrganisationActiveJobs(true)"
                     :disabled="isJobsLoading"
                     title="Refresh Active Jobs"
                     aria-label="Refresh Active Jobs"
                  >
                     <i class="fa-solid fa-arrows-rotate" :class="{ 'fa-spin': isJobsLoading }"></i>
                  </button>
                  <button
                     type="button"
                     class="action-btn-circle action-btn-circle--vertex vertex-action-btn"
                     @click="openStageJobModal()"
                     title="Stage New Job"
                     aria-label="Stage New Job"
                  >
                     <i class="fa-solid fa-plus"></i>
                  </button>
                  <router-link
                     to="/admin/jobs-catalogue"
                     class="action-btn-circle action-btn-circle--vertex vertex-action-btn"
                     title="Jobs Catalogue"
                     aria-label="Jobs Catalogue"
                  >
                     <i class="fa-solid fa-layer-group"></i>
                  </router-link>
               </div>
            </div>

            <!-- Metric Counters Summary Bar -->
            <div v-if="orgActiveJobs.length > 0" class="row g-3 mb-4">
               <div class="col-6 col-md-3">
                  <div class="p-3 bg-white border rounded-3 h-100 d-flex align-items-center justify-content-between" style="box-shadow: 0 1px 3px rgba(0,0,0,0.03);">
                     <div>
                        <span class="text-muted small text-uppercase fw-semibold" style="font-size: 0.72rem; letter-spacing: 0.5px;">Total Jobs</span>
                        <h4 class="fw-bold mb-0 mt-1 text-dark">{{ jobsMetrics.total }}</h4>
                     </div>
                     <div class="metric-icon-circle bg-light text-teal">
                        <i class="fa-solid fa-layer-group"></i>
                     </div>
                  </div>
               </div>
               <div class="col-6 col-md-3">
                  <div class="p-3 bg-white border rounded-3 h-100 d-flex align-items-center justify-content-between" style="box-shadow: 0 1px 3px rgba(0,0,0,0.03);">
                     <div>
                        <span class="text-muted small text-uppercase fw-semibold" style="font-size: 0.72rem; letter-spacing: 0.5px;">In Progress</span>
                        <h4 class="fw-bold mb-0 mt-1" style="color: #0284c7;">{{ jobsMetrics.inProgress }}</h4>
                     </div>
                     <div class="metric-icon-circle" style="background: #e0f2fe; color: #0284c7;">
                        <i class="fa-solid fa-spinner"></i>
                     </div>
                  </div>
               </div>
               <div class="col-6 col-md-3">
                  <div class="p-3 bg-white border rounded-3 h-100 d-flex align-items-center justify-content-between" style="box-shadow: 0 1px 3px rgba(0,0,0,0.03);">
                     <div>
                        <span class="text-muted small text-uppercase fw-semibold" style="font-size: 0.72rem; letter-spacing: 0.5px;">Completed</span>
                        <h4 class="fw-bold mb-0 mt-1 text-success">{{ jobsMetrics.completed }}</h4>
                     </div>
                     <div class="metric-icon-circle" style="background: #dcfce7; color: #16a34a;">
                        <i class="fa-solid fa-check-double"></i>
                     </div>
                  </div>
               </div>
               <div class="col-6 col-md-3">
                  <div class="p-3 bg-white border rounded-3 h-100 d-flex align-items-center justify-content-between" style="box-shadow: 0 1px 3px rgba(0,0,0,0.03);">
                     <div>
                        <span class="text-muted small text-uppercase fw-semibold" style="font-size: 0.72rem; letter-spacing: 0.5px;">Health On Track</span>
                        <h4 class="fw-bold mb-0 mt-1" :class="jobsMetrics.atRisk > 0 ? 'text-warning' : 'text-success'">
                           {{ jobsMetrics.onTrackPercentage }}%
                        </h4>
                     </div>
                     <div class="metric-icon-circle" :style="jobsMetrics.atRisk > 0 ? 'background: #fef3c7; color: #d97706;' : 'background: #d1fae5; color: #059669;'">
                        <i class="fa-solid fa-heart-pulse"></i>
                     </div>
                  </div>
               </div>
            </div>

            <!-- Search & Status Filter Bar -->
            <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
               <!-- Filter Pills -->
               <div class="d-flex align-items-center gap-2 flex-wrap">
                  <button
                     type="button"
                     class="btn btn-sm org-job-filter-pill"
                     :class="{ active: jobStatusFilter === 'all' }"
                     @click="jobStatusFilter = 'all'"
                  >
                     All <span class="badge rounded-pill bg-light text-dark ms-1">{{ orgActiveJobs.length }}</span>
                  </button>
                  <button
                     type="button"
                     class="btn btn-sm org-job-filter-pill"
                     :class="{ active: jobStatusFilter === 'In Progress' }"
                     @click="jobStatusFilter = 'In Progress'"
                  >
                     In Progress <span class="badge rounded-pill bg-light text-dark ms-1">{{ jobsMetrics.inProgress }}</span>
                  </button>
                  <button
                     type="button"
                     class="btn btn-sm org-job-filter-pill"
                     :class="{ active: jobStatusFilter === 'Pending' }"
                     @click="jobStatusFilter = 'Pending'"
                  >
                     Pending <span class="badge rounded-pill bg-light text-dark ms-1">{{ jobsMetrics.pending }}</span>
                  </button>
                  <button
                     type="button"
                     class="btn btn-sm org-job-filter-pill"
                     :class="{ active: jobStatusFilter === 'Completed' }"
                     @click="jobStatusFilter = 'Completed'"
                  >
                     Completed <span class="badge rounded-pill bg-light text-dark ms-1">{{ jobsMetrics.completed }}</span>
                  </button>
                  <button
                     v-if="jobsMetrics.atRisk > 0"
                     type="button"
                     class="btn btn-sm org-job-filter-pill org-job-filter-pill--risk"
                     :class="{ active: jobStatusFilter === 'At Risk' }"
                     @click="jobStatusFilter = 'At Risk'"
                  >
                     At Risk / Behind <span class="badge rounded-pill bg-danger text-white ms-1">{{ jobsMetrics.atRisk }}</span>
                  </button>
               </div>

               <!-- Search Input -->
               <div class="org-jobs-search-wrap" style="min-width: 250px;">
                  <div class="input-group input-group-sm">
                     <span class="input-group-text bg-white border-end-0 text-muted"><i class="fas fa-search"></i></span>
                     <input
                        type="text"
                        class="form-control border-start-0 ps-0"
                        placeholder="Search jobs & blueprints..."
                        v-model="jobSearchQuery"
                     />
                     <button
                        v-if="jobSearchQuery"
                        type="button"
                        class="btn btn-link text-muted p-1 border-0"
                        @click="jobSearchQuery = ''"
                        title="Clear search"
                     >
                        <i class="fas fa-times-circle"></i>
                     </button>
                  </div>
               </div>
            </div>

            <!-- Loading State -->
            <div v-if="isJobsLoading" class="text-center py-5">
               <i class="fas fa-circle-notch fa-spin fa-2x text-teal mb-2"></i>
               <p class="text-muted small mb-0">Loading active jobs...</p>
            </div>

            <!-- Empty State (No jobs at all) -->
            <div v-else-if="!orgActiveJobs.length" class="card border-0 bg-light py-5 text-center text-muted" style="border-radius: 12px;">
               <i class="fas fa-briefcase fa-3x mb-3 text-secondary animate__animated animate__pulse animate__infinite" style="opacity: 0.4;"></i>
               <p class="mb-1 fw-bold text-dark">No active jobs found for {{ profileData.name || 'this organisation' }}.</p>
               <p class="small text-muted mb-3" style="max-width: 500px; margin: 0 auto;">
                  When a lead or deal for this client is converted, active jobs are staged automatically. You can also manually stage a workflow blueprint at any time.
               </p>
               <div>
                  <button type="button" class="btn org-jobs-btn-primary btn-sm px-3" @click="openStageJobModal()">
                     <i class="fa-solid fa-plus me-1"></i> Stage First Job
                  </button>
               </div>
            </div>

            <!-- Empty State (Filter produced no results) -->
            <div v-else-if="!filteredOrgActiveJobs.length" class="card border-0 bg-light py-5 text-center text-muted" style="border-radius: 12px;">
               <i class="fas fa-filter fa-2x mb-2 text-secondary opacity-50"></i>
               <p class="mb-1 fw-bold text-dark">No jobs match your filter criteria.</p>
               <p class="small text-muted mb-2">Try clearing search keywords or switching filter tabs.</p>
               <div>
                  <button type="button" class="btn btn-outline-secondary btn-sm" @click="jobStatusFilter = 'all'; jobSearchQuery = '';">
                     Reset Filters
                  </button>
               </div>
            </div>

            <!-- Job Cards Grid -->
            <div v-else class="row g-3">
               <div v-for="job in filteredOrgActiveJobs" :key="job.id" class="col-12 col-lg-6">
                  <div class="org-job-card h-100" @click="openOrgActiveJobModal(job.id)">
                     <!-- Top Row: Status & Health Badges + Quick Actions -->
                     <div class="d-flex justify-content-between align-items-center mb-3">
                        <div class="d-flex align-items-center gap-2 flex-wrap">
                           <span class="aj-badge-status" :class="'aj-badge-status--' + (job.job_status || 'pending').toLowerCase().replace(' ', '')">
                              <span class="aj-badge-dot"></span>
                              {{ job.job_status || 'Pending' }}
                           </span>
                           <span class="aj-badge-health" :class="'aj-badge-health--' + (job.health_indicator || 'ontrack').toLowerCase().replace(' ', '')">
                              <i class="fas fa-heartbeat me-1"></i> {{ job.health_indicator || 'On Track' }}
                           </span>
                           <span v-if="job.lead_id" class="aj-trigger-badge aj-trigger-badge--lead">
                              <i class="fas fa-user-plus me-1"></i> Lead Converted
                           </span>
                           <span v-else-if="job.contract_id" class="aj-trigger-badge aj-trigger-badge--contract">
                              <i class="fas fa-file-signature me-1"></i> Contract Won
                           </span>
                           <span v-else class="aj-trigger-badge aj-trigger-badge--manual">
                              <i class="fas fa-hand-pointer me-1"></i> Manual Staging
                           </span>
                        </div>

                        <!-- Quick Action Circles -->
                        <div class="d-flex align-items-center gap-2 flex-shrink-0" @click.stop>
                           <button
                              v-if="canManageJobs"
                              type="button"
                              class="action-btn-circle action-btn-circle--delete"
                              title="Delete this active job"
                              aria-label="Delete this active job"
                              @click="deleteOrgActiveJob(job)"
                           >
                              <i class="fas fa-trash-alt"></i>
                           </button>
                           <button
                              type="button"
                              class="action-btn-circle action-btn-circle--vertex vertex-action-btn"
                              title="View Task Plan & Details"
                              aria-label="View Task Plan & Details"
                              @click="openOrgActiveJobModal(job.id)"
                           >
                              <i class="fa-solid fa-list-check"></i>
                           </button>
                        </div>
                     </div>

                     <!-- Main Identity Row -->
                     <div class="d-flex align-items-start gap-3 mb-3">
                        <div class="org-job-card__icon-box flex-shrink-0">
                           <i class="fa-solid fa-briefcase"></i>
                        </div>
                        <div class="flex-grow-1 overflow-hidden">
                           <h6 class="org-job-card__title mb-1 text-truncate" :title="job.title">{{ job.title }}</h6>
                           <div v-if="job.blueprint" class="d-flex align-items-center gap-2 flex-wrap">
                              <span class="aj-blueprint-badge">
                                 <i class="fa-solid fa-sitemap text-teal me-1"></i>
                                 <span class="text-secondary fw-medium">{{ job.blueprint.title }}</span>
                              </span>
                              <span v-if="job.blueprint.auth_item_code" class="aj-code-tag">
                                 {{ job.blueprint.auth_item_code }}
                              </span>
                           </div>
                        </div>
                     </div>

                     <!-- Progress Box -->
                     <div class="org-job-card__progress-box">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                           <span class="text-secondary fw-semibold" style="font-size: 0.78rem;">Task Completion</span>
                           <span class="fw-bold text-dark" style="font-size: 0.78rem;">
                              {{ job.completed_tasks_count || 0 }} / {{ job.tasks_count || 0 }} Tasks
                              <span class="aj-progress-pill ms-1">{{ calculateJobProgress(job) }}%</span>
                           </span>
                        </div>
                        <div class="aj-progress-track">
                           <div
                              class="aj-progress-bar"
                              role="progressbar"
                              :style="{ width: calculateJobProgress(job) + '%' }"
                           ></div>
                        </div>
                     </div>

                     <!-- Card Footer -->
                     <div class="d-flex justify-content-between align-items-center mt-3 pt-2 border-top" style="border-color: #f1f5f9 !important;">
                        <span class="text-muted" style="font-size: 0.78rem;">
                           <i class="fa-regular fa-calendar me-2"></i>Staged {{ formatDate(job.created_at) }}
                        </span>
                        <span class="text-teal fw-semibold org-job-card__cta d-inline-flex align-items-center" style="font-size: 0.8rem;">
                           <span>View Task Plan</span>
                           <i class="fa-solid fa-arrow-right ms-2 org-job-card__cta-arrow"></i>
                        </span>
                     </div>
                  </div>
               </div>
            </div>
         </div>



          <!-- MERGED ORGANISATIONS TAB -->
          <div v-if="activeTab === 'merged_organisations'" class="merged-orgs-tab-content card p-4 border-0 shadow-sm">
             <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <div>
                   <h5 class="fw-bold mb-1 text-dark">Merged Organisations</h5>
                   <p class="text-muted small mb-0">Organisations that have been merged into {{ profileData.name }}</p>
                </div>
                <button 
                   v-if="!profileData.is_merged" 
                   type="button" 
                   class="btn btn-sm btn-vertex-primary d-flex align-items-center gap-1"
                   @click="openMergeModal"
                >
                   <i class="fas fa-code-branch"></i> Merge Another Organisation
                </button>
             </div>

             <div v-if="isMergedOrgsLoading" class="text-center py-5">
                <i class="fas fa-spinner fa-spin fa-2x mb-2" style="color: #00768a;"></i>
                <p class="text-muted mb-0">Loading merged organisations...</p>
             </div>

             <div v-else-if="mergedOrganisations && mergedOrganisations.length > 0" class="table-responsive">
                <table class="table table-hover align-middle mb-0">
                   <thead class="table-light">
                      <tr>
                         <th>Organisation</th>
                         <th>Merged Date</th>
                         <th>Merged By</th>
                         <th>Reason</th>
                         <th>Absorbed Assets</th>
                         <th class="text-end">Actions</th>
                      </tr>
                   </thead>
                   <tbody>
                      <tr v-for="mOrg in mergedOrganisations" :key="mOrg.id || mOrg.uuid">
                         <td>
                            <div class="d-flex align-items-center gap-2">
                               <div class="org-avatar-box d-flex align-items-center justify-content-center" style="width: 36px; height: 36px; font-size: 14px; border-radius: 8px; background: rgba(0, 118, 138, 0.08); color: #00768a;">
                                  <i class="fas fa-building"></i>
                               </div>
                               <div>
                                  <a href="javascript:void(0)" @click="navigateToOrganisation(mOrg.uuid || mOrg.id)" class="fw-bold text-dark text-decoration-none" style="cursor: pointer;" onmouseover="this.style.color='#00768a'; this.style.textDecoration='underline';" onmouseout="this.style.color='#1e293b'; this.style.textDecoration='none';">{{ mOrg.name }}</a>
                                  <div class="text-muted small">{{ mOrg.abn || mOrg.email || 'No ABN' }}</div>
                                </div>
                            </div>
                         </td>
                         <td>
                            <span class="text-muted small">{{ mOrg.merged_at ? formatDate(mOrg.merged_at) : 'N/A' }}</span>
                         </td>
                         <td>
                            <span class="badge bg-light text-secondary border">{{ mOrg.merged_by_name || 'System' }}</span>
                         </td>
                         <td>
                            <span class="text-muted small fst-italic">{{ mOrg.merge_reason || 'No reason specified' }}</span>
                         </td>
                         <td>
                            <div class="d-flex align-items-center gap-2 small">
                               <span class="badge" style="background-color: rgba(0, 118, 138, 0.1); color: #00768a; border: 1px solid rgba(0, 118, 138, 0.2); padding: 4px 8px;">
                                  <i class="fas fa-bullseye me-1"></i>{{ mOrg.transferred_leads_count || 0 }} Leads
                                </span>
                               <span class="badge" style="background-color: rgba(0, 160, 193, 0.1); color: #00A0C1; border: 1px solid rgba(0, 160, 193, 0.2); padding: 4px 8px;">
                                  <i class="fas fa-users me-1"></i>{{ mOrg.transferred_contacts_count || 0 }} Contacts
                               </span>
                            </div>
                         </td>
                         <td class="text-end">
                            <div class="d-flex justify-content-end align-items-center gap-2">
                               <button type="button" @click="navigateToOrganisation(mOrg.uuid || mOrg.id)" class="btn btn-sm btn-outline-secondary" title="View Organisation">
                                  <i class="fas fa-external-link-alt me-1"></i> View
                               </button>
                               <button type="button" class="btn btn-sm btn-vertex-outline" @click="openUnmergeModal(mOrg)" title="Unmerge Organisation">
                                  <i class="fas fa-undo me-1"></i> Unmerge
                               </button>
                            </div>
                         </td>
                      </tr>
                   </tbody>
                </table>
             </div>

             <div v-else class="text-center py-5 text-muted">
                <div class="mb-3">
                   <i class="fas fa-code-branch fa-3x text-muted" style="opacity: 0.35;"></i>
                </div>
                <h6 class="fw-bold text-secondary mb-1">No Merged Organisations</h6>
                <p class="small text-muted mb-3">No other organisations have been merged into this organisation yet.</p>
                <button v-if="!profileData.is_merged" type="button" class="btn btn-sm btn-vertex-primary" @click="openMergeModal">
                   <i class="fas fa-code-branch me-1"></i> Merge an Organisation to Another
                </button>
             </div>
          </div>

         <!-- TIMELINE TAB -->
         <div v-if="activeTab === 'timeline'" class="timeline-tab-content card p-4 border-0 shadow-sm">
            <div class="form-section-title mb-4">Activity Timeline</div>
            <div v-if="timelineEvents && timelineEvents.length > 0" class="vertical-timeline">
               <div class="timeline-event" v-for="event in timelineEvents" :key="event.id">
                  <div class="timeline-icon">
                     <img v-if="event.image" :src="event.image" style="width: 100%; height: 100%; object-fit: contain; border-radius: 50%; padding: 2px;" />
                     <i v-else :class="event.icon"></i>
                  </div>
                  <div class="timeline-body">
                     <div class="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-2">
                        <div class="d-flex align-items-center gap-2 flex-wrap">
                           <span class="event-title fw-bold text-dark">{{ event.title }}</span>
                           <span v-if="event.isAuthSync" class="badge bg-primary-subtle text-primary border border-primary-subtle small px-2 py-1 d-inline-flex align-items-center" style="font-size: 0.75rem; font-weight: 500; background-color: #e8f4fd !important; color: #0d6efd !important; border-color: #b6d4fe !important;">
                              <i class="fas fa-sync-alt me-1"></i>Auth Sync
                           </span>
                           <span v-if="event.isSystem" class="badge bg-light text-secondary border small px-2 py-1 d-inline-flex align-items-center" style="font-size: 0.75rem; font-weight: 500;">
                              <i class="fas fa-robot me-1 text-muted"></i>System Generated
                           </span>
                           <span v-else class="badge bg-light text-primary border small px-2 py-1 d-inline-flex align-items-center" style="font-size: 0.75rem; font-weight: 500;">
                              <i class="fas fa-user-circle me-1 text-primary"></i>{{ event.userName }}
                           </span>
                        </div>
                        <span class="event-time text-muted small">{{ event.time }}</span>
                     </div>
                     <p class="event-description mb-0 text-muted">{{ event.description }}</p>
                  </div>
               </div>
            </div>
            <div v-else class="text-center py-5 text-muted">
               <i class="fas fa-history fa-2x mb-2 text-muted" style="opacity: 0.5;"></i>
               <p class="mb-0">No timeline events recorded yet.</p>
            </div>
         </div>

         <!-- ── Client Communication Modal (View Details, Create, & Edit) ── -->
         <Transition name="lead-modal-fade">
            <div v-if="showCommModal" class="modal-backdrop-custom" @click.self="closeCommModal">
               <div class="modal-content-custom comm-modal-content animate__animated animate__zoomIn animate__faster">
                  <div class="modal-header-custom d-flex justify-content-between align-items-center">
                     <h5 class="modal-title-custom d-flex align-items-center gap-2 mb-0" style="gap: 10px;">
                        <i class="fas fa-comments" style="color: #00A0C1;"></i>
                        <span v-if="commModalMode === 'view'">Communication Preference Details</span>
                        <span v-else>{{ editingCommId ? 'Edit' : 'Add' }} Client Preference</span>
                     </h5>
                     <button type="button" @click="closeCommModal" class="close-button-custom" style="position: static;">&times;</button>
                  </div>

                  <!-- VIEW DETAILS MODE -->
                  <div v-if="commModalMode === 'view'" class="modal-body-custom">
                     <!-- Contact Summary Banner -->
                     <div
                        v-if="commModalContact"
                        class="d-flex align-items-center gap-3"
                        style="padding: 14px 18px; margin-bottom: 22px; border-radius: 12px; background: #f8fafc; border: 1px solid #e2e8f0; gap: 14px;"
                     >
                        <div class="contact-card-avatar" :class="'avatar-' + (commModalContact.contact_type || 'default').toLowerCase()" style="width: 42px; height: 42px; min-width: 42px;">
                           {{ getContactInitials(commModalContact.first_name, commModalContact.last_name, commModalContact.email) }}
                        </div>
                        <div class="text-truncate">
                           <div class="fw-bold text-dark text-truncate" style="font-size: 0.98rem;">
                              {{ getContactFullName(commModalContact) }}
                           </div>
                           <div class="text-muted small text-truncate" style="font-size: 0.82rem;">
                              {{ commModalContact.email || commModalContact.role || 'Contact' }}
                           </div>
                        </div>
                     </div>

                     <!-- Core Indicators Section (3-Column Horizontal Grid) -->
                     <label class="form-label small fw-bold text-muted text-uppercase mb-2" style="letter-spacing: 0.5px; font-size: 0.72rem;">Core Indicators</label>
                     <div class="row g-3 mb-4">
                        <div class="col-12 col-md-4">
                           <div style="padding: 14px 16px; border-radius: 12px; background: #f8fafc; border: 1px solid #e2e8f0; height: 100%;">
                              <div class="mb-2">
                                 <span
                                    :class="['comm-badge', getPaceBadgeClass(commForm.clientPace)]"
                                    :title="getPaceBadgeTooltip(commForm.clientPace)"
                                    style="margin-bottom: 10px; display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px;"
                                 >
                                    <i class="fas fa-stopwatch"></i>
                                    Pace: {{ getPaceBadgeText(commForm.clientPace) }}
                                 </span>
                              </div>
                              <div style="font-size: 0.8rem; color: #475569; line-height: 1.5;">
                                 {{ commForm.clientPace ? getPaceExplainer(commForm.clientPace) : 'No pace preference specified.' }}
                              </div>
                           </div>
                        </div>
                        <div class="col-12 col-md-4">
                           <div style="padding: 14px 16px; border-radius: 12px; background: #f8fafc; border: 1px solid #e2e8f0; height: 100%;">
                              <div class="mb-2">
                                 <span
                                    :class="['comm-badge', getCostBadgeClass(commForm.costSensitivity)]"
                                    :title="getCostBadgeTooltip(commForm.costSensitivity)"
                                    style="margin-bottom: 10px; display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px;"
                                 >
                                    <i class="fas fa-dollar-sign"></i>
                                    Cost: {{ getCostBadgeText(commForm.costSensitivity) }}
                                 </span>
                              </div>
                              <div style="font-size: 0.8rem; color: #475569; line-height: 1.5;">
                                 {{ commForm.costSensitivity ? getCostExplainer(commForm.costSensitivity) : 'No cost sensitivity specified.' }}
                              </div>
                           </div>
                        </div>
                        <div class="col-12 col-md-4">
                           <div style="padding: 14px 16px; border-radius: 12px; background: #f8fafc; border: 1px solid #e2e8f0; height: 100%;">
                              <div class="mb-2">
                                 <span
                                    :class="['comm-badge', getTechBadgeClass(commForm.technicality)]"
                                    :title="getTechBadgeTooltip(commForm.technicality)"
                                    style="margin-bottom: 10px; display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px;"
                                 >
                                    <i class="fas fa-code"></i>
                                    Tech: {{ getTechBadgeText(commForm.technicality) }}
                                 </span>
                              </div>
                              <div style="font-size: 0.8rem; color: #475569; line-height: 1.5;">
                                 {{ commForm.technicality ? getTechExplainer(commForm.technicality) : 'No technicality level specified.' }}
                              </div>
                           </div>
                        </div>
                     </div>

                     <!-- Decision Style & Communication Style Section (2-Column Horizontal Grid) -->
                     <div class="row g-3">
                        <div class="col-12 col-md-6">
                           <div style="padding: 16px 18px; border-radius: 12px; background: #f8fafc; border: 1px solid #e2e8f0; height: 100%;">
                              <label class="form-label small fw-bold text-muted text-uppercase mb-2 d-block" style="letter-spacing: 0.5px; font-size: 0.72rem;">Decision Style</label>
                              <div class="text-secondary small" style="line-height: 1.55; white-space: pre-wrap;">{{ commForm.decisionStyle || 'Not specified' }}</div>
                           </div>
                        </div>
                        <div class="col-12 col-md-6">
                           <div style="padding: 16px 18px; border-radius: 12px; background: #f8fafc; border: 1px solid #e2e8f0; height: 100%;">
                              <label class="form-label small fw-bold text-muted text-uppercase mb-2 d-block" style="letter-spacing: 0.5px; font-size: 0.72rem;">Communication Style</label>
                              <div class="text-secondary small" style="line-height: 1.55; white-space: pre-wrap;">{{ commForm.communicationStyle || 'Not specified' }}</div>
                           </div>
                        </div>
                     </div>

                     <!-- Footer Actions in View Mode -->
                     <div class="modal-footer-custom d-flex justify-content-between align-items-center">
                        <button
                           v-if="editingCommId"
                           type="button"
                           class="action-btn-circle action-btn-circle--delete"
                           @click="deleteCommPrefFromModal"
                           :disabled="isLoading"
                           title="Delete communication preference"
                        >
                           <i class="fas fa-trash"></i>
                        </button>
                        <span v-else></span>
                        <div class="d-flex gap-2" style="gap: 10px;">
                           <button type="button" class="lead-btn lead-btn--ghost" @click="closeCommModal">Close</button>
                           <button type="button" class="lead-btn lead-btn--primary" @click="switchCommModalToEdit">
                              <i class="fas fa-pen me-1"></i> Edit Preferences
                           </button>
                        </div>
                     </div>
                  </div>

                  <!-- CREATE / EDIT FORM MODE -->
                  <div v-else class="modal-body-custom">
                     <div class="mb-4">
                        <template v-if="commModalContact">
                           <label class="form-label fw-bold mb-2">Contact</label>
                           <div class="d-flex align-items-center gap-3 rounded-3" style="background-color: #f8fafc; border: 1.5px solid #cbd5e1; padding: 12px 16px; gap: 14px;">
                              <div class="contact-card-avatar" :class="'avatar-' + (commModalContact.contact_type || 'default').toLowerCase()" style="width: 38px; height: 38px; min-width: 38px; font-size: 0.85rem;">
                                 {{ getContactInitials(commModalContact.first_name, commModalContact.last_name, commModalContact.email) }}
                              </div>
                              <div class="text-truncate">
                                 <div class="fw-bold text-dark text-truncate" style="font-size: 0.94rem;">
                                    {{ getContactFullName(commModalContact) }}
                                 </div>
                                 <div v-if="commModalContact.email" class="text-muted small text-truncate" style="font-size: 0.8rem;">
                                    {{ commModalContact.email }}
                                 </div>
                              </div>
                           </div>
                        </template>
                        <template v-else-if="profileData.hasAuthOrganisation == 1">
                           <label class="form-label fw-bold">Select Organisation User(s) <span class="text-danger">*</span></label>
                           <VueMultiselect
                              v-model="selectedUsers"
                              :options="filteredOrgUsers"
                              :multiple="true"
                              label="name"
                              track-by="id"
                              placeholder="Select User(s)..."
                              :show-labels="false"
                              :close-on-select="false"
                              :disabled="!!editingCommId"
                              class="vertex-comm-multiselect"
                           />
                        </template>
                        <template v-else>
                           <label class="form-label fw-bold">Contact Name <span class="text-danger">*</span></label>
                           <input type="text" class="form-control" v-model="commForm.name" placeholder="John Doe" :disabled="!!editingCommId" />
                        </template>
                     </div>

                     <!-- 3-Column Horizontal Grid for Dropdowns -->
                     <div class="row g-3 mb-3">
                        <div class="col-12 col-md-4">
                           <label class="form-label fw-bold">Client Pace</label>
                           <VueMultiselect
                              v-model="commForm.clientPace"
                              :options="['Methodical / Relaxed', 'Moderate / Standard', 'Fast-Paced', 'Urgent / Reactive']"
                              :searchable="false"
                              :allow-empty="true"
                              :show-labels="false"
                              placeholder="Select Client Pace..."
                              class="vertex-comm-multiselect"
                           />
                           <div
                              v-if="commForm.clientPace"
                              class="form-text text-muted bg-light border-start border-warning"
                              style="margin-top: 8px; padding: 8px 10px; font-size: 0.75rem; border-radius: 8px; border-left-width: 4px !important; line-height: 1.4;"
                           >
                              <i class="fas fa-info-circle text-warning me-1"></i>
                              <strong>Pace Guideline:</strong> {{ getPaceExplainer(commForm.clientPace) }}
                           </div>
                        </div>

                        <div class="col-12 col-md-4">
                           <label class="form-label fw-bold">Cost Sensitivity</label>
                           <VueMultiselect
                              v-model="commForm.costSensitivity"
                              :options="['High (Highly Budget-Conscious)', 'Moderate (Value-Driven)', 'Low (Not a Primary Concern)']"
                              :searchable="false"
                              :allow-empty="true"
                              :show-labels="false"
                              placeholder="Select Cost Sensitivity..."
                              class="vertex-comm-multiselect"
                           />
                           <div
                              v-if="commForm.costSensitivity"
                              class="form-text text-muted bg-light border-start border-primary"
                              style="margin-top: 8px; padding: 8px 10px; font-size: 0.75rem; border-radius: 8px; border-left-width: 4px !important; line-height: 1.4;"
                           >
                              <i class="fas fa-info-circle text-primary me-1"></i>
                              <strong>Budget Guideline:</strong> {{ getCostExplainer(commForm.costSensitivity) }}
                           </div>
                        </div>

                        <div class="col-12 col-md-4">
                           <label class="form-label fw-bold">Technicality</label>
                           <VueMultiselect
                              v-model="commForm.technicality"
                              :options="['Low (Business-Focused)', 'Moderate (Tech-Savvy)', 'High (Expert / Developer Level)']"
                              :searchable="false"
                              :allow-empty="true"
                              :show-labels="false"
                              placeholder="Select Technicality..."
                              class="vertex-comm-multiselect"
                           />
                           <div
                              v-if="commForm.technicality"
                              class="form-text text-muted bg-light border-start"
                              style="margin-top: 8px; padding: 8px 10px; font-size: 0.75rem; border-radius: 8px; border-left-width: 4px !important; line-height: 1.4; border-color: #7c3aed !important;"
                           >
                              <i class="fas fa-info-circle me-1" style="color: #7c3aed;"></i>
                              <strong>Technical Guideline:</strong> {{ getTechExplainer(commForm.technicality) }}
                           </div>
                        </div>
                     </div>

                     <!-- 2-Column Horizontal Grid for Decision & Communication Styles -->
                     <div class="row g-3">
                        <div class="col-12 col-md-6">
                           <label class="form-label fw-bold">Decision Style</label>
                           <textarea class="form-control" rows="3" v-model="commForm.decisionStyle" placeholder="eg. Mostly prefers options but sometimes needs to be provided strong recommendations"></textarea>
                        </div>

                        <div class="col-12 col-md-6">
                           <label class="form-label fw-bold">Communication Style</label>
                           <textarea class="form-control" rows="3" v-model="commForm.communicationStyle" placeholder="eg. Likes to discuss security concepts, talk tech and come to an agreement through discussions"></textarea>
                        </div>
                     </div>

                     <div class="modal-footer-custom d-flex justify-content-between align-items-center">
                        <button
                           v-if="editingCommId"
                           type="button"
                           class="action-btn-circle action-btn-circle--delete"
                           @click="deleteCommPrefFromModal"
                           :disabled="isLoading"
                           title="Delete communication preference"
                        >
                           <i class="fas fa-trash"></i>
                        </button>
                        <span v-else></span>
                        <div class="d-flex gap-2" style="gap: 10px;">
                           <button type="button" class="lead-btn lead-btn--ghost" @click="closeCommModal">Cancel</button>
                           <button
                              type="button"
                              class="lead-btn lead-btn--primary"
                              @click="saveCommPrefs"
                              :disabled="isLoadingUsers || isLoading || (!editingCommId && !commModalContact && ((profileData.hasAuthOrganisation == 1 && selectedUsers.length === 0) || (profileData.hasAuthOrganisation != 1 && !commForm.name.trim())))"
                           >
                              <i class="fas fa-save me-1"></i> Save Preference
                           </button>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </Transition>


      <!-- ── Delete Organisation Confirmation Modal ── -->
      <Transition name="lead-modal-fade">
         <div v-if="showDeleteOrgModal" class="lead-modal-overlay" @click.self="closeDeleteOrgModal">
            <div class="lead-modal" style="max-width: 450px;">
               <div class="lead-modal__header">
                  <h5 class="lead-modal__title"><i class="fas fa-exclamation-triangle text-danger"></i> Delete Organisation</h5>
                  <button type="button" @click="closeDeleteOrgModal" class="lead-modal__close">&times;</button>
               </div>
               <div class="lead-modal__divider"></div>
               <div class="lead-modal__body p-4 text-center">
                  <div class="mb-3">
                     <i class="fas fa-trash-alt fa-3x text-danger animate__animated animate__bounceIn"></i>
                  </div>
                  <h6 class="fw-bold text-dark mb-3">Confirm Permanent Deletion</h6>
                  <p class="text-muted mb-4">
                     Are you sure you want to delete this organisation and relevant data?
                  </p>
                  <div class="alert alert-danger text-start small">
                     <i class="fas fa-exclamation-circle me-1"></i>
                     This action is permanent and will cascade to all associated Leads, Notes, Proposals, and Contracts.
                  </div>
                  <div class="lead-modal__actions d-flex justify-content-center gap-2 mt-4">
                     <button type="button" class="btn btn-secondary px-4" @click="closeDeleteOrgModal">Cancel</button>
                     <button type="button" class="btn btn-danger px-4" @click="deleteOrganisation" :disabled="isDeleting">
                        <span v-if="isDeleting"><i class="fas fa-spinner fa-spin me-1"></i> Deleting...</span>
                        <span v-else><i class="fas fa-trash-alt me-1"></i> Yes, Delete</span>
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </Transition>

      <!-- ── Deactivate Organisation Confirmation Modal ── -->
      <Transition name="lead-modal-fade">
         <div v-if="showDeactivateOrgModal" class="lead-modal-overlay" @click.self="closeDeactivateOrgModal">
            <div class="lead-modal" style="max-width: 450px;">
               <div class="lead-modal__header">
                  <h5 class="lead-modal__title"><i class="fas fa-exclamation-triangle text-warning"></i> Deactivate Organisation</h5>
                  <button type="button" @click="closeDeactivateOrgModal" class="lead-modal__close">&times;</button>
               </div>
               <div class="lead-modal__divider"></div>
               <div class="lead-modal__body p-4 text-center">
                  <div class="mb-3">
                     <i class="fas fa-ban fa-3x text-warning animate__animated animate__bounceIn"></i>
                  </div>
                  <h6 class="fw-bold text-dark mb-3">Confirm Deactivation</h6>
                  <p class="text-muted mb-4">
                     Are you sure you want to deactivate this organisation?
                  </p>
                  <div class="alert alert-warning text-start small" style="background-color: #fffbeb; border-color: #fde68a; color: #92400e;">
                     <i class="fas fa-info-circle me-1"></i>
                     This will soft-delete the organisation record. Associated leads, proposals, and contracts will remain intact in the database but the organisation will be set as inactive.
                  </div>
                  <div class="lead-modal__actions d-flex justify-content-center gap-2 mt-4">
                     <button type="button" class="btn btn-secondary px-4" @click="closeDeactivateOrgModal">Cancel</button>
                     <button type="button" class="btn btn-warning px-4 text-white" @click="deactivateOrganisation" :disabled="isDeactivating" style="background-color: #f59e0b; border-color: #f59e0b;">
                        <span v-if="isDeactivating"><i class="fas fa-spinner fa-spin me-1"></i> Deactivating...</span>
                        <span v-else><i class="fas fa-ban me-1"></i> Yes, Deactivate</span>
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </Transition>

      <!-- ── Merge Organisation Modal ── -->
      <Transition name="lead-modal-fade">
         <div v-if="showMergeModal" class="lead-modal-overlay" @click.self="closeMergeModal">
            <div class="lead-modal" style="max-width: 680px;">
               <div class="lead-modal__header">
                  <h5 class="lead-modal__title">
                     <i class="fas fa-code-branch" style="color: #00768a;"></i> Merge Organisation
                  </h5>
                  <button type="button" @click="closeMergeModal" class="lead-modal__close">&times;</button>
               </div>
               <div class="lead-modal__divider"></div>
               <div class="lead-modal__body p-4">
                  <div class="alert d-flex align-items-start gap-2 small mb-3" style="background-color: #f0fdfa; border: 1px solid #99f6e4; border-left: 4px solid #00768a; color: #134e4a;">
                     <i class="fas fa-info-circle mt-1" style="color: #00768a;"></i>
                     <div>
                         Merging <strong>{{ profileData.name }}</strong> will transfer all associated leads, contacts, and communication preferences into the selected destination organisation.
                     </div>
                  </div>

                  <div v-if="profileData.has_auth_organisation == 1 || profileData.is_auth_synced || (profileData.auth_organisation_id && profileData.auth_organisation_id > 0)" class="alert d-flex align-items-center gap-2 small py-2 px-3 mb-3 border-0 rounded-3" style="background-color: #eff6ff; color: #1e40af; border-left: 4px solid #3b82f6 !important;">
                     <i class="fas fa-link text-primary"></i>
                     <div>
                        <strong>Auth-Synced Organisation:</strong> Because this organisation is linked/synced with Auth, only other <strong>Auth-synced organisations</strong> can be selected as the destination.
                     </div>
                  </div>

                  <!-- Destination Search / Autocomplete -->
                  <div class="mb-3">
                     <label class="lead-form-label fw-bold">Destination Organisation <span class="text-danger">*</span></label>
                     <VueMultiselect
                        v-model="selectedMergeTarget"
                        :options="mergeSearchResults"
                        :searchable="true"
                        :loading="isSearchingMergeTargets"
                        :internal-search="true"
                        :clear-on-select="false"
                        :close-on-select="true"
                        :preserve-search="false"
                        placeholder="Search destination organisation..."
                        label="name"
                        track-by="id"
                        :show-no-results="true"
                        select-label=""
                        deselect-label=""
                        selected-label=""
                        class="merge-target-multiselect"
                     >
                        <template #option="props">
                           <div class="merge-multiselect-option d-flex flex-column py-1">
                              <div class="d-flex align-items-center gap-2">
                                 <span class="merge-option-name fw-bold">{{ props.option.name }}</span>
                                 <span v-if="props.option.has_auth_organisation == 1 || props.option.is_auth_synced || (props.option.auth_organisation_id && props.option.auth_organisation_id > 0)" class="merge-auth-badge badge bg-primary-subtle text-primary border border-primary-subtle flex-shrink-0" style="font-size: 0.65rem; padding: 2px 6px;">
                                    <i class="fas fa-link me-1"></i>Auth Synced
                                 </span>
                              </div>
                              <span class="merge-option-sub small">{{ props.option.abn || props.option.email || 'No ABN' }}</span>
                           </div>
                        </template>
                        <template #noResult>
                           <div class="text-muted small py-1">No matching organisations found</div>
                        </template>
                     </VueMultiselect>
                  </div>

                  <!-- Merge Reason -->
                  <div class="mb-3">
                     <label class="lead-form-label fw-bold">Reason for Merge</label>
                     <textarea 
                        v-model="mergeReason" 
                        class="form-control" 
                        rows="2" 
                        placeholder="e.g. Company acquisition, duplicate record, restructuring..."
                     ></textarea>
                  </div>

                  <!-- Confirmation Checkbox -->
                  <div 
                     class="vertex-checkbox-card d-flex align-items-start gap-2 p-3 rounded-3 mb-3"
                     :class="{ 'vertex-checkbox-card--checked': mergeConfirmationChecked }"
                     @click="mergeConfirmationChecked = !mergeConfirmationChecked"
                  >
                     <div class="vertex-custom-checkbox pt-1" @click.stop>
                        <input 
                           class="form-check-input vertex-checkbox-input" 
                           type="checkbox" 
                           id="mergeConfirmCheckbox" 
                           v-model="mergeConfirmationChecked"
                        />
                     </div>
                     <label class="form-check-label small text-dark mb-0 user-select-none" for="mergeConfirmCheckbox" style="cursor: pointer; line-height: 1.45;" @click.stop="mergeConfirmationChecked = !mergeConfirmationChecked">
                        I understand that all active records from <strong>{{ profileData.name }}</strong> will be reassigned to <strong>{{ selectedMergeTarget ? selectedMergeTarget.name : 'the destination organisation' }}</strong>.
                     </label>
                  </div>

                  <div class="lead-modal__actions d-flex justify-content-end gap-2 mt-4">
                     <button type="button" class="btn btn-secondary px-3" @click="closeMergeModal">Cancel</button>
                     <button 
                        type="button" 
                        class="btn btn-vertex-primary px-4" 
                        :disabled="!selectedMergeTarget || !mergeConfirmationChecked || isMerging" 
                        @click="confirmMerge"
                     >
                        <i v-if="isMerging" class="fas fa-spinner fa-spin me-1"></i>
                        <i v-else class="fas fa-code-branch me-1"></i>
                        {{ isMerging ? 'Merging...' : 'Confirm Merge' }}
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </Transition>

      <!-- ── Unmerge Organisation Modal ── -->
      <Transition name="lead-modal-fade">
         <div v-if="showUnmergeModal" class="lead-modal-overlay" @click.self="closeUnmergeModal">
            <div class="lead-modal" style="max-width: 480px;">
               <div class="lead-modal__header">
                  <h5 class="lead-modal__title">
                     <i class="fas fa-undo" style="color: #00768a;"></i> Unmerge Organisation
                  </h5>
                  <button type="button" @click="closeUnmergeModal" class="lead-modal__close">&times;</button>
               </div>
               <div class="lead-modal__divider"></div>
               <div class="lead-modal__body p-4">
                  <div class="text-center mb-3">
                     <div class="d-inline-flex align-items-center justify-content-center rounded-circle mb-2" style="width: 56px; height: 56px; background-color: rgba(0, 118, 138, 0.1); color: #00768a;">
                        <i class="fas fa-undo fa-2x"></i>
                     </div>
                     <h6 class="fw-bold text-dark">Confirm Unmerge</h6>
                  </div>
                  <p class="small text-muted mb-3">
                     Are you sure you want to unmerge <strong>{{ unmergeTargetOrg ? (unmergeTargetOrg.name || unmergeTargetOrg.organisation_name) : '' }}</strong>? This will restore the organisation back to active status and return all originally transferred leads, contacts, and preferences.
                  </p>
                  <div class="lead-modal__actions d-flex justify-content-end gap-2 mt-4">
                     <button type="button" class="btn btn-secondary px-3" @click="closeUnmergeModal">Cancel</button>
                     <button 
                        type="button" 
                        class="btn btn-vertex-primary px-4" 
                        :disabled="isUnmerging" 
                        @click="confirmUnmerge"
                     >
                        <i v-if="isUnmerging" class="fas fa-spinner fa-spin me-1"></i>
                        <i v-else class="fas fa-undo me-1"></i>
                        {{ isUnmerging ? 'Unmerging...' : 'Confirm Unmerge' }}
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </Transition>

      <!-- ── Mark / Unmark Auth Creation Modal ── -->
      <Transition name="lead-modal-fade">
         <div v-if="authMarkModal.show" class="lead-modal-overlay" @click.self="closeAuthMarkModal">
            <div class="lead-modal" style="max-width: 460px;">
               <div class="lead-modal__header">
                  <h5 class="lead-modal__title">
                     <i :class="authMarkModal.type === 'mark' ? 'fas fa-clock text-info' : 'fas fa-times-circle text-danger'"></i>
                     {{ authMarkModal.type === 'mark' ? 'Mark for Auth Creation' : 'Unmark from Auth Creation' }}
                  </h5>
                  <button type="button" @click="closeAuthMarkModal" class="lead-modal__close">&times;</button>
               </div>
               <div class="lead-modal__divider"></div>
               <div class="lead-modal__body p-4 text-center">
                  <div class="mb-3">
                     <i v-if="authMarkModal.type === 'mark'" class="fas fa-clock fa-3x animate__animated animate__bounceIn" style="color: #00A0C1;"></i>
                     <i v-else class="fas fa-times-circle fa-3x text-danger animate__animated animate__bounceIn"></i>
                  </div>
                  <h6 class="fw-bold text-dark mb-2">
                     {{ authMarkModal.type === 'mark' ? 'Confirm Auth Creation Queue' : 'Confirm Unmark from Auth' }}
                  </h6>
                  <p class="text-muted mb-3" style="font-size: 0.95rem;">
                     Are you sure you want to {{ authMarkModal.type === 'mark' ? 'mark' : 'unmark' }}
                     <strong class="text-dark">{{ profileData.name || 'this organisation' }}</strong>
                     {{ authMarkModal.type === 'mark' ? 'for creation in Auth?' : 'from Auth creation?' }}
                  </p>
                  <div v-if="authMarkModal.type === 'mark'" class="alert alert-info text-start small mb-0" style="background-color: #f0f9ff; border-color: #bae6fd; color: #0369a1;">
                     <i class="fas fa-info-circle me-1"></i>
                     This organisation will be queued for creation in Auth.
                  </div>
                  <div v-else class="alert alert-warning text-start small mb-0" style="background-color: #fffbeb; border-color: #fde68a; color: #92400e;">
                     <i class="fas fa-exclamation-triangle me-1"></i>
                     This organisation will be removed from the Auth creation queue and reverted to unlinked.
                  </div>
                  <div class="lead-modal__actions d-flex justify-content-center gap-2 mt-4">
                     <button type="button" class="btn btn-secondary px-4" @click="closeAuthMarkModal" :disabled="authMarkModal.isSubmitting">Cancel</button>
                     <button type="button" 
                             class="btn px-4 text-white" 
                             :class="authMarkModal.type === 'mark' ? 'btn-vertex-primary' : 'btn-danger'" 
                             @click="confirmAuthMarkAction" 
                             :disabled="authMarkModal.isSubmitting">
                        <span v-if="authMarkModal.isSubmitting">
                           <i class="fas fa-spinner fa-spin me-1"></i> Processing...
                        </span>
                        <span v-else>
                           <i :class="authMarkModal.type === 'mark' ? 'fas fa-check me-1' : 'fas fa-times me-1'"></i>
                           {{ authMarkModal.type === 'mark' ? 'Yes, Mark Organisation' : 'Yes, Unmark' }}
                        </span>
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </Transition>

      <!-- ── Stage New Active Job Modal ── -->
      <Transition name="fade">
         <div v-if="showStageJobModal" class="lead-modal-overlay" @click.self="closeStageJobModal">
            <div class="lead-modal" style="max-width: 600px; background: #fff; border-radius: 16px; overflow: hidden; box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18);">
               <div class="lead-modal__header p-3 px-4 d-flex justify-content-between align-items-center border-bottom bg-white">
                  <div class="d-flex align-items-center gap-3">
                     <div style="width: 42px; height: 42px; border-radius: 10px; background: #e6f7fa; color: #00A0C1; display: flex; align-items: center; justify-content: center; font-size: 1.15rem; flex-shrink: 0;">
                        <i class="fas fa-plus"></i>
                     </div>
                     <div>
                        <h5 class="fw-bold mb-0 text-dark" style="font-size: 1.15rem; letter-spacing: -0.01em;">Stage New Workflow Job</h5>
                        <span class="text-muted small">For {{ profileData.name }}</span>
                     </div>
                  </div>
                  <button type="button" class="action-btn-circle action-btn-circle--close" @click="closeStageJobModal" title="Close" aria-label="Close">
                     <i class="fa-solid fa-xmark"></i>
                  </button>
               </div>

               <div class="lead-modal__body p-4">
                  <form @submit.prevent="submitStageJob">
                     <div class="mb-3">
                        <label class="form-label fw-semibold small text-dark">Select Blueprint <span class="text-danger">*</span></label>
                        <select
                           v-model="stageJobForm.blueprint_id"
                           class="form-select form-select-sm"
                           required
                           @change="onBlueprintSelected"
                           :disabled="isLoadingBlueprints"
                           style="border-radius: 8px; border-color: #cbd5e1;"
                        >
                           <option value="" disabled>-- Select a Job Blueprint --</option>
                           <option v-for="bp in availableBlueprints" :key="bp.id" :value="bp.id">
                              {{ bp.title }} {{ bp.auth_item_code ? '(' + bp.auth_item_code + ')' : '' }} - {{ (bp.tasks && bp.tasks.length) || 0 }} tasks
                           </option>
                        </select>
                        <div v-if="isLoadingBlueprints" class="form-text small text-teal mt-1">
                           <i class="fas fa-circle-notch fa-spin me-1"></i> Loading available blueprints...
                        </div>
                     </div>

                     <div class="mb-3">
                        <label class="form-label fw-semibold small text-dark">Job Title <span class="text-danger">*</span></label>
                        <input
                           type="text"
                           class="form-control form-control-sm"
                           v-model="stageJobForm.title"
                           placeholder="Enter custom job title..."
                           required
                           style="border-radius: 8px; border-color: #cbd5e1;"
                        />
                     </div>

                     <div class="mb-3">
                        <label class="form-label fw-semibold small text-dark">Job Description / Scope Notes</label>
                        <textarea
                           class="form-control form-control-sm"
                           rows="3"
                           v-model="stageJobForm.description"
                           placeholder="Optional notes or context for this staged job..."
                           style="border-radius: 8px; border-color: #cbd5e1;"
                        ></textarea>
                     </div>

                     <div class="d-flex justify-content-end gap-2 pt-3 border-top" style="border-color: #f1f5f9 !important;">
                        <button type="button" class="btn btn-outline-secondary btn-sm px-3 fw-semibold" style="border-radius: 8px; height: 36px;" @click="closeStageJobModal">Cancel</button>
                        <button
                           type="submit"
                           class="btn org-jobs-btn-primary btn-sm px-4 d-inline-flex align-items-center gap-2"
                           :disabled="isStagingJob || !stageJobForm.blueprint_id || !stageJobForm.title"
                           style="height: 36px;"
                        >
                           <i v-if="isStagingJob" class="fas fa-circle-notch fa-spin me-1"></i>
                           <span>{{ isStagingJob ? 'Staging Job...' : 'Stage Job' }}</span>
                        </button>
                     </div>
                  </form>
               </div>
            </div>
         </div>
      </Transition>

      <!-- ── Organisation Active Job Tasks Modal ── -->
      <Transition name="fade">
         <div v-if="showOrgJobDetailsModal" class="lead-modal-overlay" @click.self="closeOrgActiveJobModal">
            <div class="lead-modal lead-modal--wide" style="max-width: 960px; background: #fff; border-radius: 16px; overflow: hidden; box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18);">
               <div class="lead-modal__header p-3 px-4 d-flex justify-content-between align-items-center border-bottom bg-white">
                  <div class="d-flex align-items-center gap-3">
                     <div style="width: 44px; height: 44px; border-radius: 10px; background: #e6f7fa; color: #00A0C1; display: flex; align-items: center; justify-content: center; font-size: 1.25rem; flex-shrink: 0;">
                        <i class="fa-solid fa-bolt"></i>
                     </div>
                     <div>
                        <h5 class="fw-bold mb-1 text-dark" style="font-size: 1.15rem; letter-spacing: -0.01em;">{{ selectedOrgActiveJob?.title || 'Active Job Workflow' }}</h5>
                        <div class="d-flex align-items-center gap-2 flex-wrap small text-muted">
                           <span class="d-inline-flex align-items-center"><i class="fas fa-building me-1 text-muted"></i> {{ profileData.name }}</span>
                           <span>•</span>
                           <span class="d-inline-flex align-items-center"><i class="fa-regular fa-calendar me-1 text-muted"></i> Staged {{ formatDate(selectedOrgActiveJob?.created_at) }}</span>
                           <span>•</span>
                           <span v-if="selectedOrgActiveJob?.lead_id" class="aj-trigger-badge aj-trigger-badge--lead">
                              <i class="fas fa-user-plus me-1"></i> Converted Lead
                           </span>
                           <span v-else-if="selectedOrgActiveJob?.contract_id" class="aj-trigger-badge aj-trigger-badge--contract">
                              <i class="fas fa-file-signature me-1"></i> Executed Contract
                           </span>
                           <span v-else class="aj-trigger-badge aj-trigger-badge--manual">
                              <i class="fas fa-hand-pointer me-1"></i> Manual Staging
                           </span>
                        </div>
                     </div>
                  </div>
                  <button type="button" class="action-btn-circle action-btn-circle--close" @click="closeOrgActiveJobModal" title="Close" aria-label="Close">
                     <i class="fa-solid fa-xmark"></i>
                  </button>
               </div>

               <div class="lead-modal__body p-4" style="max-height: 72vh; overflow-y: auto;">
                  <div v-if="loadingOrgJobDetails" class="text-center py-5">
                     <i class="fas fa-circle-notch fa-spin fa-2x text-teal mb-2"></i>
                     <p class="text-muted small">Loading active job structure and tasks...</p>
                  </div>

                  <div v-else-if="selectedOrgActiveJob">
                     <!-- Quick Controls & Status Changers Bar (Segmented Capsule) -->
                     <div class="aj-segmented-bar p-3 mb-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div class="d-flex align-items-center gap-2 flex-wrap">
                           <span class="aj-segmented-label me-1"><i class="fa-solid fa-sliders me-1 text-teal"></i>Status:</span>
                           <div class="aj-segmented-capsule">
                              <button
                                 type="button"
                                 class="aj-segmented-pill"
                                 :class="{ 'active active--pending': selectedOrgActiveJob.job_status === 'Pending' }"
                                 @click="updateActiveJobStatus(selectedOrgActiveJob, 'Pending')"
                              >
                                 <span class="aj-pill-dot"></span>
                                 Pending
                              </button>
                              <button
                                 type="button"
                                 class="aj-segmented-pill"
                                 :class="{ 'active active--in-progress': selectedOrgActiveJob.job_status === 'In Progress' }"
                                 @click="updateActiveJobStatus(selectedOrgActiveJob, 'In Progress')"
                              >
                                 <span class="aj-pill-dot"></span>
                                 In Progress
                              </button>
                              <button
                                 type="button"
                                 class="aj-segmented-pill"
                                 :class="{ 'active active--completed': selectedOrgActiveJob.job_status === 'Completed' }"
                                 @click="updateActiveJobStatus(selectedOrgActiveJob, 'Completed')"
                              >
                                 <span class="aj-pill-dot"></span>
                                 Completed
                              </button>
                           </div>
                        </div>

                        <div class="d-flex align-items-center gap-2 flex-wrap">
                           <span class="aj-segmented-label me-1"><i class="fa-solid fa-heart-pulse me-1 text-teal"></i>Health:</span>
                           <div class="aj-segmented-capsule">
                              <button
                                 type="button"
                                 class="aj-segmented-pill"
                                 :class="{ 'active active--on-track': selectedOrgActiveJob.health_indicator === 'On Track' }"
                                 @click="updateActiveJobHealth(selectedOrgActiveJob, 'On Track')"
                              >
                                 <span class="aj-pill-dot"></span>
                                 On Track
                              </button>
                              <button
                                 type="button"
                                 class="aj-segmented-pill"
                                 :class="{ 'active active--behind': selectedOrgActiveJob.health_indicator === 'Behind Plan' }"
                                 @click="updateActiveJobHealth(selectedOrgActiveJob, 'Behind Plan')"
                              >
                                 <span class="aj-pill-dot"></span>
                                 Behind
                              </button>
                              <button
                                 type="button"
                                 class="aj-segmented-pill"
                                 :class="{ 'active active--at-risk': selectedOrgActiveJob.health_indicator === 'At Risk' }"
                                 @click="updateActiveJobHealth(selectedOrgActiveJob, 'At Risk')"
                              >
                                 <span class="aj-pill-dot"></span>
                                 At Risk
                              </button>
                           </div>
                        </div>
                     </div>

                     <!-- Top Info Cards: Horizontal Metric Strip -->
                     <div class="row g-3 mb-4">
                        <div class="col-12 col-md-4">
                           <div class="aj-summary-card p-3 bg-white h-100 d-flex align-items-center gap-3">
                              <div class="aj-summary-icon" style="background: #e6f7fa; color: #00A0C1; width: 40px; height: 40px; font-size: 1rem; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                                 <i class="fa-solid fa-project-diagram"></i>
                              </div>
                              <div class="flex-grow-1 overflow-hidden">
                                 <span class="aj-summary-label d-block mb-1">Source Blueprint</span>
                                 <div class="d-flex align-items-center gap-2 flex-wrap">
                                    <strong class="text-dark text-truncate" :title="selectedOrgActiveJob.blueprint?.title || 'Standard Blueprint'">
                                       {{ selectedOrgActiveJob.blueprint?.title || 'Standard Blueprint' }}
                                    </strong>
                                    <span v-if="selectedOrgActiveJob.blueprint?.auth_item_code" class="aj-code-tag">
                                       {{ selectedOrgActiveJob.blueprint.auth_item_code }}
                                    </span>
                                 </div>
                              </div>
                           </div>
                        </div>

                        <div class="col-12 col-md-4">
                           <div class="aj-summary-card p-3 bg-white h-100 d-flex align-items-center gap-3">
                              <div class="aj-summary-icon" style="background: #e0f2fe; color: #0284c7; width: 40px; height: 40px; font-size: 1rem; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                                 <i class="fa-solid fa-bolt"></i>
                              </div>
                              <div class="flex-grow-1 overflow-hidden">
                                 <span class="aj-summary-label d-block mb-1">Trigger Source</span>
                                 <div>
                                    <span v-if="selectedOrgActiveJob.lead_id" class="aj-trigger-badge aj-trigger-badge--lead">
                                       <i class="fas fa-user-plus me-1"></i> Converted Lead
                                    </span>
                                    <span v-else-if="selectedOrgActiveJob.contract_id" class="aj-trigger-badge aj-trigger-badge--contract">
                                       <i class="fas fa-file-signature me-1"></i> Executed Contract
                                    </span>
                                    <span v-else class="aj-trigger-badge aj-trigger-badge--manual">
                                       <i class="fas fa-hand-pointer me-1"></i> Manual Staging
                                    </span>
                                 </div>
                              </div>
                           </div>
                        </div>

                        <div class="col-12 col-md-4">
                           <div class="aj-summary-card p-3 bg-white h-100 d-flex align-items-center gap-3">
                              <div class="aj-summary-icon" style="background: #d1fae5; color: #059669; width: 40px; height: 40px; font-size: 1rem; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                                 <i class="fa-solid fa-chart-pie"></i>
                              </div>
                              <div class="flex-grow-1">
                                 <div class="d-flex justify-content-between align-items-center mb-1">
                                    <span class="aj-summary-label">Progress</span>
                                    <span class="aj-progress-pill">{{ calculateJobProgress(selectedOrgActiveJob) }}%</span>
                                 </div>
                                 <div class="aj-progress-track mb-1" style="height: 6px;">
                                    <div
                                       class="aj-progress-bar"
                                       role="progressbar"
                                       :style="{ width: calculateJobProgress(selectedOrgActiveJob) + '%' }"
                                    ></div>
                                 </div>
                                 <span class="text-muted small" style="font-size: 0.74rem;">
                                    {{ selectedOrgActiveJob.completed_tasks_count || 0 }} of {{ selectedOrgActiveJob.tasks_count || 0 }} Tasks
                                 </span>
                              </div>
                           </div>
                        </div>
                     </div>

                     <!-- Task Hierarchy Header -->
                     <div class="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom" style="border-color: #f1f5f9 !important;">
                        <div class="d-flex align-items-center gap-2">
                           <div class="aj-summary-icon" style="background: #e6f7fa; color: #00A0C1; width: 32px; height: 32px; font-size: 0.9rem; border-radius: 8px; display: flex; align-items: center; justify-content: center;">
                              <i class="fa-solid fa-list-check"></i>
                           </div>
                           <div>
                              <h6 class="fw-bold text-dark mb-0" style="font-size: 0.96rem;">Operational Tasks Plan</h6>
                              <p class="text-muted small mb-0" style="font-size: 0.75rem;">Execution pipeline and delivery milestones</p>
                           </div>
                        </div>
                        <span class="badge rounded-pill bg-light text-secondary border px-3 py-1" style="font-size: 0.75rem; font-weight: 600;">
                           {{ (selectedOrgActiveJob.tasks && selectedOrgActiveJob.tasks.length) || 0 }} Top-Level Tasks
                        </span>
                     </div>

                     <div v-if="!selectedOrgActiveJob.tasks || !selectedOrgActiveJob.tasks.length" class="p-4 bg-light rounded-3 text-center text-muted border">
                        <i class="fas fa-inbox fa-2x mb-2 opacity-50"></i>
                        <p class="mb-0 small">No tasks registered for this job.</p>
                     </div>

                     <div v-else class="d-flex flex-column gap-2">
                        <div
                           v-for="(task, idx) in selectedOrgActiveJob.tasks"
                           :key="task.id"
                           class="org-task-item p-3 border bg-white"
                           :class="{ 'org-task-item--completed': task.task_status === 'Completed' }"
                        >
                           <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                              <div class="d-flex align-items-start gap-3 flex-grow-1">
                                 <!-- Task Checkbox / Status Circle Button -->
                                 <button
                                    type="button"
                                    class="aj-task-check-btn flex-shrink-0 mt-1"
                                    :class="{
                                       'is-completed': task.task_status === 'Completed',
                                       'is-in-progress': task.task_status === 'InProgress' || task.task_status === 'In Progress'
                                    }"
                                    @click="updateActiveTaskStatus(task, task.task_status === 'Completed' ? 'Planned' : 'Completed')"
                                    :title="task.task_status === 'Completed' ? 'Mark as Incomplete' : 'Mark as Completed'"
                                    :disabled="updatingTaskId === task.id"
                                    aria-label="Toggle task completion"
                                 >
                                    <i v-if="updatingTaskId === task.id" class="fas fa-circle-notch fa-spin text-muted" style="font-size: 0.85rem;"></i>
                                    <i v-else-if="task.task_status === 'Completed'" class="fa-solid fa-check"></i>
                                    <i v-else-if="task.task_status === 'InProgress' || task.task_status === 'In Progress'" class="fa-solid fa-play" style="font-size: 0.65rem;"></i>
                                    <i v-else class="fa-solid fa-check" style="opacity: 0.35;"></i>
                                 </button>

                                 <div class="flex-grow-1">
                                    <div class="d-flex align-items-center gap-2 flex-wrap mb-1">
                                       <span class="aj-task-num-pill">#{{ idx + 1 }}</span>
                                       <strong class="aj-task-title text-dark" :class="{ 'text-decoration-line-through text-muted': task.task_status === 'Completed' }">
                                          {{ task.title }}
                                       </strong>
                                    </div>

                                    <p v-if="task.description" class="text-muted small mb-2 mt-1" style="font-size: 0.82rem; line-height: 1.45;">{{ task.description }}</p>

                                    <div class="d-flex align-items-center gap-2 flex-wrap mt-2">
                                       <span class="aj-meta-pill">
                                          <i class="fa-solid fa-layer-group me-1 text-muted"></i> {{ task.hierarchy_level || 'Task' }}
                                       </span>
                                       <span class="aj-meta-pill">
                                          <i class="fas me-1 text-muted" :class="task.execution_mode === 'Concurrent' ? 'fa-arrows-split-up-and-left' : 'fa-arrow-down-short-wide'"></i> {{ task.execution_mode || 'Sequential' }}
                                       </span>
                                       <span v-if="task.assigned_role" class="aj-meta-pill aj-meta-pill--role">
                                          <i class="fa-solid fa-user-gear me-1"></i> {{ task.assigned_role }}
                                       </span>
                                       <span v-if="task.is_approval_gate" class="aj-meta-pill aj-meta-pill--gate">
                                          <i class="fa-solid fa-shield-halved me-1"></i> Approval Gate
                                       </span>
                                       <span v-if="task.integration_target && task.integration_target !== 'None'" class="aj-meta-pill aj-meta-pill--target">
                                          <i class="fa-solid fa-plug me-1"></i> {{ task.integration_target }}
                                       </span>
                                    </div>
                                 </div>
                              </div>

                              <!-- Task Status Selector Dropdown -->
                              <div class="d-flex align-items-center gap-2 flex-shrink-0">
                                 <select
                                    class="form-select form-select-sm aj-task-status-select"
                                    :class="'aj-task-status-select--' + (task.task_status || 'planned').toLowerCase().replace(' ', '')"
                                    :value="task.task_status || 'Planned'"
                                    @change="updateActiveTaskStatus(task, $event.target.value)"
                                    :disabled="updatingTaskId === task.id"
                                 >
                                    <option value="Planned">Planned</option>
                                    <option value="InProgress">In Progress</option>
                                    <option value="Completed">Completed</option>
                                    <option value="Blocked">Blocked</option>
                                    <option value="Skipped">Skipped</option>
                                 </select>
                              </div>
                           </div>

                           <!-- Subtasks -->
                           <div v-if="task.sub_tasks && task.sub_tasks.length" class="ms-4 mt-3 ps-3 border-start d-flex flex-column gap-2" style="border-color: #e2e8f0 !important;">
                              <div
                                 v-for="(sub, sIdx) in task.sub_tasks"
                                 :key="sub.id"
                                 class="aj-subtask-item p-2 px-3 rounded-2 border d-flex justify-content-between align-items-center gap-2 flex-wrap"
                                 :class="{ 'aj-subtask-item--completed': sub.task_status === 'Completed' }"
                              >
                                 <div class="d-flex align-items-center gap-2 flex-grow-1">
                                    <button
                                       type="button"
                                       class="aj-task-check-btn aj-task-check-btn--sub flex-shrink-0"
                                       :class="{
                                          'is-completed': sub.task_status === 'Completed',
                                          'is-in-progress': sub.task_status === 'InProgress' || sub.task_status === 'In Progress'
                                       }"
                                       @click="updateActiveTaskStatus(sub, sub.task_status === 'Completed' ? 'Planned' : 'Completed')"
                                       :disabled="updatingTaskId === sub.id"
                                       aria-label="Toggle subtask completion"
                                    >
                                       <i v-if="updatingTaskId === sub.id" class="fas fa-circle-notch fa-spin text-muted" style="font-size: 0.7rem;"></i>
                                       <i v-else-if="sub.task_status === 'Completed'" class="fa-solid fa-check" style="font-size: 0.65rem;"></i>
                                       <i v-else-if="sub.task_status === 'InProgress' || sub.task_status === 'In Progress'" class="fa-solid fa-play" style="font-size: 0.55rem;"></i>
                                       <i v-else class="fa-solid fa-check" style="opacity: 0.35; font-size: 0.65rem;"></i>
                                    </button>
                                    <span class="aj-task-num-pill aj-task-num-pill--sub">#{{ idx + 1 }}.{{ sIdx + 1 }}</span>
                                    <strong class="text-dark small" :class="{ 'text-decoration-line-through text-muted': sub.task_status === 'Completed' }">{{ sub.title }}</strong>
                                    <span v-if="sub.assigned_role" class="aj-meta-pill aj-meta-pill--role small"><i class="fa-solid fa-user-gear me-1"></i> {{ sub.assigned_role }}</span>
                                 </div>
                                 <div class="flex-shrink-0">
                                    <select
                                       class="form-select form-select-sm aj-task-status-select aj-task-status-select--sub"
                                       :class="'aj-task-status-select--' + (sub.task_status || 'planned').toLowerCase().replace(' ', '')"
                                       :value="sub.task_status || 'Planned'"
                                       @change="updateActiveTaskStatus(sub, $event.target.value)"
                                       :disabled="updatingTaskId === sub.id"
                                    >
                                       <option value="Planned">Planned</option>
                                       <option value="InProgress">In Progress</option>
                                       <option value="Completed">Completed</option>
                                       <option value="Blocked">Blocked</option>
                                       <option value="Skipped">Skipped</option>
                                    </select>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>

               <div class="lead-modal__footer p-3 px-4 border-top d-flex justify-content-between align-items-center bg-white" style="border-color: #f1f5f9 !important;">
                  <div class="d-flex align-items-center gap-2 text-muted small">
                     <i class="fa-solid fa-shield-halved text-teal"></i>
                     <span>Changes save automatically to job state</span>
                  </div>
                  <button type="button" class="btn btn-outline-secondary btn-sm px-4 fw-semibold" style="border-radius: 8px; height: 36px;" @click="closeOrgActiveJobModal">Close</button>
               </div>
            </div>
         </div>
      </Transition>

      <!-- ── Company Background or Purpose Rich Text Modal ── -->
      <Transition name="lead-modal-fade">
         <div v-if="showCompanyBackgroundModal" class="lead-modal-overlay" @click.self="closeCompanyBackgroundModal">
            <div class="lead-modal" style="max-width: 680px;">
               <div class="lead-modal__header">
                  <h5 class="lead-modal__title">
                     <span class="cb-modal-icon-badge">
                        <i class="fas fa-align-left"></i>
                     </span>
                     Edit Company Background or Purpose
                  </h5>
                  <button type="button" class="lead-modal__close cb-modal-close-btn" @click="closeCompanyBackgroundModal" title="Close">&times;</button>
               </div>
               <div class="lead-modal__divider"></div>
               <div class="lead-modal__body p-4">
                  <div class="cb-editor-shell">
                     <div class="cb-modern-toolbar">
                        <button type="button" class="cb-modern-toolbar-btn" @mousedown.prevent="execBackgroundCommand('bold')" title="Bold">
                           <i class="fas fa-bold"></i>
                        </button>
                        <button type="button" class="cb-modern-toolbar-btn" @mousedown.prevent="execBackgroundCommand('italic')" title="Italic">
                           <i class="fas fa-italic"></i>
                        </button>
                        <button type="button" class="cb-modern-toolbar-btn" @mousedown.prevent="execBackgroundCommand('underline')" title="Underline">
                           <i class="fas fa-underline"></i>
                        </button>
                        <span class="cb-toolbar-divider"></span>
                        <button type="button" class="cb-modern-toolbar-btn" @mousedown.prevent="execBackgroundCommand('insertUnorderedList')" title="Bullet List">
                           <i class="fas fa-list-ul"></i>
                        </button>
                     </div>
                     <div
                        ref="companyBackgroundEditor"
                        class="cb-modern-editor"
                        contenteditable="true"
                        @paste="onCompanyBackgroundPaste"
                     ></div>
                  </div>
                  <div class="lead-modal__actions d-flex justify-content-end gap-2 mt-4">
                     <button type="button" class="btn btn-secondary px-4" style="border-radius: 8px; font-weight: 600;" @click="closeCompanyBackgroundModal">Cancel</button>
                     <button type="button" class="btn btn-vertex-primary px-4" style="border-radius: 8px;" @click="saveCompanyBackgroundModal">
                        <i class="fas fa-check me-1"></i> Save Changes
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </Transition>
   </div>
</div>
</template>

<script>
import moment from 'moment';
import LoadingPage from '@/components/Utilities/vertex-loading.vue';
import { getBusinessNumberShortLabel, getBusinessNumberLabel } from '@/plugins/businessValidator.js';
import VueMultiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import "@/assets/scss/vue-multiselect.scss";

export default {
   name: 'ClientsDashboard',
   components: {
      LoadingPage,
      VueMultiselect
   },
   data() {
      return {
         isLoading: false,
         isSyncing: false,
         currentFetchingUuid: '',
         activeTab: 'profile',
         orgActiveJobs: [],
         isJobsLoading: false,
         showOrgJobDetailsModal: false,
         selectedOrgActiveJob: null,
         loadingOrgJobDetails: false,
         jobStatusFilter: 'all',
         jobSearchQuery: '',
         showStageJobModal: false,
         availableBlueprints: [],
         isLoadingBlueprints: false,
         isStagingJob: false,
         updatingTaskId: null,
         stageJobForm: {
            blueprint_id: '',
            title: '',
            description: ''
         },
         showCompanyBackgroundModal: false,
         sectionOpen: {
            organisationDetails: true,
            contactInformation: false,
            relationships: false,
            accountInformation: false,
            supplierGovernance: false,
            socialPurpose: false,
            technologyStack: false
         },
         selectedLeadId: '',
         totalOrganisationsCount: 0,
         leads: [],
         searchQuery: '',
         showSearchDropdown: false,
         selectedPartnerToAdd: '',
         availablePartners: ['Cyber Alliance', 'SecureNet Inc', 'Defendify Ltd', 'ShieldCorp'],
         formalPolicyOptions: [
            'Code of Ethics',
            'Supplier Code of Conduct',
            'Anti-Corruption and Anti-Bribery Policies',
            'Environmental Sustainability Policy',
            'Diversity, Equity & Inclusion Policy',
            'None of the above'
         ],
         positivePracticeOptions: [
            { key: 'Living Wage - we guarantee all employees are paid a verified living wage', title: 'Living Wage', desc: 'we guarantee all employees are paid a verified living wage' },
            { key: 'Carbon Reduction - we track our GHG emissions and have a formal reduction target', title: 'Carbon Reduction', desc: 'we track our GHG emissions and have a formal reduction target' },
            { key: 'Renewable Energy - we source more than 50% of our energy from renewable sources', title: 'Renewable Energy', desc: 'we source more than 50% of our energy from renewable sources' },
            { key: 'Waste Reduction - we have a zero-waste-to-landfill policy or formal recycling/circular economy initiatives', title: 'Waste Reduction', desc: 'we have a zero-waste-to-landfill policy or formal recycling/circular economy initiatives' },
            { key: 'Community Giving - we formally donate a set percentage of revenue or profit to charitable causes', title: 'Community Giving', desc: 'we formally donate a set percentage of revenue or profit to charitable causes' },
            { key: 'None of the above', title: 'None of the above', desc: '' }
         ],
         certCategories: {
            infoSec: [
               'ISO/IEC 27001',
               'SOC 2 (Type II)',
               'PCI DSS',
               'Essential Eight Assessment (Australia)'
            ],
            esg: [
               'Certified B Corporation (B Corp)',
               'EcoVadis',
               'Sedex (SMETA)',
               'ISO 14001',
               'FSC (Forest Stewardship Council) / Fairtrade'
            ],
            diversity: [
               'Supply Nation (Australia)',
               'Social Traders (Australia)',
               'WEConnect International',
               'Disability:IN',
               'NMSDC / WBENC (US & Global)'
            ],
            quality: [
               { name: 'ISO 9001', summary: 'Quality Management Systems (QMS)', desc: 'The foundational standard for Quality Management Systems. Proves robust processes to consistently provide products/services that meet customer and regulatory requirements.' },
               { name: 'ISO 45001', summary: 'Occupational Health & Safety (OH&S)', desc: 'The global standard for Occupational Health and Safety, crucial if workers are on-site or in high-risk environments.' }
            ]
         },
         underservedTypeOptions: [
            'Low Income',
            'Poor/Very Poor',
            'Chronic Discrimination - Gender',
            'Chronic Discrimination - Race',
            'Chronic Discrimination - Age',
            'Chronic Discrimination - Ethnicity',
            'Chronic Discrimination - Colour',
            'Chronic Discrimination - Disability',
            'Chronic Discrimination - Political Opinion',
            'Chronic Discrimination - Sexual Orientation',
            'Chronic Discrimination - Religion',
            'Chronic Discrimination - Social Origin',
            'Chronic Discrimination - Other'
         ],
         ratingOptions: ['Low', 'Medium', 'High'],
         organisationTypeOptions: ['Organisation', 'Government', 'Individual'],
         purposeDrivenTypeOptions: [
            'None',
            'Certified BCorp',
            'Not For Profit Organisation',
            'Registered Charity',
            'Registered Social Enterprise',
            'Worker Owned Cooperative'
         ],
         industryOptions: [
            'Aerospace and Defense',
            'Agriculture and Forestry',
            'Alcohol',
            'Animal Products or Services',
            'Architecture and Design',
            'Arts and Culture',
            'Automotive',
            'Banking and Financial Services (General)',
            'Biotechnology',
            'Chemicals',
            'Construction and Engineering',
            'Consumer Goods',
            'Debt Collection Services',
            'Education and Training',
            'Energy (Renewable and Alternative)',
            'Entertainment and Media',
            'Firearms / Weapons',
            'Food and Beverage',
            'Fossil Fuels',
            'Gambling',
            'Genetically Modified Organisms',
            'Government and Public Administration',
            'Healthcare and Medical',
            'Hospitality and Tourism',
            'Illegal Products or Subject to Phase Out',
            'Industries at Risk of Human Rights Violations',
            'Information Technology and Software',
            'Insurance (General)',
            'Legal Services',
            'Logistics and Supply Chain',
            'Manufacturing',
            'Mining',
            'Non-Profit and Philanthropy',
            'Nuclear Power or Hazardous Materials',
            'Payday, Short Term or High Interest Lending',
            'Pharmaceuticals',
            'Pornography',
            'Prisons',
            'Professional Services and Consulting',
            'Real Estate',
            'Retail and E-commerce',
            'Tax Advisory Services',
            'Telecommunications',
            'Tobacco',
            'Transportation',
            'Utilities',
            'Volunteer Placement to Orphanages',
            'Whole Life Insurance',
            'Other'
         ],
         disclosureIndustryOptions: [
            'Alcohol',
            'Tobacco',
            'Gambling',
            'Firearms / Weapons',
            'Pornography',
            'Payday, Short Term or High Interest Lending',
            'Fossil Fuels',
            'Mining',
            'Nuclear Power or Hazardous Materials',
            'Prisons',
            'Whole Life Insurance',
            'Debt Collection Services',
            'Volunteer Placement to Orphanages',
            'Tax Advisory Services',
            'Animal Products or Services',
            'Genetically Modified Organisms',
            'Illegal Products or Subject to Phase Out',
            'Industries at Risk of Human Rights Violations'
         ],
         purposeOptions: [
            'Advancing Culture',
            'Advancing Education',
            'Advancing Health',
            'Promote or Oppose Law/Government Policy/Practice',
            'Advancing Natural Environment',
            'Promoting or Protecting Human Rights',
            'Purposes Beneficial to the General Public',
            'Promoting Reconciliation, Mutual Respect and Tolerance',
            'Advancing Religion',
            'Advancing Social or Public Welfare',
            'Advancing Security or Safety of Australia or Australian Public',
            'Preventing or Relieving Suffering of Animals'
         ],
         populationOptions: [
            'Aboriginal or TSI',
            'Adults',
            'Aged Persons',
            'Children',
            'Communities Overseas',
            'Early Childhood',
            'Ethnic Groups',
            'Families',
            'Females',
            'Financially Disadvantaged',
            'LGBTIQA+',
            'General Community in Australia',
            'Males',
            'Migrants, Refugees or Asylum Seekers',
            'Other Beneficiaries',
            'Other Charities',
            'People at Risk of Homelessness',
            'People with Chronic Illness',
            'People with Disabilities',
            'Pre/Post Release Offenders',
            'Rural/Regional/Remote Communities',
            'Unemployed Persons',
            'Veterans or Their Families',
            'Victims of Crime',
            'Victims of Disasters',
            'Youth',
            'Animals',
            'Environment',
            'Other Gender Identities'
         ],
         isCheckingAcnc: false,
         acncValidationMessage: '',
         acncValidationStatus: '',
         mspOptions: [],
         partnerOptions: [],
         ecosystemOptions: [],
         showCommModal: false,
         showDeleteOrgModal: false,
         isDeleting: false,
         showDeactivateOrgModal: false,
         isDeactivating: false,
         isRestoringOrg: false,
         mergedOrganisations: [],
         isMergedOrgsLoading: false,
         showMergeModal: false,
         isMerging: false,
         selectedMergeTarget: null,
         mergeSearchResults: [],
         isSearchingMergeTargets: false,
         mergeReason: '',
         mergeConfirmationChecked: false,
         showUnmergeModal: false,
         isUnmerging: false,
         unmergeTargetOrg: null,
         bdms: [],
         vertexUsers: [],
         authMarkModal: {
            show: false,
            type: 'mark',
            isSubmitting: false
         },
         profileData: {
            id: '',
            uuid: '',
            name: '',
            abn: '',
            firstName: '',
            lastName: '',
            leadName: '',
            phone: '',
            email: '',
            address: '',
            background: '',
            firstContactDate: '',
            clientType: 'Lead',
            isTestData: false,
            isAuthSynced: false,
            isAuthDeleted: false,
            isSupplier: false,
            isPartner: true,
            currency: 'AUD',
            assigned_user_id: 0,
            clients: [],
            suppliers: [],
            partners: [],
            leadTypeId: '',
            leadStatusId: '',
            leadPriorityId: '',
            hasAuthOrganisation: 0,
            authOrganisationName: '',
            authOrganisationUuid: '',
            authOrganisationId: 0,
            organisationUuid: '',
            xeroContactId: '',
            isMspManaged: false,
            mspUuid: '',
            isPartnerReferred: false,
            partnerUuid: '',
            isEcosystem: false,
            ecosystemUuid: '',
            is_merged: false,
            isMerged: false,
            isDeactivated: false,
            is_deactivated: false,
            is_inactive: false,
            merged_into_uuid: '',
            merged_into_name: '',
            merged_at: null,
            merged_by: 0,
            merged_by_name: '',
            merge_reason: '',
            merge_snapshot: '',
            former_names: [],
            diversity_form_completed: false,
            code_of_conduct_accepted_date: '',
            security_risk_rating: '',
            esg_practices_rating: '',
            is_independent_private_business: false,
            formal_written_policies: [],
            positive_practices: [],
            certifications: [],
            certifications_other: '',
            organisation_type: 'Organisation',
            purpose_driven_type: 'None',
            underserved_beneficiaries: false,
            underserved_types: [],
            organisational_purpose: [],
            population_served: [],
            is_pbi: false,
            is_hpc: false,
            reporting_current: false,
            acnc_town_city: '',
            acnc_state: '',
            acnc_postcode: '',
            is_local_supplier: false,
            is_women_owned: false,
            is_minority_owned: false,
            is_indigenous_owned: false,
            industry_uuid: '',
            industry: '',
            is_disclosure_industry: false,
            technology_stack: []
         },
          timelineEvents: [],
          initialProfileData: {},
          orgContacts: [],
          isContactsLoading: false,
          isContactsSyncing: false,
          contactsSearchQuery: '',
          contactsRoleFilter: [],
          showContactsFilterDropdown: false,
          copiedContactEmail: null,
          clientCommunications: [],
          orgUsers: [],
         selectedUsers: [],
         commForm: {
            name: '',
            clientPace: '',
            costSensitivity: '',
            technicality: '',
            decisionStyle: '',
            communicationStyle: ''
         },
         editingCommId: null,
         commModalMode: 'form',
         commModalContact: null,
         isLoadingUsers: false,
         fieldStates: {
            name: 'idle',
            abn: 'idle',
            leadName: 'idle',
            phone: 'idle',
            email: 'idle',
            org_lead_email: 'idle',
            address: 'idle',
            address2: 'idle',
            city: 'idle',
            state: 'idle',
            postcode: 'idle',
            country: 'idle',
            billing_address: 'idle',
            billing_address2: 'idle',
            billing_city: 'idle',
            billing_state: 'idle',
            billing_postcode: 'idle',
            billing_country: 'idle',
            billing_email: 'idle',
            delivery_address: 'idle',
            delivery_address2: 'idle',
            delivery_city: 'idle',
            delivery_state: 'idle',
            delivery_postcode: 'idle',
            delivery_country: 'idle',
            service_address: 'idle',
            service_address2: 'idle',
            service_city: 'idle',
            service_state: 'idle',
            service_postcode: 'idle',
            service_country: 'idle',
            service_email: 'idle',
            background: 'idle',
            currency: 'idle',
            assigned_user_id: 'idle',
            vertex_org_manager_id: 'idle',
            classifications: 'idle',
            isMspManaged: 'idle',
            mspUuid: 'idle',
            isPartnerReferred: 'idle',
            partnerUuid: 'idle',
            isEcosystem: 'idle',
            ecosystemUuid: 'idle',
            diversity_form_completed: 'idle',
            code_of_conduct_accepted_date: 'idle',
            security_risk_rating: 'idle',
            esg_practices_rating: 'idle',
            is_independent_private_business: 'idle',
            formal_written_policies: 'idle',
            positive_practices: 'idle',
            certifications: 'idle',
            certifications_other: 'idle',
            organisation_type: 'idle',
            purpose_driven_type: 'idle',
            underserved_beneficiaries: 'idle',
            underserved_types: 'idle',
            organisational_purpose: 'idle',
            population_served: 'idle',
            is_pbi: 'idle',
            is_hpc: 'idle',
            reporting_current: 'idle',
            is_local_supplier: 'idle',
            is_women_owned: 'idle',
            is_minority_owned: 'idle',
            is_indigenous_owned: 'idle',
            industry: 'idle',
            is_disclosure_industry: 'idle',
            technology_stack: 'idle'
         },
         techScanUrl: '',
         isScanningTechStack: false,
         techScanResults: null,
         techScanMeta: null,
         techScanSelected: {},
         techStackSearch: '',
         customTechInput: '',
         techStackCatalog: [
            {
               name: 'Cloud & Infrastructure',
               icon: 'fas fa-cloud',
               color: '#00A0C1',
               items: [
                  { name: 'Amazon Web Services (AWS)', icon: 'fab fa-aws', color: '#FF9900' },
                  { name: 'Microsoft Azure', icon: 'fab fa-microsoft', color: '#0089D6' },
                  { name: 'Google Cloud (GCP)', icon: 'fab fa-google', color: '#4285F4' },
                  { name: 'Cloudflare', icon: 'fab fa-cloudflare', color: '#F38020' },
                  { name: 'DigitalOcean', icon: 'fab fa-digital-ocean', color: '#0080FF' },
                  { name: 'VMware', icon: 'fas fa-server', color: '#607078' },
                  { name: 'Vercel', icon: 'fas fa-caret-up', color: '#111827' },
                  { name: 'Docker', icon: 'fab fa-docker', color: '#2496ED' },
                  { name: 'Kubernetes', icon: 'fas fa-dharmachakra', color: '#326CE5' }
               ]
            },
            {
               name: 'Identity & Security',
               icon: 'fas fa-shield-alt',
               color: '#00A0C1',
               items: [
                  { name: 'Microsoft 365', icon: 'fab fa-microsoft', color: '#D83B01' },
                  { name: 'Microsoft Entra ID', icon: 'fas fa-id-badge', color: '#0078D4' },
                  { name: 'Microsoft Defender', icon: 'fas fa-shield-alt', color: '#0078D4' },
                  { name: 'Okta', icon: 'fas fa-key', color: '#007DC1' },
                  { name: '1Password', icon: 'fas fa-lock', color: '#0A85EA' },
                  { name: 'CrowdStrike', icon: 'fas fa-shield-virus', color: '#E01E26' },
                  { name: 'SentinelOne', badgeText: 'S1', color: '#6B2FEB' },
                  { name: 'Palo Alto Networks', icon: 'fas fa-fire-alt', color: '#FA582D' },
                  { name: 'Fortinet', icon: 'fas fa-shield-alt', color: '#EE3124' },
                  { name: 'Cisco', icon: 'fas fa-network-wired', color: '#049FD9' },
                  { name: 'Mimecast', icon: 'fas fa-envelope-shield', color: '#1E3A8A' },
                  { name: 'Proofpoint', icon: 'fas fa-user-shield', color: '#0284C7' },
                  { name: 'KnowBe4', icon: 'fas fa-user-secret', color: '#FF6600' }
               ]
            },
            {
               name: 'Web & CMS',
               icon: 'fas fa-globe',
               color: '#00A0C1',
               items: [
                  { name: 'WordPress', icon: 'fab fa-wordpress', color: '#21759B' },
                  { name: 'Shopify', icon: 'fab fa-shopify', color: '#7AB55C' },
                  { name: 'Webflow', icon: 'fas fa-layer-group', color: '#4353FF' },
                  { name: 'Squarespace', icon: 'fab fa-squarespace', color: '#111827' },
                  { name: 'Wix', icon: 'fab fa-wix', color: '#0C6EFC' },
                  { name: 'Drupal', icon: 'fab fa-drupal', color: '#0678BE' },
                  { name: 'Joomla', icon: 'fab fa-joomla', color: '#F44321' },
                  { name: 'Magento', icon: 'fab fa-magento', color: '#EE672F' }
               ]
            },
            {
               name: 'Software & Web Stack',
               icon: 'fas fa-code',
               color: '#00A0C1',
               items: [
                  { name: 'Go (Golang)', icon: 'fab fa-golang', color: '#00ADD8' },
                  { name: 'Rust', icon: 'fab fa-rust', color: '#CE422B' },
                  { name: 'Ruby', icon: 'fas fa-gem', color: '#CC342D' },
                  { name: 'Ruby on Rails', badgeText: 'RoR', color: '#CC0000' },
                  { name: 'Python', icon: 'fab fa-python', color: '#3776AB' },
                  { name: 'Django', badgeText: 'DJ', color: '#092E20' },
                  { name: 'Java', icon: 'fab fa-java', color: '#E76F00' },
                  { name: 'Spring Boot', icon: 'fas fa-leaf', color: '#6DB33F' },
                  { name: '.NET / C#', icon: 'fab fa-microsoft', color: '#512BD4' },
                  { name: 'C / C++', badgeText: 'C++', color: '#00599C' },
                  { name: 'PHP', icon: 'fab fa-php', color: '#777BB4' },
                  { name: 'Laravel', icon: 'fab fa-laravel', color: '#FF2D20' },
                  { name: 'JavaScript', icon: 'fab fa-js-square', color: '#F7DF1E' },
                  { name: 'TypeScript', badgeText: 'TS', color: '#3178C6' },
                  { name: 'Node.js', icon: 'fab fa-node-js', color: '#339933' },
                  { name: 'React', icon: 'fab fa-react', color: '#00B4D8' },
                  { name: 'Next.js', badgeText: 'N', color: '#111827' },
                  { name: 'Vue.js', icon: 'fab fa-vuejs', color: '#42B883' },
                  { name: 'Nuxt.js', icon: 'fas fa-mountain', color: '#00DC82' },
                  { name: 'Angular', icon: 'fab fa-angular', color: '#DD0031' },
                  { name: 'Svelte', badgeText: 'SV', color: '#FF3E00' },
                  { name: 'Swift', icon: 'fab fa-swift', color: '#F05138' },
                  { name: 'Kotlin', badgeText: 'KT', color: '#7F52FF' },
                  { name: 'Flutter', badgeText: 'FL', color: '#02569B' },
                  { name: 'GraphQL', icon: 'fas fa-project-diagram', color: '#E10098' },
                  { name: 'Tailwind CSS', icon: 'fas fa-wind', color: '#06B6D4' },
                  { name: 'Bootstrap', icon: 'fab fa-bootstrap', color: '#7952B3' },
                  { name: 'jQuery', icon: 'fab fa-js-square', color: '#0769AD' }
               ]
            },
            {
               name: 'Data & DevOps',
               icon: 'fas fa-server',
               color: '#00A0C1',
               items: [
                  { name: 'GitHub', icon: 'fab fa-github', color: '#24292E' },
                  { name: 'GitLab', icon: 'fab fa-gitlab', color: '#FC6D26' },
                  { name: 'Bitbucket', icon: 'fab fa-bitbucket', color: '#0052CC' },
                  { name: 'Atlassian (Jira / Confluence)', icon: 'fab fa-atlassian', color: '#0052CC' },
                  { name: 'Terraform', icon: 'fas fa-cubes', color: '#7B42BC' },
                  { name: 'Nginx', icon: 'fas fa-server', color: '#009639' },
                  { name: 'Apache', icon: 'fas fa-feather-alt', color: '#D22128' },
                  { name: 'PostgreSQL', icon: 'fas fa-database', color: '#336791' },
                  { name: 'MySQL', icon: 'fas fa-database', color: '#4479A1' },
                  { name: 'SQL Server', icon: 'fas fa-database', color: '#CC2927' },
                  { name: 'MongoDB', icon: 'fas fa-database', color: '#47A248' },
                  { name: 'Redis', icon: 'fas fa-memory', color: '#DC382D' }
               ]
            },
            {
               name: 'Business & SaaS',
               icon: 'fas fa-briefcase',
               color: '#00A0C1',
               items: [
                  { name: 'Microsoft Teams', icon: 'fab fa-microsoft', color: '#6264A7' },
                  { name: 'Xero', badgeText: 'XERO', color: '#13B5EA' },
                  { name: 'ServiceNow', icon: 'fas fa-cogs', color: '#293E40' },
                  { name: 'Salesforce', icon: 'fab fa-salesforce', color: '#00A1E0' },
                  { name: 'HubSpot', icon: 'fab fa-hubspot', color: '#FF7A59' },
                  { name: 'Google Workspace', icon: 'fab fa-google', color: '#EA4335' },
                  { name: 'Zendesk', icon: 'fas fa-headset', color: '#03363D' },
                  { name: 'Intercom', icon: 'fab fa-intercom', color: '#286EFA' },
                  { name: 'Stripe', icon: 'fab fa-stripe-s', color: '#635BFF' },
                  { name: 'DocuSign', icon: 'fas fa-file-signature', color: '#FF2600' },
                  { name: 'SendGrid', icon: 'fas fa-paper-plane', color: '#1A82E2' },
                  { name: 'Mailgun', icon: 'fas fa-envelope-open-text', color: '#F05032' },
                  { name: 'Slack', icon: 'fab fa-slack', color: '#4A154B' },
                  { name: 'Zoom', icon: 'fas fa-video', color: '#2D8CFF' },
                  { name: 'Google Analytics', icon: 'fas fa-chart-line', color: '#E37400' },
                  { name: 'Google Tag Manager', icon: 'fas fa-tags', color: '#246FDB' }
               ]
            }
         ]      };
   },
   created() {
      const initialOrgId = this.$route.query.organisation_uuid || this.$route.query.lead_id;
      if (initialOrgId) {
         this.selectedLeadId = initialOrgId;
         this.fetchOrganisationDetails(initialOrgId);
      }
      const requestedTab = this.$route.query.tab || this.$route.query.activeTab;
      if (requestedTab) {
         this.activeTab = requestedTab === 'client_communication' ? 'contacts' : requestedTab;
      } else if (this.activeTab === 'client_communication') {
         this.activeTab = 'contacts';
      }
      this.fetchLeads();
      this.getBdms();
      this.getVertexUsers();
      this.fetchDropdownCategories();
      this.debounceTimeouts = {};
   },
   mounted() {
      document.addEventListener('click', this.handleDocumentClick);
   },
   beforeUnmount() {
      document.removeEventListener('click', this.handleDocumentClick);
      if (this.debounceTimeouts) {
         for (const key in this.debounceTimeouts) {
            clearTimeout(this.debounceTimeouts[key]);
         }
      }
   },
   computed: {
      filteredOrgActiveJobs() {
         let jobs = this.orgActiveJobs || [];
         if (this.jobStatusFilter !== 'all') {
            if (this.jobStatusFilter === 'At Risk') {
               jobs = jobs.filter(j => j.health_indicator === 'Behind Plan' || j.health_indicator === 'At Risk' || j.health_indicator === 'Overdue');
            } else {
               jobs = jobs.filter(j => (j.job_status || '').toLowerCase() === this.jobStatusFilter.toLowerCase());
            }
         }
         if (this.jobSearchQuery && this.jobSearchQuery.trim()) {
            const query = this.jobSearchQuery.toLowerCase().trim();
            jobs = jobs.filter(j => {
               const titleMatch = (j.title || '').toLowerCase().includes(query);
               const bpMatch = j.blueprint && (
                  (j.blueprint.title || '').toLowerCase().includes(query) ||
                  (j.blueprint.auth_item_code || '').toLowerCase().includes(query)
               );
               return titleMatch || bpMatch;
            });
         }
         return jobs;
      },
      jobsMetrics() {
         const jobs = this.orgActiveJobs || [];
         const total = jobs.length;
         const inProgress = jobs.filter(j => (j.job_status || '').toLowerCase() === 'in progress').length;
         const completed = jobs.filter(j => (j.job_status || '').toLowerCase() === 'completed').length;
         const pending = jobs.filter(j => (j.job_status || '').toLowerCase() === 'pending' || !j.job_status).length;
         const atRisk = jobs.filter(j => j.health_indicator === 'Behind Plan' || j.health_indicator === 'At Risk' || j.health_indicator === 'Overdue').length;
         const onTrack = total - atRisk;
         const onTrackPercentage = total > 0 ? Math.round((onTrack / total) * 100) : 100;
         return {
            total,
            inProgress,
            completed,
            pending,
            atRisk,
            onTrack,
            onTrackPercentage
         };
      },
      businessNumberShortLabel() {
         return getBusinessNumberShortLabel(this.profileData?.country || 'Australia');
      },
      businessNumberLabel() {
         return getBusinessNumberLabel(this.profileData?.country || 'Australia');
      },
      searchDropdownList() {
         if (!this.searchQuery) return [];
         const query = this.searchQuery.toLowerCase();
         return this.leads.filter(c => 
            !c.is_merged && (
               (c.organisation_name && c.organisation_name.toLowerCase().includes(query)) ||
               (c.email && c.email.toLowerCase().includes(query)) ||
               (c.former_names && c.former_names.some(fn => fn.toLowerCase().includes(query)))
            )
         );
      },
      isLinkedToAuthOrg() {
         return !!this.profileData.isAuthSynced;
      },
      booleanYesNoOptions() {
         return [
            { label: 'No', value: false },
            { label: 'Yes', value: true }
         ];
      },
      diversityFormCompletedSelected: {
         get() {
            return this.profileData.diversity_form_completed
               ? { label: 'Yes', value: true }
               : { label: 'No', value: false };
         },
         set(opt) {
            this.profileData.diversity_form_completed = opt ? Boolean(opt.value) : false;
            this.debounceSave('Diversity Self-Declaration Form', 'diversity_form_completed');
         }
      },
      isIndependentPrivateBusinessSelected: {
         get() {
            return this.profileData.is_independent_private_business
               ? { label: 'Yes', value: true }
               : { label: 'No', value: false };
         },
         set(opt) {
            this.profileData.is_independent_private_business = opt ? Boolean(opt.value) : false;
            this.debounceSave('Independent Private Business', 'is_independent_private_business');
         }
      },
      computedIndustryOptions() {
         if (!this.industryOptions || !Array.isArray(this.industryOptions)) return [];
         return this.industryOptions.map(ind => typeof ind === 'object' && ind !== null ? (ind.display_name || ind.name) : ind);
      },
      selectedOrgObject: {
         get() {
            const found = this.leads.find(l => l.id === this.selectedLeadId || l.uuid === this.selectedLeadId || l.organisation_uuid === this.selectedLeadId);
            if (found) return found;
            if (this.profileData && (this.profileData.id === this.selectedLeadId || this.profileData.uuid === this.selectedLeadId || this.profileData.organisationUuid === this.selectedLeadId)) {
               return {
                  id: this.profileData.uuid || this.profileData.id,
                  uuid: this.profileData.uuid || this.profileData.id,
                  organisation_uuid: this.profileData.uuid || this.profileData.id,
                  organisation_name: this.profileData.name || 'Unnamed Org',
                  name: this.profileData.name || 'Unnamed Org',
                  company: this.profileData.name || 'Unnamed Org',
                  abn: this.profileData.abn || 'N/A',
                  email: this.profileData.email || '',
                  type: (this.profileData.classifications && this.profileData.classifications[0]) || 'Organisation',
                  classifications: this.profileData.classifications || [],
                  is_merged: !!(this.profileData.is_merged || this.profileData.isMerged),
                  isMerged: !!(this.profileData.is_merged || this.profileData.isMerged),
                  is_deactivated: !!(this.profileData.isDeactivated || this.profileData.is_deactivated || this.profileData.is_inactive),
                  isDeactivated: !!(this.profileData.isDeactivated || this.profileData.is_deactivated || this.profileData.is_inactive),
                  is_inactive: !!(this.profileData.isDeactivated || this.profileData.is_deactivated || this.profileData.is_inactive),
                  is_partner: !!this.profileData.isPartner,
                  is_supplier: !!this.profileData.isSupplier,
                  former_names: this.profileData.former_names || [],
               };
            }
            return null;
         },
         set(val) {
            if (val) {
               this.selectedLeadId = val.id || val.uuid || val.organisation_uuid;
            } else {
               this.selectedLeadId = '';
            }
         }
      },
      primaryClassification: {
         get() {
            if (!this.profileData || !this.profileData.classifications) return 'Lead';
            if (this.profileData.classifications.includes('Client')) return 'Client';
            if (this.profileData.classifications.includes('Past Client')) return 'Past Client';
            if (this.profileData.classifications.includes('Prospect')) return 'Prospect';
            return 'Lead';
         },
         set(val) {
            if (!this.profileData) return;
            let list = (this.profileData.classifications || []).filter(c => c !== 'Lead' && c !== 'Prospect' && c !== 'Client' && c !== 'Past Client');
            list.push(val);
            this.profileData.classifications = list;
         }
      },
      isPastClient() {
         if (!this.profileData) return false;
         const classes = Array.isArray(this.profileData.classifications) ? this.profileData.classifications : [];
         return classes.includes('Past Client') || this.profileData.clientType === 'Past Client';
      },
      canShowJobsBoard() {
         return this.primaryClassification === 'Client' || this.primaryClassification === 'Past Client';
      },
      isPartnerClassification: {
         get() {
            return this.profileData && this.profileData.classifications && this.profileData.classifications.includes('Partner');
         },
         set(checked) {
            if (!this.profileData) return;
            let list = [...(this.profileData.classifications || [])];
            if (checked) {
               if (!list.includes('Partner')) {
                  list.push('Partner');
               }
            } else {
               list = list.filter(c => c !== 'Partner');
            }
            this.profileData.classifications = list;
         }
      },
      isSupplierClassification: {
         get() {
            return this.profileData && this.profileData.classifications && this.profileData.classifications.includes('Supplier');
         },
         set(checked) {
            if (!this.profileData) return;
            let list = [...(this.profileData.classifications || [])];
            if (checked) {
               if (!list.includes('Supplier')) {
                  list.push('Supplier');
               }
            } else {
               list = list.filter(c => c !== 'Supplier');
            }
            this.profileData.classifications = list;
         }
      },
      isSupplierOrPartner() {
         if (!this.profileData) return false;
         if (this.isSupplierClassification || this.isPartnerClassification) return true;
         const classes = this.profileData.classifications || [];
         return classes.includes('Supplier') || classes.includes('Partner') || !!this.profileData.isSupplier || !!this.profileData.isPartner;
      },
      combinedAddress: {
         get() {
            if (!this.profileData) return '';
            const parts = [
               this.profileData.address,
               this.profileData.address2,
               this.profileData.city,
               this.profileData.state,
               this.profileData.country,
               this.profileData.postcode
            ];
            return parts.filter(p => p && p.trim()).join(', ');
         },
         set(newValue) {
            if (!this.profileData) return;
            this.profileData.address = newValue;
            this.profileData.address2 = '';
            this.profileData.city = '';
            this.profileData.state = '';
            this.profileData.country = '';
            this.profileData.postcode = '';
         }
      },
      combinedBillingAddress: {
         get() {
            if (!this.profileData) return '';
            const parts = [
               this.profileData.billing_address,
               this.profileData.billing_address2,
               this.profileData.billing_city,
               this.profileData.billing_state,
               this.profileData.billing_country,
               this.profileData.billing_postcode
            ];
            return parts.filter(p => p && p.trim()).join(', ');
         },
         set(newValue) {
            if (!this.profileData) return;
            this.profileData.billing_address = newValue;
            this.profileData.billing_address2 = '';
            this.profileData.billing_city = '';
            this.profileData.billing_state = '';
            this.profileData.billing_country = '';
            this.profileData.billing_postcode = '';
         }
      },
      combinedDeliveryAddress: {
         get() {
            if (!this.profileData) return '';
            const parts = [
               this.profileData.delivery_address,
               this.profileData.delivery_address2,
               this.profileData.delivery_city,
               this.profileData.delivery_state,
               this.profileData.delivery_country,
               this.profileData.delivery_postcode
            ];
            return parts.filter(p => p && p.trim()).join(', ');
         },
         set(newValue) {
            if (!this.profileData) return;
            this.profileData.delivery_address = newValue;
            this.profileData.delivery_address2 = '';
            this.profileData.delivery_city = '';
            this.profileData.delivery_state = '';
            this.profileData.delivery_country = '';
            this.profileData.delivery_postcode = '';
         }
      },
      combinedServiceAddress: {
         get() {
            if (!this.profileData) return '';
            const parts = [
               this.profileData.service_address,
               this.profileData.service_address2,
               this.profileData.service_city,
               this.profileData.service_state,
               this.profileData.service_country,
               this.profileData.service_postcode
            ];
            return parts.filter(p => p && p.trim()).join(', ');
         },
         set(newValue) {
            if (!this.profileData) return;
            this.profileData.service_address = newValue;
            this.profileData.service_address2 = '';
            this.profileData.service_city = '';
            this.profileData.service_state = '';
            this.profileData.service_country = '';
            this.profileData.service_postcode = '';
         }
      },
      filteredOrgUsers() {
          if (!this.orgUsers || !this.clientCommunications) return [];
          const existingUserIds = this.clientCommunications
             .filter(c => c.is_linked && c.user_id)
             .map(c => c.user_id);
          return this.orgUsers.filter(user => !existingUserIds.includes(user.id));
       },
      previewContacts() {
         if (!this.orgContacts || !this.orgContacts.length) return [];
         return this.orgContacts.slice(0, 3);
      },
      filteredOrgContacts() {
         let list = this.orgContacts ? [...this.orgContacts] : [];
         
         // Merge standalone clientCommunications
         if (this.clientCommunications && this.clientCommunications.length > 0) {
            this.clientCommunications.forEach(comm => {
               // Check if this comm matches an existing contact
               let matched = false;
               if (comm.is_linked && comm.user_id) {
                  matched = list.some(contact => Number(contact.auth_user_id) === Number(comm.user_id));
                  if (!matched) {
                     const u = this.orgUsers.find(user => Number(user.id) === Number(comm.user_id));
                     if (u) {
                        matched = list.some(contact => {
                           const cEmail = (contact.email || '').toLowerCase();
                           const cName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim().toLowerCase();
                           return (cEmail && cEmail === u.email.toLowerCase()) || (cName && cName === u.name.toLowerCase());
                        });
                     }
                  }
               }
               if (!matched && comm.name) {
                  const commName = comm.name.trim().toLowerCase();
                  matched = list.some(contact => {
                     const cName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim().toLowerCase();
                     const cEmail = (contact.email || '').toLowerCase();
                     return cName === commName || cEmail === commName;
                  });
               }

               if (!matched) {
                  // Create a virtual contact for this standalone preference
                  let vEmail = '';
                  let vName = comm.name || 'Unknown Contact';
                  if (comm.is_linked && comm.user_id) {
                     const u = this.orgUsers.find(user => Number(user.id) === Number(comm.user_id));
                     if (u) {
                        vName = u.name;
                        vEmail = u.email;
                     }
                  }
                  
                  const parts = vName.split(' ');
                  const first = parts[0];
                  const last = parts.slice(1).join(' ');

                  list.push({
                     id: `virtual-comm-${comm.id}`,
                     uuid: `virtual-comm-${comm.id}`,
                     first_name: first,
                     last_name: last,
                     email: vEmail,
                     role: 'Communication Preference',
                     contact_type: 'Other',
                     auth_user_id: comm.is_linked ? comm.user_id : null,
                     isVirtual: true
                  });
               }
            });
         }

         if (this.contactsRoleFilter && this.contactsRoleFilter.length > 0) {
            list = list.filter(c => {
               const isPrimary = c.is_primary || (c.contact_type && c.contact_type.toLowerCase() === 'primary');
               const cType = (c.contact_type || '').toLowerCase();
               return this.contactsRoleFilter.some(selected => {
                  if (selected === 'primary') return isPrimary;
                  return cType === selected.toLowerCase();
               });
            });
         }

         if (this.contactsSearchQuery && this.contactsSearchQuery.trim()) {
            const q = this.contactsSearchQuery.trim().toLowerCase();
            list = list.filter(c => {
               const fullName = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
               const email = (c.email || '').toLowerCase();
               const phone = (c.phone_number || '').toLowerCase();
               const role = (c.role || '').toLowerCase();
               const type = (c.contact_type || '').toLowerCase();
               return fullName.includes(q) || email.includes(q) || phone.includes(q) || role.includes(q) || type.includes(q);
            });
         }

         return list;
      },
      contactRoleOptions() {
         const standard = [
            { value: 'primary', text: 'Primary' },
            { value: 'technical', text: 'Technical' },
            { value: 'billing', text: 'Billing' },
            { value: 'signer', text: 'Signer' },
            { value: 'user', text: 'User' },
            { value: 'lead', text: 'Lead' }
         ];
         const standardKeys = standard.map(s => s.value);
         const dynamic = [];
         if (this.orgContacts && this.orgContacts.length) {
            for (const c of this.orgContacts) {
               if (c.contact_type) {
                  const val = c.contact_type.toLowerCase();
                  if (!standardKeys.includes(val) && !dynamic.some(d => d.value === val)) {
                     dynamic.push({
                        value: val,
                        text: c.contact_type.charAt(0).toUpperCase() + c.contact_type.slice(1)
                     });
                  }
               }
            }
         }
         return [...standard, ...dynamic];
      },
      filteredTechStackCategories() {
         if (!this.techStackCatalog) return [];
         const q = (this.techStackSearch || '').trim().toLowerCase();
         if (!q) return this.techStackCatalog;
         return this.techStackCatalog.map(cat => {
            const filteredItems = cat.items.filter(item => 
               item.name.toLowerCase().includes(q) || cat.name.toLowerCase().includes(q)
            );
            return {
               ...cat,
               items: filteredItems
            };
         }).filter(cat => cat.items.length > 0);
      },
      selectedScanItemsCount() {
         if (!this.techScanResults || !this.techScanResults.length) return 0;
         return this.techScanResults.filter(
            item => !this.isTechSelected(item.name) && !!this.techScanSelected[item.name]
         ).length;
      },
      isAllScanItemsSelected() {
         if (!this.techScanResults || !this.techScanResults.length) return false;
         const newItems = this.techScanResults.filter(item => !this.isTechSelected(item.name));
         return newItems.length > 0 && newItems.every(item => !!this.techScanSelected[item.name]);
      },
      formattedSocialPurposeLocation() {
         if (!this.profileData) return 'Not Set';
         if (this.profileData.purpose_driven_type === 'Registered Charity') {
            const hasAcncLocation = this.profileData.acnc_town_city || this.profileData.acnc_state || this.profileData.acnc_postcode;
            if (hasAcncLocation) {
               return [
                  this.profileData.acnc_town_city,
                  this.profileData.acnc_state,
                  this.profileData.country || 'Australia',
                  this.profileData.acnc_postcode
               ].filter(Boolean).join(', ') || 'Not Set';
            }
            return [
               this.profileData.city,
               this.profileData.state,
               this.profileData.country,
               this.profileData.postcode
            ].filter(Boolean).join(', ') || 'Not Set';
         }

         return [
            this.profileData.city,
            this.profileData.state,
            this.profileData.country,
            this.profileData.postcode
         ].filter(Boolean).join(', ') || 'Not Set';
      },
      todayDateString() {
         return moment().format('YYYY-MM-DD');
      }
   },
   watch: {
      'profileData.isCharity'(newVal) {
         if (newVal) {
            if (this.profileData.purpose_driven_type !== 'Registered Charity') {
               this.profileData.purpose_driven_type = 'Registered Charity';
               this.debounceSave('Purpose Driven Type', 'purpose_driven_type');
            }
            this.validateAcnc({ silent: true });
         }
      },
      selectedLeadId(newVal, oldVal) {
         if (newVal) {
            if (newVal !== oldVal && newVal !== this.profileData.id && newVal !== this.profileData.uuid) {
               this.fetchOrganisationDetails(newVal);
            }
            if (this.$route.query.organisation_uuid !== newVal && this.$route.query.lead_id !== newVal) {
               this.$router.replace({
                  query: { ...this.$route.query, organisation_uuid: newVal }
               }).catch(() => {});
            }
         }
      },
      '$route.query.organisation_uuid'(newVal) {
         if (newVal && newVal !== this.selectedLeadId) {
            this.selectedLeadId = newVal;
         }
      },
      '$route.query.lead_id'(newVal) {
         if (newVal && newVal !== this.selectedLeadId) {
            this.selectedLeadId = newVal;
         }
      },
      'profileData.classifications'() {
         if (this.activeTab === 'client_communication') {
            this.activeTab = 'contacts';
         }
         if (this.activeTab === 'jobs_board' && !this.canShowJobsBoard) {
            this.activeTab = 'profile';
         }
      }
   },
   methods: {
      navigateToOrganisation(orgUuid) {
         if (!orgUuid) return;
         this.selectedLeadId = orgUuid;
         if (this.activeTab === 'merged_organisations') {
            this.activeTab = 'profile';
         }
      },
      onSearchInput() {
         this.showSearchDropdown = true;
      },
      clearSearchQuery() {
         this.searchQuery = '';
         this.showSearchDropdown = false;
      },
      selectOrgFromDropdown(org) {
         this.showSearchDropdown = false;
         this.searchQuery = '';
         this.selectedLeadId = org.id || org.organisation_uuid;
      },
      toggleSection(sectionName) {
         if (this.sectionOpen[sectionName] !== undefined) {
            this.sectionOpen[sectionName] = !this.sectionOpen[sectionName];
         }
      },
      getTechMeta(name) {
         if (!name) return { icon: 'fas fa-cube', color: '#00A0C1', category: 'Other' };
         const cleanName = (name || '').toLowerCase().trim();
         let lookupName = cleanName;
         if (cleanName === 'go' || cleanName === 'golang') {
            lookupName = 'go (golang)';
         }
         if (this.techStackCatalog) {
            for (const cat of this.techStackCatalog) {
               const found = cat.items.find(i => i.name.toLowerCase() === lookupName);
               if (found) {
                  return { ...found, category: cat.name };
               }
            }
         }
         return { name, icon: 'fas fa-cube', color: '#00A0C1', category: 'Custom' };
      },
      isTechSelected(name) {
         if (!this.profileData?.technology_stack || !Array.isArray(this.profileData.technology_stack)) {
            return false;
         }
         const target = (name || '').toLowerCase().trim();
         return this.profileData.technology_stack.some(item => {
            const it = (item || '').toLowerCase().trim();
            if (it === target) return true;
            if ((target === 'go (golang)' || target === 'go' || target === 'golang') && (it === 'go (golang)' || it === 'go' || it === 'golang')) {
               return true;
            }
            return false;
         });
      },
      toggleTechStack(name) {
         if (!name) return;
         const cleanName = name.trim();
         if (!Array.isArray(this.profileData.technology_stack)) {
            this.profileData.technology_stack = [];
         }
         const existingIndex = this.profileData.technology_stack.findIndex(
            item => (item || '').toLowerCase().trim() === cleanName.toLowerCase()
         );
         if (existingIndex >= 0) {
            this.profileData.technology_stack.splice(existingIndex, 1);
         } else {
            this.profileData.technology_stack.push(cleanName);
         }
         this.debounceSave('Technology Stack', 'technology_stack');
      },
      removeTechStack(name) {
         if (!name || !Array.isArray(this.profileData.technology_stack)) return;
         const cleanName = name.trim().toLowerCase();
         this.profileData.technology_stack = this.profileData.technology_stack.filter(
            item => (item || '').toLowerCase().trim() !== cleanName
         );
         this.debounceSave('Technology Stack', 'technology_stack');
      },
      clearAllTechStack() {
         if (!this.profileData.technology_stack || this.profileData.technology_stack.length === 0) return;
         if (confirm('Are you sure you want to clear all technologies from this organisation\'s stack?')) {
            this.profileData.technology_stack = [];
            this.debounceSave('Technology Stack', 'technology_stack');
         }
      },
      addCustomTechStack() {
         const val = (this.customTechInput || '').trim();
         if (!val) return;
         const sanitized = val.replace(/<[^>]*>/g, '').trim().slice(0, 100);
         if (!sanitized) {
            this.$toast.error('Please enter a valid technology name.');
            return;
         }
         if (!Array.isArray(this.profileData.technology_stack)) {
            this.profileData.technology_stack = [];
         }
         const alreadyExists = this.profileData.technology_stack.some(
            item => (item || '').toLowerCase().trim() === sanitized.toLowerCase()
         );
         if (alreadyExists) {
            this.$toast.info(`"${sanitized}" is already in the technology stack.`);
            this.customTechInput = '';
            return;
         }
         this.profileData.technology_stack.push(sanitized);
         this.customTechInput = '';
         this.debounceSave('Technology Stack', 'technology_stack');
         this.$toast.success(`Added "${sanitized}" to technology stack.`);
      },
      async scanWebsiteTechStack() {
         const targetUrl = (this.techScanUrl || '').trim();
         if (!targetUrl) {
            this.$toast.error('Please enter a website URL or domain to scan.');
            return;
         }

         this.isScanningTechStack = true;
         this.techScanResults = null;
         this.techScanMeta = null;
         this.techScanSelected = {};

         try {
            const res = await this.$axios.post('/organisations/scan-tech-stack', {
               url: targetUrl
            });

            if (res.data && res.data.success) {
               const detected = Array.isArray(res.data.detected) ? res.data.detected : [];
               this.techScanResults = detected;
               this.techScanMeta = {
                  url: res.data.url || targetUrl,
                  domain: res.data.domain || targetUrl,
                  count: detected.length
               };

               const selectedMap = {};
               detected.forEach(item => {
                  if (!this.isTechSelected(item.name)) {
                     selectedMap[item.name] = true;
                  }
               });
               this.techScanSelected = selectedMap;

               if (detected.length === 0) {
                  this.$toast.info('Scan completed. No detectable technologies found on this website.');
               } else {
                  this.$toast.success(`Scan completed: detected ${detected.length} technologies.`);
               }
            } else {
               const msg = (res.data && res.data.message) || 'Failed to complete website scan.';
               this.$toast.error(msg);
            }
         } catch (err) {
            console.error('Website tech stack scan error:', err);
            const msg = err.response?.data?.message || err.message || 'Error occurred while scanning website.';
            this.$toast.error(msg);
         } finally {
            this.isScanningTechStack = false;
         }
      },
      toggleSelectAllNewScanResults() {
         if (!this.techScanResults || !this.techScanResults.length) return;
         const newItems = this.techScanResults.filter(item => !this.isTechSelected(item.name));
         const allSelected = newItems.length > 0 && newItems.every(item => !!this.techScanSelected[item.name]);
         
         const updated = { ...this.techScanSelected };
         newItems.forEach(item => {
            updated[item.name] = !allSelected;
         });
         this.techScanSelected = updated;
      },
      applySelectedScanResults() {
         if (!this.techScanResults || !this.techScanResults.length) return;
         if (!Array.isArray(this.profileData.technology_stack)) {
            this.profileData.technology_stack = [];
         }
         const itemsToAdd = this.techScanResults
            .filter(item => !this.isTechSelected(item.name) && !!this.techScanSelected[item.name])
            .map(item => item.name);

         if (itemsToAdd.length === 0) {
            this.$toast.info('No new technologies selected to add.');
            return;
         }

         itemsToAdd.forEach(name => {
            if (!this.isTechSelected(name)) {
               this.profileData.technology_stack.push(name);
            }
         });

         this.debounceSave('Technology Stack', 'technology_stack');
         this.$toast.success(`Added ${itemsToAdd.length} technologies to organisation stack.`);
         this.dismissTechScanResults();
      },
      dismissTechScanResults() {
         this.techScanResults = null;
         this.techScanMeta = null;
         this.techScanSelected = {};
      },
      getMspName(uuid) {
         if (!uuid) return '';
         const item = (this.mspOptions || []).find(o => o.id === uuid || o.uuid === uuid);
         return item ? item.display_name : uuid;
      },
      getPartnerName(uuid) {
         if (!uuid) return '';
         const item = (this.partnerOptions || []).find(o => o.id === uuid || o.uuid === uuid);
         return item ? item.display_name : uuid;
      },
      getEcosystemName(uuid) {
         if (!uuid) return '';
         const item = (this.ecosystemOptions || []).find(o => o.id === uuid || o.uuid === uuid);
         return item ? item.display_name : uuid;
      },
      getOrgClassification(org) {
         if (org && org.classifications && org.classifications.length > 0) {
            const primary = org.classifications.find(c => {
               const val = typeof c === 'string' ? c : (c.classification || '');
               return ['client', 'past client', 'lead', 'prospect'].includes(val.toLowerCase());
            });
            if (primary) {
               return typeof primary === 'string' ? primary : primary.classification;
            }
            const first = org.classifications[0];
            return typeof first === 'string' ? first : (first.classification || 'Client');
         }
         return 'Client';
      },
      isPartner(org) {
         if (!org) return false;
         if (org.is_partner || org.isPartner) return true;
         if (org.classifications && Array.isArray(org.classifications)) {
            return org.classifications.some(c => typeof c === 'string' ? c.toLowerCase() === 'partner' : (c.classification && c.classification.toLowerCase() === 'partner'));
         }
         return false;
      },
      isSupplier(org) {
         if (!org) return false;
         if (org.is_supplier || org.isSupplier) return true;
         if (org.classifications && Array.isArray(org.classifications)) {
            return org.classifications.some(c => typeof c === 'string' ? c.toLowerCase() === 'supplier' : (c.classification && c.classification.toLowerCase() === 'supplier'));
         }
         return false;
      },
      getVertexLead(org) {
         if (!org) return '';

         // Vertex Organisation Lead only (do not fall back to Vertex BDM)
         const orgManagerId = org.vertex_org_manager_id !== undefined && org.vertex_org_manager_id !== null
            ? Number(org.vertex_org_manager_id)
            : null;
         if (orgManagerId !== null) {
            if (orgManagerId > 0) {
               if (this.vertexUsers && this.vertexUsers.length > 0) {
                  const matchedUser = this.vertexUsers.find(u => u.id === orgManagerId || u.auth_user_id === orgManagerId);
                  if (matchedUser) {
                     return matchedUser.name || `${matchedUser.first_name || ''} ${matchedUser.last_name || ''}`.trim() || matchedUser.email || '';
                  }
               }
               if (this.bdms && this.bdms.length > 0) {
                  const matched = this.bdms.find(b => b.auth_user_id === orgManagerId || b.id === orgManagerId || b.db_id === orgManagerId);
                  if (matched) {
                     return matched.name || `${matched.first_name || ''} ${matched.last_name || ''}`.trim() || matched.email || '';
                  }
               }
               if (org.vertex_org_manager) {
                  const name = org.vertex_org_manager.name || `${org.vertex_org_manager.first_name || ''} ${org.vertex_org_manager.last_name || ''}`.trim() || org.vertex_org_manager.email;
                  if (name) return name;
               }
               if (org.vertex_org_manager_name) return org.vertex_org_manager_name;
            }
         } else {
            if (org.vertex_org_manager) {
               const name = org.vertex_org_manager.name || `${org.vertex_org_manager.first_name || ''} ${org.vertex_org_manager.last_name || ''}`.trim() || org.vertex_org_manager.email;
               if (name) return name;
            }
            if (org.vertex_org_manager_name) return org.vertex_org_manager_name;
         }

         return '';
      },
      getVertexLeadName() {
         return this.getVertexLead(this.profileData);
      },
      getDisplayFormerNames() {
         if (!this.profileData) return '';
         if (this.profileData.former_names && this.profileData.former_names.length) {
            return this.profileData.former_names.join(', ');
         }
         if (this.profileData.originalOrganisationName && this.profileData.originalOrganisationName !== this.profileData.name) {
            return this.profileData.originalOrganisationName;
         }
         return '';
      },
      handleDocumentClick(e) {
         const container = this.$el.querySelector('.header-search-container');
         if (container && !container.contains(e.target)) {
            this.showSearchDropdown = false;
         }
         const filterDropdown = this.$el.querySelector('.contacts-filter-dropdown-wrapper');
         if (filterDropdown && !filterDropdown.contains(e.target)) {
            this.showContactsFilterDropdown = false;
         }
      },
      normalizePace(val) {
          if (!val) return '';
          if (val.includes('Relaxed') || val.includes('Methodical')) return 'Methodical / Relaxed';
          if (val.includes('Standard') || val.includes('Moderate')) return 'Moderate / Standard';
          if (val.includes('Fast')) return 'Fast-Paced';
          if (val.includes('Urgent') || val.includes('Reactive')) return 'Urgent / Reactive';
          return val;
       },
       normalizeCost(val) {
          if (!val) return '';
          if (val.includes('High')) return 'High (Highly Budget-Conscious)';
          if (val.includes('Moderate')) return 'Moderate (Value-Driven)';
          if (val.includes('Low')) return 'Low (Not a Primary Concern)';
          return val;
       },
       normalizeTech(val) {
          if (!val) return '';
          if (val.includes('Low')) return 'Low (Business-Focused)';
          if (val.includes('Moderate')) return 'Moderate (Tech-Savvy)';
          if (val.includes('High')) return 'High (Expert / Developer Level)';
          return val;
       },
       getPaceBadgeText(val) {
          if (!val) return 'Standard';
          if (val.includes('Relaxed') || val.includes('Methodical')) return 'Relaxed';
          if (val.includes('Standard') || val.includes('Moderate')) return 'Standard';
          if (val.includes('Fast')) return 'Fast';
          if (val.includes('Urgent') || val.includes('Reactive')) return 'Urgent';
          return val;
       },
       getCostBadgeText(val) {
          if (!val) return 'Moderate';
          if (val.includes('High')) return 'High';
          if (val.includes('Moderate')) return 'Moderate';
          if (val.includes('Low')) return 'Low';
          return val;
       },
       getTechBadgeText(val) {
          if (!val) return 'Moderate';
          if (val.includes('Low')) return 'Low';
          if (val.includes('Moderate')) return 'Moderate';
          if (val.includes('High')) return 'High';
          return val;
       },
       getPaceBadgeClass(val) {
          if (!val) return 'comm-badge--intensity-medium';
          if (val.includes('Relaxed') || val.includes('Methodical')) return 'comm-badge--intensity-low';
          if (val.includes('Standard') || val.includes('Moderate')) return 'comm-badge--intensity-medium';
          if (val.includes('Fast')) return 'comm-badge--intensity-high';
          if (val.includes('Urgent') || val.includes('Reactive')) return 'comm-badge--intensity-critical';
          return 'comm-badge--intensity-medium';
       },
       getCostBadgeClass(val) {
          if (!val) return 'comm-badge--intensity-medium';
          if (val.includes('Low')) return 'comm-badge--intensity-low';
          if (val.includes('Moderate')) return 'comm-badge--intensity-medium';
          if (val.includes('High')) return 'comm-badge--intensity-high';
          return 'comm-badge--intensity-medium';
       },
       getTechBadgeClass(val) {
          if (!val) return 'comm-badge--intensity-medium';
          if (val.includes('Low')) return 'comm-badge--intensity-low';
          if (val.includes('Moderate')) return 'comm-badge--intensity-medium';
          if (val.includes('High')) return 'comm-badge--intensity-high';
          return 'comm-badge--intensity-medium';
       },
       getPaceExplainer(value) {
          if (!value) return '';
          if (value.includes('Relaxed') || value.includes('Methodical')) {
             return "Prefers thoroughness over speed. Needs time to review, digest information, and consult internally. Rarely pushes for urgent deadlines.";
          }
          if (value.includes('Standard') || value.includes('Moderate')) {
             return "Expects typical, predictable turnaround times. Balances speed with quality and respects standard SLA response times.";
          }
          if (value.includes('Fast')) {
             return "High energy and expects quick responses/rapid turnarounds. Prefers to keep momentum high and take immediate action.";
          }
          if (value.includes('Urgent') || value.includes('Reactive')) {
             return "Frequently operates in rush mode. Requires immediate replies, expects team to drop everything for their requests, and pushes aggressive deadlines.";
          }
          return "";
       },
       getCostExplainer(value) {
          if (!value) return '';
          if (value.includes('High')) {
             return "Every dollar counts. Requires strict, detailed justification for all costs. Will actively seek the cheapest option and frequently push back on pricing.";
          }
          if (value.includes('Moderate')) {
             return "Will spend money if the Return on Investment (ROI) or long-term value is clearly demonstrated. Looks for fair pricing rather than the absolute cheapest option.";
          }
          if (value.includes('Low')) {
             return "Quality, speed, or specific technical outcomes trump cost. Willing to authorise premium rates for the right result, expert advice, or reduced friction.";
          }
          return "";
       },
       getTechExplainer(value) {
          if (!value) return '';
          if (value.includes('Low')) {
             return "Non-technical. Needs concepts explained in plain English, focusing on business outcomes and ROI. Avoid jargon, acronyms, or deep architecture discussions.";
          }
          if (value.includes('Moderate')) {
             return "Understands general technical concepts and how systems interact at a high level. Comfortable with tech terminology but does not need to see the underlying code.";
          }
          if (value.includes('High')) {
             return "Deeply technical. Wants to see architecture diagrams, understand the exact tech stack, discuss security protocols, and review implementation details.";
          }
          return "";
       },
       getPaceBadgeTooltip(val) {
          const explainer = this.getPaceExplainer(val);
          return explainer ? `Client Pace Preference: ${explainer}` : 'Client Pace Preference';
       },
       getCostBadgeTooltip(val) {
          const explainer = this.getCostExplainer(val);
          return explainer ? `Client Cost Sensitivity: ${explainer}` : 'Client Cost Sensitivity';
       },
       getTechBadgeTooltip(val) {
          const explainer = this.getTechExplainer(val);
          return explainer ? `Client Technicality: ${explainer}` : 'Client Technicality';
       },
       getUserInitials(name) {
          if (!name) return 'U';
          const parts = name.trim().split(/\s+/);
          if (parts.length >= 2) {
             return (parts[0][0] + parts[1][0]).toUpperCase();
          }
          return name.substring(0, 2).toUpperCase();
       },
       getAvatarStyle(name) {
          if (!name) name = 'User';
          const colors = ['#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4', '#f43f5e'];
          let hash = 0;
          for (let i = 0; i < name.length; i++) {
             hash = name.charCodeAt(i) + ((hash << 5) - hash);
          }
          const color = colors[Math.abs(hash) % colors.length];
          return `background-color: ${color}15; color: ${color}; font-weight: 700; width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 1.05rem; border: 1.5px solid ${color}30;`;
       },
      async getBdms() {
         try {
            const bdmResp = await this.$axios.get('/vertex-bdm');
            if (bdmResp.data && bdmResp.data.success) {
               this.bdms = bdmResp.data.bdms || [];
            }
         } catch (error) {
            console.error('Error fetching BDMs inside dashboard:', error);
         }
      },
      async getVertexUsers() {
         let users = this.$store.state.auth && this.$store.state.auth.usersWithVertexRoles;
         if (!users || !users.length) {
            try {
               const response = await this.$axios.get("/auth/users/super-admin");
               users = response.data.super_admin_users || [];
               this.$store.commit("auth/setUsersWithVertexRoles", users);
            } catch (error) {
               console.error("Error fetching super admin users in dashboard:", error);
               users = [];
            }
         }
         this.vertexUsers = (users || []).map(item => ({
            id: item.id,
            first_name: item.first_name || '',
            last_name: item.last_name || '',
            email: item.email || '',
            name: `${item.first_name || ''} ${item.last_name || ''}`.trim() || item.email || ''
         }));
      },
      async fetchDropdownCategories() {
         try {
            const [mspRes, partnerRes, ecosystemRes, underservedRes, industryRes, disclosureRes] = await Promise.allSettled([
               this.$axios.get("/categories?name=MSP"),
               this.$axios.get("/categories?name=Partner"),
               this.$axios.get("/categories?name=Ecosystem"),
               this.$axios.get("/categories?name=UnderservedType"),
               this.$axios.get("/categories?name=Industry"),
               this.$axios.get("/categories?name=DisclosureIndustry")
            ]);

            if (mspRes.status === 'fulfilled' && mspRes.value?.data?.category?.subcategories) {
               this.mspOptions = mspRes.value.data.category.subcategories;
            }
            if (partnerRes.status === 'fulfilled' && partnerRes.value?.data?.category?.subcategories) {
               this.partnerOptions = partnerRes.value.data.category.subcategories;
            }
            if (ecosystemRes.status === 'fulfilled' && ecosystemRes.value?.data?.category?.subcategories) {
               this.ecosystemOptions = ecosystemRes.value.data.category.subcategories;
            }
            if (underservedRes.status === 'fulfilled' && underservedRes.value?.data?.category?.subcategories?.length > 0) {
               this.underservedTypeOptions = underservedRes.value.data.category.subcategories.map(s => s.display_name || s.name);
            }
            if (industryRes.status === 'fulfilled' && industryRes.value?.data?.category?.subcategories?.length > 0) {
               this.industryOptions = industryRes.value.data.category.subcategories;
            }
            if (disclosureRes.status === 'fulfilled' && disclosureRes.value?.data?.category?.subcategories?.length > 0) {
               this.disclosureIndustryOptions = disclosureRes.value.data.category.subcategories;
            }
         } catch (err) {
            console.error('Error fetching categories for dropdowns:', err);
         }
      },
      async fetchLeads() {
         const shouldManageLoading = !this.selectedLeadId && !this.profileData.id;
         if (shouldManageLoading) {
            this.isLoading = true;
         }
         try {
            const response = await this.$axios.get('/organisations?pageSize=10000&page=1&excludeTestData=false&includeMerged=true');
            if (response.data && response.data.organisations) {
               this.totalOrganisationsCount = response.data.organisations.filter(org => !org.is_merged).length;
            }
            if (response.data && response.data.organisations) {
               this.leads = response.data.organisations.map(org => {
                  const classifications = org.classifications ? org.classifications.map(c => c.classification) : [];
                  let assignedUserName = '';
                  if (org.assigned_user) {
                     assignedUserName = org.assigned_user.name || `${org.assigned_user.first_name || ''} ${org.assigned_user.last_name || ''}`.trim() || org.assigned_user.email || '';
                  }
                  let vertexOrgManagerName = '';
                  if (org.vertex_org_manager) {
                     vertexOrgManagerName = org.vertex_org_manager.name || `${org.vertex_org_manager.first_name || ''} ${org.vertex_org_manager.last_name || ''}`.trim() || org.vertex_org_manager.email || '';
                  }
                  return {
                     id: org.id,
                     uuid: org.uuid || org.id,
                     organisation_uuid: org.uuid || org.id,
                     organisation_name: org.name,
                     name: org.name,
                     abn: org.abn,
                     email: org.email,
                     is_merged: org.is_merged || false,
                     is_deactivated: !!(org.is_inactive || org.is_deactivated),
                     isDeactivated: !!(org.is_inactive || org.is_deactivated),
                     is_inactive: !!(org.is_inactive || org.is_deactivated),
                     merged_into_uuid: org.merged_into_uuid || '',
                     merged_into_name: org.merged_into_name || '',
                     created_at: org.date_created,
                     classifications: classifications,
                     is_partner: classifications.includes('Partner') || org.is_partner || false,
                     is_supplier: classifications.includes('Supplier') || org.is_supplier || false,
                     assigned_user_id: org.assigned_user_id,
                     assigned_user: org.assigned_user,
                     assigned_user_name: assignedUserName,
                     vertex_org_manager_id: org.vertex_org_manager_id || 0,
                     vertex_org_manager: org.vertex_org_manager || null,
                     vertex_org_manager_name: vertexOrgManagerName,
                     former_names: org.former_names || [],
                  };
               });
               
               const queryOrgId = this.$route.query.organisation_uuid || this.$route.query.lead_id;
               if (queryOrgId) {
                  this.selectedLeadId = queryOrgId;
               } else if (!this.selectedLeadId && this.leads.length > 0) {
                  this.selectedLeadId = this.leads[0].id;
               }

               if (this.profileData && (this.profileData.uuid || this.profileData.id)) {
                  const curId = this.profileData.uuid || this.profileData.id;
                  const exists = this.leads.some(l => l.id === curId || l.uuid === curId || l.organisation_uuid === curId);
                  if (!exists) {
                     this.leads.unshift({
                        id: curId,
                        uuid: curId,
                        organisation_uuid: curId,
                        organisation_name: this.profileData.name || 'Unnamed Org',
                        name: this.profileData.name || 'Unnamed Org',
                        company: this.profileData.name || 'Unnamed Org',
                        abn: this.profileData.abn || 'N/A',
                        email: this.profileData.email || '',
                        type: (this.profileData.classifications && this.profileData.classifications[0]) || 'Organisation',
                        classifications: this.profileData.classifications || [],
                        is_merged: !!(this.profileData.is_merged || this.profileData.isMerged),
                        isMerged: !!(this.profileData.is_merged || this.profileData.isMerged),
                        is_deactivated: !!(this.profileData.isDeactivated || this.profileData.is_deactivated || this.profileData.is_inactive),
                        isDeactivated: !!(this.profileData.isDeactivated || this.profileData.is_deactivated || this.profileData.is_inactive),
                        is_inactive: !!(this.profileData.isDeactivated || this.profileData.is_deactivated || this.profileData.is_inactive),
                        merged_into_uuid: this.profileData.merged_into_uuid || '',
                        merged_into_name: this.profileData.merged_into_name || '',
                        created_at: this.profileData.firstContactDate,
                        is_partner: !!this.profileData.isPartner,
                        is_supplier: !!this.profileData.isSupplier,
                        former_names: this.profileData.former_names || [],
                     });
                  }
               }
            }
         } catch (err) {
            console.error('Error fetching organisations:', err);
            this.$toast.error('Could not load organisation list.');
         } finally {
            if (shouldManageLoading) {
               this.isLoading = false;
            }
         }
      },
      async fetchOrganisationDetails(uuid) {
         if (!uuid) return;
         if (this.currentFetchingUuid === uuid) return;
         this.currentFetchingUuid = uuid;
         this.isLoading = true;
         try {
            let org = null;
            let lead = null;
            let leadUuid = null;

            // 1. Try fetching as Organisation
            try {
               const orgResponse = await this.$axios.get(`/organisations/${uuid}`);
               if (orgResponse.data && orgResponse.data.organisation) {
                  org = orgResponse.data.organisation;
                  leadUuid = org.lead_uuid;
               }
            } catch (err) {
               console.log('UUID is not an organisation, trying lead fallback...', err);
            }

            // 2. If not found, try fetching as Lead
            if (!org) {
               try {
                  const leadResponse = await this.$axios.get(`/leads/${uuid}`);
                  if (leadResponse.data && leadResponse.data.lead) {
                     lead = leadResponse.data.lead;
                     leadUuid = lead.id;
                     if (lead.organisation_uuid) {
                        const orgResponse = await this.$axios.get(`/organisations/${lead.organisation_uuid}`);
                        if (orgResponse.data && orgResponse.data.organisation) {
                           org = orgResponse.data.organisation;
                        }
                     }
                  }
               } catch (err) {
                  console.error('Failed to fetch lead fallback:', err);
               }
            } else if (leadUuid) {
               // Fetch lead details for contact names/statuses
               try {
                  const leadResponse = await this.$axios.get(`/leads/${leadUuid}`);
                  if (leadResponse.data && leadResponse.data.lead) {
                     lead = leadResponse.data.lead;
                  }
               } catch (err) {
                  console.warn('Could not fetch associated lead:', err);
               }
            }

            // If we still don't have an organisation but have a lead, build a mock org from the lead
            if (!org && lead) {
               org = {
                  id: lead.organisation_uuid || lead.id,
                  name: lead.organisation_name || '',
                  abn: lead.abn || '',
                  email: lead.email || '',
                  phone_number: lead.phone_number || '',
                  address: lead.country_code || '',
                  background: lead.background || '',
                  is_charity: lead.is_charity || false,
                  is_test_data: lead.is_test_data || false,
                  is_msp_managed: false,
                  msp_uuid: '',
                  is_partner_referred: false,
                  partner_uuid: '',
                  is_ecosystem: false,
                  ecosystem_uuid: '',
                  classifications: lead.classification ? lead.classification.split(',').map(c => ({ classification: c.trim() })) : []
               };
            }

            if (!org) {
               throw new Error('Organisation record not found');
            }

            const classifications = org.classifications ? org.classifications.map(c => c.classification) : [];

            const orgUuid = org.uuid || org.id;
            const isValidatedCharity = Boolean(org.is_charity || (lead && lead.is_charity));
            let purposeDrivenType = org.purpose_driven_type || 'None';
            let reportingCurrent = typeof org.reporting_current !== 'undefined' ? Boolean(org.reporting_current) : false;

            if (isValidatedCharity && (!org.purpose_driven_type || org.purpose_driven_type === 'None')) {
               purposeDrivenType = 'Registered Charity';
               if (typeof org.reporting_current === 'undefined' || (!org.acnc_town_city && !org.acnc_postcode)) {
                  reportingCurrent = true;
               }
            }

            const activePostcode = (purposeDrivenType === 'Registered Charity')
               ? (org.acnc_postcode || org.postcode || '')
               : (org.postcode || '');
            const isLocalSupplier = activePostcode ? this.isPostcodeWithin80km(activePostcode) : Boolean(org.is_local_supplier);

            this.profileData = {
               id: org.id,
               numeric_id: org.id,
               uuid: orgUuid,
               organisation_uuid: orgUuid,
               classifications: classifications || [],
               name: org.name || '',
               abn: org.abn || '',
                firstName: (org && org.org_lead_first_name) ? org.org_lead_first_name : (lead ? (lead.first_name || '') : ''),
                lastName: (org && org.org_lead_last_name) ? org.org_lead_last_name : (lead ? (lead.last_name || '') : ''),
                leadName: (org && (org.org_lead_first_name || org.org_lead_last_name))
                   ? `${org.org_lead_first_name || ''} ${org.org_lead_last_name || ''}`.trim()
                   : ((lead && `${lead.first_name || ''} ${lead.last_name || ''}`.trim()) ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim() : ''),
                phone: (org && (org.org_phone_number || org.phone_number))
                   ? (org.org_phone_number || org.phone_number)
                   : (lead ? (lead.phone_number || '') : ''),
                email: (org && org.email) ? org.email : '',
                org_lead_email: (org && org.org_lead_email) ? org.org_lead_email : (lead ? (lead.email || '') : ''),
                org_lead_first_name: (org && org.org_lead_first_name) ? org.org_lead_first_name : (lead ? (lead.first_name || '') : ''),
                org_lead_last_name: (org && org.org_lead_last_name) ? org.org_lead_last_name : (lead ? (lead.last_name || '') : ''),
                org_lead_phone: (org && org.org_lead_phone) ? org.org_lead_phone : (lead ? (lead.phone_number || '') : ''),
               isCharity: isValidatedCharity || org.is_charity || false,
               address: org.address || '',
               address2: org.address2 || '',
               city: org.city || '',
               state: org.state || '',
               country: org.country || '',
               postcode: org.postcode || '',
               billing_address: org.billing_address || '',
               billing_address2: org.billing_address2 || '',
               billing_city: org.billing_city || '',
               billing_state: org.billing_state || '',
               billing_country: org.billing_country || '',
               billing_postcode: org.billing_postcode || '',
               billing_email: org.billing_email || '',
               delivery_address: org.delivery_address || '',
               delivery_address2: org.delivery_address2 || '',
               delivery_city: org.delivery_city || '',
               delivery_state: org.delivery_state || '',
               delivery_country: org.delivery_country || '',
               delivery_postcode: org.delivery_postcode || '',
               service_address: org.service_address || '',
               service_address2: org.service_address2 || '',
               service_city: org.service_city || '',
               service_state: org.service_state || '',
               service_country: org.service_country || '',
               service_postcode: org.service_postcode || '',
               service_email: org.service_email || '',
               primary_contact_first_name: org.primary_contact_first_name || '',
               primary_contact_last_name: org.primary_contact_last_name || '',
               primary_contact_email: org.primary_contact_email || '',
               primary_contact_phone: org.primary_contact_phone || '',
               background: org.background || '',
               firstContactDate: this.formatDate(org.auth_creation_date || org.created_at || org.date_created),
               clientType: classifications.includes('Client') ? 'Client' : (classifications.includes('Past Client') ? 'Past Client' : (classifications.includes('Prospect') ? 'Prospect' : 'Lead')),
               isTestData: org.is_test_data || false,
               isAuthSynced: org.is_auth_synced || false,
               isAuthDeleted: org.is_auth_deleted || false,
               isSupplier: classifications.includes('Supplier'),
               isPartner: classifications.includes('Partner'),
               currency: org.currency || 'AUD',
               assigned_user_id: Number(org.assigned_user_id || (lead ? lead.assigned_user_id : 0) || 0),
               assigned_user: org.assigned_user || (lead ? lead.assigned_user : null) || null,
               vertex_org_manager_id: Number(org.vertex_org_manager_id || 0),
               vertex_org_manager: org.vertex_org_manager || null,
               clients: ['Vertex Cyber Sec', 'Vertex Cyber Secure', 'Vertex Cyber Security'],
               suppliers: ['Vertex Cyber Sec', 'Vertex Cyber Secure', 'Vertex Cyber Security'],
               partners: ['Partner 1'],
               leadTypeId: lead ? lead.lead_type_id : '',
               leadStatusId: lead ? lead.lead_status_id : '',
               leadPriorityId: lead ? lead.lead_priority_id : '',
               leadUuid: leadUuid || '',
               hasAuthOrganisation: org.has_auth_organisation || 0,
               authOrganisationName: org.auth_organisation_name || "",
               authOrganisationUuid: org.auth_organisation_uuid || "",
                authOrganisationId: org.auth_organisation_id || 0,
                originalOrganisationName: org.original_organisation_name || (lead ? lead.original_organisation_name : '') || (org.pre_link_snapshot ? (() => { try { return JSON.parse(org.pre_link_snapshot).original_organisation_name } catch(e){ return '' } })() : '') || '',
                syncedAt: org.synced_at || null,
               organisationUuid: org.organisation_uuid || org.id,
               xeroContactId: org.xero_contact_id || "",
               isMspManaged: org.is_msp_managed || false,
               mspUuid: org.msp_uuid || '',
               isPartnerReferred: org.is_partner_referred || false,
               partnerUuid: org.partner_uuid || '',
               isEcosystem: org.is_ecosystem || false,
               ecosystemUuid: org.ecosystem_uuid || '',
               is_merged: org.is_merged || false,
               isMerged: org.is_merged || false,
               isDeactivated: !!(org.is_inactive || org.is_deactivated),
               is_deactivated: !!(org.is_inactive || org.is_deactivated),
               is_inactive: !!(org.is_inactive || org.is_deactivated),
               merged_into_uuid: org.merged_into_uuid || '',
               merged_into_name: org.merged_into_name || '',
               merged_at: org.merged_at || null,
               merged_by: org.merged_by || 0,
               merged_by_name: org.merged_by_name || '',
               merge_reason: org.merge_reason || '',
               merge_snapshot: org.merge_snapshot || '',
               former_names: org.former_names || [],
               diversity_form_completed: org.diversity_form_completed || false,
               code_of_conduct_accepted_date: org.code_of_conduct_accepted_date ? moment(org.code_of_conduct_accepted_date).format('YYYY-MM-DD') : '',
               security_risk_rating: org.security_risk_rating || '',
               esg_practices_rating: org.esg_practices_rating || '',
               is_independent_private_business: org.is_independent_private_business || false,
               formal_written_policies: Array.isArray(org.formal_written_policies) ? org.formal_written_policies : [],
               positive_practices: Array.isArray(org.positive_practices) ? org.positive_practices : [],
               certifications: Array.isArray(org.certifications) ? org.certifications : [],
               certifications_other: org.certifications_other || '',
               organisation_type: org.organisation_type || 'Organisation',
               purpose_driven_type: purposeDrivenType,
               underserved_beneficiaries: org.underserved_beneficiaries || false,
               underserved_types: Array.isArray(org.underserved_types) ? org.underserved_types : [],
               organisational_purpose: Array.isArray(org.organisational_purpose) ? org.organisational_purpose : [],
               population_served: Array.isArray(org.population_served) ? org.population_served : [],
               is_pbi: org.is_pbi || false,
               is_hpc: org.is_hpc || false,
               reporting_current: reportingCurrent,
               acnc_town_city: org.acnc_town_city || '',
               acnc_state: org.acnc_state || '',
               acnc_postcode: org.acnc_postcode || '',
               is_local_supplier: isLocalSupplier,
               is_women_owned: org.is_women_owned || false,
               is_minority_owned: org.is_minority_owned || false,
               is_indigenous_owned: org.is_indigenous_owned || false,
               industry_uuid: org.industry_uuid || '',
               industry: org.industry || '',
               is_disclosure_industry: org.is_disclosure_industry || false,
               technology_stack: Array.isArray(org.technology_stack) ? org.technology_stack : []
            };

            this.initialProfileData = JSON.parse(JSON.stringify(this.profileData));

            // Support deactivated org or org not in this.leads selector
            const existingLeadIndex = this.leads.findIndex(l => l.id === orgUuid || l.uuid === orgUuid || l.organisation_uuid === orgUuid || l.id === org.id);
            const leadItem = {
               id: orgUuid,
               uuid: orgUuid,
               organisation_uuid: orgUuid,
               organisation_name: org.name || 'Unnamed Org',
               name: org.name || 'Unnamed Org',
               company: org.name || 'Unnamed Org',
               abn: org.abn || 'N/A',
               email: org.email || '',
               type: classifications[0] || 'Organisation',
               classifications: classifications,
               is_merged: !!org.is_merged,
               isMerged: !!org.is_merged,
               is_deactivated: !!(org.is_inactive || org.is_deactivated),
               isDeactivated: !!(org.is_inactive || org.is_deactivated),
               is_inactive: !!(org.is_inactive || org.is_deactivated),
               merged_into_uuid: org.merged_into_uuid || '',
               merged_into_name: org.merged_into_name || '',
               created_at: org.date_created || org.created_at,
               is_partner: classifications.includes('Partner'),
               is_supplier: classifications.includes('Supplier'),
               assigned_user_id: org.assigned_user_id,
               assigned_user: org.assigned_user,
               vertex_org_manager_id: org.vertex_org_manager_id || 0,
               vertex_org_manager: org.vertex_org_manager || null,
               former_names: org.former_names || [],
            };
            if (existingLeadIndex === -1) {
               this.leads.unshift(leadItem);
            } else {
               this.leads[existingLeadIndex] = { ...this.leads[existingLeadIndex], ...leadItem };
            }
            
            // Parallelize secondary details fetches to avoid waterfall latency
            const isClientOrPastClient = classifications.includes('Client') || classifications.includes('Past Client');
            if (!isClientOrPastClient) {
               this.orgActiveJobs = [];
               if (this.activeTab === 'jobs_board') {
                  this.activeTab = 'profile';
               }
            }
            const secondaryPromises = [
               this.fetchCombinedTimeline(orgUuid),
               this.fetchClientCommunications(orgUuid),
               this.fetchOrganisationContacts(),
               this.fetchMergedOrganisations(orgUuid),
               ...(isClientOrPastClient ? [this.fetchOrganisationActiveJobs(orgUuid)] : [])
            ];

            if (this.profileData.hasAuthOrganisation == 1 && this.profileData.authOrganisationId) {
               secondaryPromises.push(this.fetchOrgUsers(this.profileData.authOrganisationId));
            } else {
               this.orgUsers = [];
            }

            await Promise.allSettled(secondaryPromises);

            if (this.profileData.purpose_driven_type === 'Registered Charity' && this.profileData.abn && (!this.profileData.acnc_town_city || !this.profileData.acnc_postcode)) {
               this.validateAcnc({ silent: true });
            }
         } catch (err) {
            console.error('Error fetching organisation/lead details:', err);
            this.$toast.error('Could not load organisation details.');
         } finally {
            this.currentFetchingUuid = '';
            this.isLoading = false;
         }
      },
      async fetchOrganisationActiveJobs(orgUuid = '') {
         const targetUuid = orgUuid || (this.profileData && (this.profileData.id || this.profileData.uuid)) || this.selectedLeadId;
         if (!targetUuid) return;
         this.isJobsLoading = true;
         try {
            const res = await this.$axios.get(`/api/v1/jobs/active?organisation_uuid=${targetUuid}`);
            if (res.data && res.data.data) {
               this.orgActiveJobs = res.data.data;
            } else {
               this.orgActiveJobs = [];
            }
         } catch (err) {
            console.error('Error fetching organisation active jobs:', err);
            this.orgActiveJobs = [];
         } finally {
            this.isJobsLoading = false;
         }
      },
      async fetchAvailableBlueprints() {
         if (this.availableBlueprints && this.availableBlueprints.length > 0) return;
         this.isLoadingBlueprints = true;
         try {
            const res = await this.$axios.get('/api/v1/jobs/blueprints?is_active=true');
            if (res.data && res.data.data) {
               this.availableBlueprints = res.data.data;
            }
         } catch (err) {
            console.error('Error fetching job blueprints:', err);
         } finally {
            this.isLoadingBlueprints = false;
         }
      },
      openStageJobModal() {
         this.stageJobForm = {
            blueprint_id: '',
            title: '',
            description: ''
         };
         this.showStageJobModal = true;
         this.fetchAvailableBlueprints();
      },
      closeStageJobModal() {
         this.showStageJobModal = false;
         this.isStagingJob = false;
      },
      onBlueprintSelected() {
         const bp = (this.availableBlueprints || []).find(b => String(b.id) === String(this.stageJobForm.blueprint_id) || b.uuid === this.stageJobForm.blueprint_id);
         if (bp && !this.stageJobForm.title) {
            this.stageJobForm.title = bp.title;
         }
      },
      async submitStageJob() {
         const targetUuid = (this.profileData && (this.profileData.id || this.profileData.uuid)) || this.selectedLeadId;
         if (!targetUuid) {
            this.$toast.error('Unable to determine organisation identifier.');
            return;
         }
         this.isStagingJob = true;
         try {
            const payload = {
               blueprint_id: String(this.stageJobForm.blueprint_id),
               organisation_uuid: String(targetUuid),
               title: this.stageJobForm.title,
               description: this.stageJobForm.description
            };
            const res = await this.$axios.post('/api/v1/jobs/active', payload);
            if (res.data && res.data.status) {
               this.$toast.success('Workflow job staged successfully!');
               this.closeStageJobModal();
               await this.fetchOrganisationActiveJobs();
               if (res.data.data && res.data.data.id) {
                  this.openOrgActiveJobModal(res.data.data.id);
               }
            } else {
               this.$toast.error(res.data?.message || 'Failed to stage workflow job.');
            }
         } catch (err) {
            console.error('Error staging active job:', err);
            this.$toast.error(err.response?.data?.message || 'Failed to stage active job.');
         } finally {
            this.isStagingJob = false;
         }
      },
      async openOrgActiveJobModal(jobId) {
         if (!jobId) return;
         this.showOrgJobDetailsModal = true;
         this.loadingOrgJobDetails = true;
         this.selectedOrgActiveJob = null;
         try {
            const res = await this.$axios.get(`/api/v1/jobs/active/${jobId}`);
            if (res.data && res.data.data) {
               this.selectedOrgActiveJob = res.data.data;
            }
         } catch (err) {
            console.error('Error fetching active job details:', err);
            this.$toast.error('Failed to load active job details.');
         } finally {
            this.loadingOrgJobDetails = false;
         }
      },
      closeOrgActiveJobModal() {
         this.showOrgJobDetailsModal = false;
         this.selectedOrgActiveJob = null;
      },
      async updateActiveJobStatus(job, newStatus) {
         if (!job) return;
         const jobUuid = job.uuid || job.id;
         if (!jobUuid) return;
         try {
            const res = await this.$axios.put(`/api/v1/jobs/active/${jobUuid}`, { job_status: newStatus });
            if (res.data && res.data.status) {
               job.job_status = newStatus;
               const match = this.orgActiveJobs.find(j => (j.uuid || j.id) === jobUuid);
               if (match) match.job_status = newStatus;
               this.$toast.success(`Job marked as ${newStatus}`);
            }
         } catch (err) {
            console.error('Error updating job status:', err);
            this.$toast.error(err.response?.data?.message || 'Failed to update job status.');
         }
      },
      async updateActiveJobHealth(job, newHealth) {
         if (!job) return;
         const jobUuid = job.uuid || job.id;
         if (!jobUuid) return;
         try {
            const res = await this.$axios.put(`/api/v1/jobs/active/${jobUuid}`, { health_indicator: newHealth });
            if (res.data && res.data.status) {
               job.health_indicator = newHealth;
               const match = this.orgActiveJobs.find(j => (j.uuid || j.id) === jobUuid);
               if (match) match.health_indicator = newHealth;
               this.$toast.success(`Job health updated to ${newHealth}`);
            }
         } catch (err) {
            console.error('Error updating job health:', err);
            this.$toast.error(err.response?.data?.message || 'Failed to update job health.');
         }
      },
      async updateActiveTaskStatus(task, newStatus) {
         if (!task || (!task.uuid && !task.id) || !this.selectedOrgActiveJob) return;
         const jobUuid = this.selectedOrgActiveJob.uuid || this.selectedOrgActiveJob.id;
         const taskUuid = task.uuid || task.id;
         this.updatingTaskId = task.id;
         try {
            const res = await this.$axios.put(`/api/v1/jobs/active/${jobUuid}/tasks/${taskUuid}`, { task_status: newStatus });
            if (res.data && res.data.status) {
               task.task_status = newStatus;
               this.$toast.success(`Task status updated to ${newStatus}`);
               // Refresh parent job details to recalculate progress
               const jobRes = await this.$axios.get(`/api/v1/jobs/active/${jobUuid}`);
               if (jobRes.data && jobRes.data.data) {
                  this.selectedOrgActiveJob = jobRes.data.data;
                  const idx = this.orgActiveJobs.findIndex(j => (j.uuid || j.id) === jobUuid);
                  if (idx !== -1) {
                     this.orgActiveJobs[idx] = { ...this.orgActiveJobs[idx], ...jobRes.data.data };
                  }
               }
            }
         } catch (err) {
            console.error('Error updating task status:', err);
            this.$toast.error(err.response?.data?.message || 'Failed to update task status.');
         } finally {
            this.updatingTaskId = null;
         }
      },
      async deleteOrgActiveJob(job) {
         if (!job) return;
         const confirmDelete = window.confirm(`Are you sure you want to delete the active job "${job.title}"? This cannot be undone.`);
         if (!confirmDelete) return;
         const jobUuid = job.uuid || job.id;
         try {
            const res = await this.$axios.delete(`/api/v1/jobs/active/${jobUuid}`);
            if (res.data && res.data.status) {
               this.$toast.success('Active job deleted.');
               this.orgActiveJobs = this.orgActiveJobs.filter(j => (j.uuid || j.id) !== jobUuid);
               if (this.selectedOrgActiveJob && (this.selectedOrgActiveJob.uuid || this.selectedOrgActiveJob.id) === jobUuid) {
                  this.closeOrgActiveJobModal();
               }
            }
         } catch (err) {
            console.error('Error deleting active job:', err);
            this.$toast.error(err.response?.data?.message || 'Failed to delete active job.');
         }
      },
      calculateJobProgress(job) {
         if (!job || !job.tasks_count || job.tasks_count === 0) return 0;
         const completed = job.completed_tasks_count || 0;
         return Math.round((completed / job.tasks_count) * 100);
      },
      getJobStatusBadgeClass(status) {
         switch (status) {
            case 'Completed':
               return 'bg-success text-white';
            case 'In Progress':
               return 'bg-info text-white';
            case 'Cancelled':
               return 'bg-danger text-white';
            case 'Pending':
            default:
               return 'bg-warning text-dark';
         }
      },
      getJobHealthBadgeClass(health) {
         switch (health) {
            case 'Behind Plan':
               return 'bg-warning text-dark';
            case 'Overdue':
               return 'bg-danger text-white';
            case 'On Track':
            default:
               return 'bg-success text-white';
         }
      },
      getTaskStatusBadgeClass(status) {
         switch (status) {
            case 'Completed':
               return 'bg-success text-white';
            case 'InProgress':
            case 'In Progress':
               return 'bg-primary text-white';
            case 'Initiated':
               return 'bg-info text-white';
            case 'Planned':
            default:
               return 'bg-light text-secondary border';
         }
      },
      async fetchOrganisationContacts(forceSync = false) {
         const orgId = (this.profileData && (this.profileData.id || this.profileData.uuid)) || this.selectedLeadId;
         if (!orgId) return;

         if (forceSync) {
            this.isContactsSyncing = true;
         } else {
            this.isContactsLoading = true;
         }

         try {
            const url = `/organisations/${orgId}/contacts${forceSync ? '?sync=true' : ''}`;
            const res = await this.$axios.get(url);
            if (res.data && res.data.contacts) {
               this.orgContacts = res.data.contacts;
               if (forceSync) {
                  this.$toast.success(res.data.message || 'Contacts synced successfully.');
               }
            } else {
               this.orgContacts = [];
            }
         } catch (err) {
            console.error('Error fetching organisation contacts:', err);
            if (forceSync) {
               this.$toast.error('Failed to sync contacts.');
            }
         } finally {
            this.isContactsLoading = false;
            this.isContactsSyncing = false;
         }
      },
      async syncOrganisationContacts() {
         if (this.profileData.hasAuthOrganisation != 1) {
            this.$toast.warning("Please link this client to an Auth organisation first to enable sync.");
            return;
         }
         await this.fetchOrganisationContacts(true);
      },
      getContactTypeBadgeClass(type) {
         if (!type) return 'contact-type-badge-default';
         const t = type.toLowerCase().trim();
         switch (t) {
            case 'lead':
               return 'contact-type-badge-lead';
            case 'primary':
               return 'contact-type-badge-primary';
            case 'technical':
               return 'contact-type-badge-technical';
            case 'billing':
               return 'contact-type-badge-billing';
            case 'signer':
               return 'contact-type-badge-signer';
            case 'user':
               return 'contact-type-badge-user';
            default:
               return 'contact-type-badge-default';
         }
      },
      getContactInitials(firstName, lastName, email) {
         const f = (firstName || '').trim();
         const l = (lastName || '').trim();
         if (f && l) {
            return (f[0] + l[0]).toUpperCase();
         }
         if (f) return f.slice(0, 2).toUpperCase();
         if (l) return l.slice(0, 2).toUpperCase();
         if (email) return email.slice(0, 2).toUpperCase();
         return 'C';
      },
      async copyContactEmail(email) {
         if (!email) return;
         try {
            await navigator.clipboard.writeText(email);
            this.copiedContactEmail = email;
            this.$toast.success(`Copied ${email} to clipboard!`);
            setTimeout(() => {
               if (this.copiedContactEmail === email) {
                  this.copiedContactEmail = null;
               }
            }, 2500);
         } catch (err) {
            const el = document.createElement('textarea');
            el.value = email;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            this.copiedContactEmail = email;
            this.$toast.success(`Copied ${email} to clipboard!`);
            setTimeout(() => {
               if (this.copiedContactEmail === email) {
                  this.copiedContactEmail = null;
               }
            }, 2500);
         }
      },
      createProposalForContact(contact) {
         const orgId = (this.profileData && (this.profileData.id || this.profileData.uuid || this.profileData.organisationUuid)) || this.selectedLeadId;
         const query = {
            create: 'true'
         };
         if (orgId) {
            query.org_id = orgId;
         }
         if (this.profileData && this.profileData.leadUuid) {
            query.lead_id = this.profileData.leadUuid;
         }
         if (contact) {
            if (contact.id) query.contact_id = String(contact.id);
            if (contact.uuid) query.contact_uuid = String(contact.uuid);
            if (contact.email) query.contact_email = String(contact.email);
         }
         this.$router.push({ path: '/proposals', query });
      },
      resolveTimelineUser(userId) {
         if (!userId || userId === 0 || userId === '0') {
            return { isSystem: true, name: 'System Generated' };
         }

         const numericId = parseInt(userId);

         // 1. Check currentUser
         const currentUser = this.$store.state.auth && this.$store.state.auth.currentUser;
         if (currentUser && (currentUser.id === numericId || currentUser.auth_user_id === numericId)) {
            const name = `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.name || currentUser.email;
            if (name) return { isSystem: false, name };
         }

         // 2. Check usersWithVertexRoles in store
         const usersWithRoles = (this.$store.state.auth && this.$store.state.auth.usersWithVertexRoles) || [];
         if (Array.isArray(usersWithRoles)) {
            const matchedRoleUser = usersWithRoles.find(u => u.id === numericId || u.auth_user_id === numericId);
            if (matchedRoleUser) {
               const name = `${matchedRoleUser.first_name || ''} ${matchedRoleUser.last_name || ''}`.trim() || matchedRoleUser.name || matchedRoleUser.email;
               if (name) return { isSystem: false, name };
            }
         }

         // 3. Check bdms list
         if (this.bdms && Array.isArray(this.bdms)) {
            const matchedBdm = this.bdms.find(b => b.auth_user_id === numericId || b.id === numericId || b.user_id === numericId);
            if (matchedBdm) {
               const name = matchedBdm.name || `${matchedBdm.first_name || ''} ${matchedBdm.last_name || ''}`.trim() || matchedBdm.email;
               if (name) return { isSystem: false, name };
            }
         }

         // 4. Check orgUsers if available
         if (this.orgUsers && Array.isArray(this.orgUsers)) {
            const matchedOrgUser = this.orgUsers.find(u => u.id === numericId);
            if (matchedOrgUser && matchedOrgUser.name) {
               return { isSystem: false, name: matchedOrgUser.name };
            }
         }

         return { isSystem: false, name: `User #${numericId}` };
      },
      async fetchCombinedTimeline(orgUuid) {
         if (!orgUuid) {
            this.timelineEvents = [];
            return;
         }
         try {
            const response = await this.$axios.get(`/organisations/${orgUuid}/combined-timeline`);
            if (response.data && response.data.lead_notes) {
               const notes = response.data.lead_notes;
               
               this.timelineEvents = notes.map(note => {
                  const type = note.note_type || '';
                  const isAuthSync = !!note.is_auth_sync ||
                                     type === 'auth_org_link' ||
                                     type === 'auth_sync' ||
                                     (note.note_details && (
                                        note.note_details.includes('Auth Sync') ||
                                        note.note_details.includes('Auth organisation') ||
                                        note.note_details.includes('creation in Auth')
                                     ));

                  let image = null;
                  if (isAuthSync || type.includes('auth') || type === 'auth_org_link' || type === 'auth_sync') {
                     image = require('@/assets/img/vertex_products_logo/Auth.svg');
                  } else if (type.includes('proposal') || type === 'proposal_sent' || type === 'proposal_accepted') {
                     image = require('@/assets/img/S.svg');
                  }

                  let isSystem = false;
                  let userName = '';
                  if (note.is_system !== undefined && note.is_system !== null) {
                     isSystem = !!note.is_system;
                     userName = note.user_name || (isSystem ? 'System Generated' : `User #${note.created_by}`);
                  } else {
                     const userInfo = this.resolveTimelineUser(note.created_by);
                     isSystem = userInfo.isSystem;
                     userName = userInfo.name;
                  }

                  let title = this.getNoteTypeName(type);
                  if (type !== 'classification' && type !== 'org_update' && type !== 'client_communication' && !title.endsWith('Update') && !title.endsWith('Updated') && !title.endsWith('Linkage') && !title.endsWith('Sync') && !title.endsWith('Generated') && !title.endsWith('Sent') && !title.endsWith('Signed') && !title.endsWith('Declined')) {
                     title += ' Event';
                  }

                  return {
                     id: note.id || note.uuid,
                     time: moment(note.date_created || note.created_at).format('D-MMM-YYYY h:mm A'),
                     title: title,
                     description: this.formatNoteDetails(note.note_details, type),
                     icon: this.getNoteTypeIcon(type),
                     image: image,
                     isSystem: isSystem,
                     isAuthSync: isAuthSync,
                     userName: userName
                  };
               });
            } else {
               this.timelineEvents = [];
            }
         } catch (err) {
            console.error('Error fetching timeline:', err);
            this.timelineEvents = [];
         }
      },
      formatNoteDetails(details, type) {
         if (!details) return '';
         if (type === 'classification') {
            const parts = details.split(',').map(p => p.trim());
            const primaryClasses = ['Client', 'Past Client', 'Prospect', 'Lead'];
            const primaryParts = parts.filter(p => primaryClasses.includes(p));
            if (primaryParts.length >= 2) {
               return `${primaryParts[0]} ➔ ${primaryParts[1]}`;
            } else if (primaryParts.length === 1) {
               return primaryParts[0];
            }
            return details;
         }
         if (type && (type.includes('status') || type.includes('priority'))) {
            const parts = details.split(',').map(p => p.trim());
            const formattedParts = parts.map(part => {
               // Add space before capital letters in camelCase
               return part.replace(/([a-z])([A-Z])/g, '$1 $2');
            });
            return formattedParts.join(' ➔ ');
         }
         return details;
      },
      getNoteTypeName(type) {
         if (!type) return 'Note';
         if (type === 'classification') return 'Organisation Type Updated';
         if (type === 'lead_generated' || type.includes('initial_status')) return 'Lead Generated';
         if (type === 'proposal_sent') return 'Proposal Sent';
         if (type === 'proposal_accepted') return 'Proposal Accepted';
         if (type === 'proposal_declined') return 'Proposal Declined';
         if (type === 'contract_sent') return 'Contract Sent';
         if (type === 'contract_accepted') return 'Contract Signed';
         if (type === 'contract_declined') return 'Contract Declined';
         if (type === 'auth_org_link') return 'Auth Organisation Linkage';
         if (type === 'auth_sync') return 'Auth Organisation Sync';
         if (type === 'client_communication') return 'Client Communication';
         if (type === 'org_update') return 'Organisation Profile Updated';
         if (type === 'org_merge') return 'Organisation Merged';
         if (type === 'org_unmerge') return 'Organisation Unmerged';

         if (type.includes('status')) return 'Status Update';
         if (type.includes('priority')) return 'Priority Update';
         if (type.includes('proposal')) return 'Proposal';
         if (type.includes('contract')) return 'Contract';
         return 'Note';
      },
      getNoteTypeIcon(type) {
         if (!type) return 'fas fa-sticky-note text-secondary';
         if (type === 'classification') return 'fas fa-building text-info';
         if (type === 'lead_generated' || type.includes('initial_status')) return 'fas fa-user-plus text-primary';
         if (type === 'proposal_sent') return 'fas fa-paper-plane text-info';
         if (type === 'proposal_accepted') return 'fas fa-check-circle text-success';
         if (type === 'proposal_declined') return 'fas fa-times-circle text-danger';
         if (type === 'contract_sent') return 'fas fa-file-contract text-warning';
         if (type === 'contract_accepted') return 'fas fa-signature text-success';
         if (type === 'contract_declined') return 'fas fa-times-circle text-danger';
         if (type === 'auth_org_link' || type === 'auth_sync') return 'fas fa-shield-alt text-primary';
         if (type === 'client_communication') return 'fas fa-comments text-primary';
         if (type === 'org_update') return 'fas fa-id-card text-info';
         if (type === 'org_merge') return 'fas fa-code-branch text-warning';
         if (type === 'org_unmerge') return 'fas fa-undo text-success';

         if (type.includes('status')) return 'fas fa-tasks text-warning';
         if (type.includes('priority')) return 'fas fa-exclamation-circle text-danger';
         if (type.includes('proposal')) return 'fas fa-file-invoice text-info';
         if (type.includes('contract')) return 'fas fa-file-signature text-success';
         return 'fas fa-sticky-note text-secondary';
      },

      openDeleteOrgModal() {
         this.showDeleteOrgModal = true;
      },
      closeDeleteOrgModal() {
         this.showDeleteOrgModal = false;
      },
      async deleteOrganisation() {
         const uuid = this.profileData.uuid || this.profileData.id;
         if (!uuid) return;
         this.isDeleting = true;
         try {
            const response = await this.$axios.delete(`/organisations/${uuid}`);
            if (response.data.success) {
               this.$toast.success(response.data.message || 'Organisation deleted successfully.');
               this.closeDeleteOrgModal();
               this.$router.push({ name: 'clients-view' });
            } else {
               this.$toast.error(response.data.message || 'Failed to delete organisation.');
            }
         } catch (error) {
            const msg = error.response && error.response.data && error.response.data.message
               ? error.response.data.message
               : 'Error deleting organisation';
            this.$toast.error(msg);
         } finally {
            this.isDeleting = false;
         }
      },
      openDeactivateOrgModal() {
         this.showDeactivateOrgModal = true;
      },
      closeDeactivateOrgModal() {
         this.showDeactivateOrgModal = false;
      },
      async deactivateOrganisation() {
         const uuid = this.profileData.uuid || this.profileData.id;
         if (!uuid) return;
         this.isDeactivating = true;
         try {
            const response = await this.$axios.delete(`/organisations/${uuid}`);
            if (response.data.success) {
               this.$toast.success('Organisation deactivated successfully.');
               this.closeDeactivateOrgModal();
               this.$router.push({ name: 'clients-view' });
            } else {
               this.$toast.error(response.data.message || 'Failed to deactivate organisation.');
            }
         } catch (error) {
            const msg = error.response && error.response.data && error.response.data.message
               ? error.response.data.message
               : 'Error deactivating organisation';
             this.$toast.error(msg);
         } finally {
             this.isDeactivating = false;
         }
      },
      confirmDeleteOrganisation() {
         this.openDeactivateOrgModal();
      },
      async confirmRestoreOrganisation() {
         const uuid = this.selectedLeadId || (this.profileData && (this.profileData.uuid || this.profileData.id));
         if (!uuid) return;

         if (this.$swal) {
            const result = await this.$swal({
               title: 'Restore Organisation?',
               text: 'Are you sure you want to restore this organisation back to active status?',
               icon: 'question',
               showCancelButton: true,
               confirmButtonText: 'Yes, Restore',
               cancelButtonText: 'Cancel',
               confirmButtonColor: '#10b981'
            });
            if (!result || (!result.isConfirmed && result.value !== true)) {
               return;
            }
         }

         this.isRestoringOrg = true;
         try {
            const httpClient = this.$http || this.$axios;
            let response;
            try {
               response = await httpClient.put(`/organisations/${uuid}/restore`);
            } catch (putErr) {
               if (putErr.response && putErr.response.status === 405) {
                  response = await httpClient.post(`/organisations/${uuid}/restore`);
               } else {
                  throw putErr;
               }
            }

            if (response && response.data && (response.data.success || response.data.status === 'success')) {
               this.profileData.isDeactivated = false;
               this.profileData.is_deactivated = false;
               this.profileData.is_inactive = false;
               this.$toast.success((response.data && response.data.message) || 'Organisation restored successfully.');
               await this.fetchLeads();
               await this.fetchOrganisationDetails(uuid);
            } else {
               this.$toast.error((response && response.data && response.data.message) || 'Failed to restore organisation.');
            }
         } catch (error) {
            const msg = error.response && error.response.data && error.response.data.message
               ? error.response.data.message
               : 'Error restoring organisation';
            this.$toast.error(msg);
         } finally {
            this.isRestoringOrg = false;
         }
      },
      openInAuth() {
         if (this.profileData.hasAuthOrganisation == 1 && this.profileData.authOrganisationId) {
            const authBaseUrl = this.$config.getAuthBaseUrl();
            const accessToken = this.$store.state.auth.accessToken;
            const orgId = this.profileData.authOrganisationId;
            const url = `${authBaseUrl}/organisations/${orgId}/settings/?access_token=${accessToken}&org_selected=${orgId}`;
            window.open(url, '_blank');
         } else {
            this.$toast.warning("This organisation is not linked to Auth yet. Please link it first under 'Auth Organisation Linkage'.");
         }
      },
      openInXero() {
         if (this.profileData.hasAuthOrganisation == 1) {
            const xeroBaseUrl = process.env.VUE_APP_XERO_BASE_URL || 'https://go.xero.com';
            const shortCode = process.env.VUE_APP_XERO_SHORT_CODE || '!skYq5';
            const contactId = this.profileData.xeroContactId;
            const url = contactId 
               ? `${xeroBaseUrl}/app/${shortCode}/contacts/contact/${contactId}`
               : `${xeroBaseUrl}/Organisation/Dashboard`;
            window.open(url, '_blank');
         } else {
            this.$toast.warning("This organisation is not linked to Auth/Xero yet. Please link it first to enable Xero integration.");
         }
      },
      async manualSyncFromAuth() {
         if (this.isPastClient) {
            if (typeof this.$notify === 'function') {
               this.$notify({
                  type: 'warning',
                  title: 'Sync Disabled',
                  text: 'Auth sync is disabled for Past Clients.',
               });
            } else if (this.$toast && typeof this.$toast.warning === 'function') {
               this.$toast.warning('Auth sync is disabled for Past Clients.');
            }
            return;
         }
         const orgId = this.profileData.authOrganisationId;
         if (!orgId) {
            this.$toast.warning("Please link this client to an Auth organisation first to enable manual sync.");
            return;
         }

         this.isSyncing = true;
         try {
            const response = await this.$axios.post(`/auth/organisations/${orgId}/sync`);
            const uuid = this.profileData.uuid || this.profileData.id;
            if (response.data && response.data.success) {
               this.$toast.success(response.data.message || "Organisation synced successfully.");
               await this.fetchOrganisationDetails(uuid);
               await this.fetchLeads();
            } else if (response.data && response.data.tagged_past_client) {
               this.$toast.warning(response.data.message || "Auth organisation not found. Marked as Past Client.");
               await this.fetchOrganisationDetails(uuid);
               await this.fetchLeads();
            } else {
               this.$toast.error(response.data.message || "Failed to sync organisation.");
            }
         } catch (error) {
            console.error("Sync error:", error);
            const msg = error.response && error.response.data && error.response.data.message ? error.response.data.message : 'Error syncing organisation details.';
            this.$toast.error(msg);
            const uuid = this.profileData.uuid || this.profileData.id;
            if (uuid && error.response && (error.response.status === 404 || (error.response.data && error.response.data.tagged_past_client) || msg.toLowerCase().includes('past client'))) {
               await this.fetchOrganisationDetails(uuid);
               await this.fetchLeads();
            }
         } finally {
            this.isSyncing = false;
         }
      },
      openMarkAuthModal() {
         this.authMarkModal = {
            show: true,
            type: 'mark',
            isSubmitting: false
         };
      },
      openUnmarkAuthModal() {
         this.authMarkModal = {
            show: true,
            type: 'unmark',
            isSubmitting: false
         };
      },
      closeAuthMarkModal() {
         this.authMarkModal.show = false;
         this.authMarkModal.isSubmitting = false;
      },
      async confirmAuthMarkAction() {
         const isMark = this.authMarkModal.type === 'mark';
         const uuid = this.profileData.uuid || this.profileData.id;
         const orgData = {
            auth_organisation_id: 0,
            has_auth_organisation: isMark ? 2 : 0,
            auth_organisation_name: "",
            auth_organisation_uuid: "",
            xero_contact_id: "",
            marked_auth_creation: isMark
         };

         this.authMarkModal.isSubmitting = true;
         try {
            const response = await this.$axios.put(`/organisations/${uuid}/auth/organisation`, orgData);
            if (response.data.success) {
               this.$toast.success(response.data.message || (isMark ? 'Marked for Auth creation' : 'Unmarked from Auth creation'));
               this.closeAuthMarkModal();
               this.fetchOrganisationDetails(uuid);
            } else {
               this.$toast.error(response.data.message);
            }
         } catch (error) {
            const msg = error.response && error.response.data && error.response.data.message ? error.response.data.message : `Error ${isMark ? 'marking' : 'unmarking'} organisation`;
            this.$toast.error(msg);
         } finally {
            this.authMarkModal.isSubmitting = false;
         }
      },
      addPartner() {
         if (this.selectedPartnerToAdd && !this.profileData.partners.includes(this.selectedPartnerToAdd)) {
            this.profileData.partners.push(this.selectedPartnerToAdd);
            this.$toast.success(`Linked partner: ${this.selectedPartnerToAdd}`);
         }
         this.selectedPartnerToAdd = '';
      },
       removePartner(partner) {
          this.profileData.partners = this.profileData.partners.filter(p => p !== partner);
          this.$toast.info(`Unlinked partner: ${partner}`);
       },
       async fetchOrgUsers(authOrgId) {
          if (!authOrgId) {
             this.orgUsers = [];
             return;
          }
          this.isLoadingUsers = true;
          try {
             const response = await this.$axios.get(`/auth/organisations/${authOrgId}/users`);
             if (response.data && response.data.users) {
                this.orgUsers = response.data.users.map(u => ({
                   id: u.id,
                   name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
                   email: u.email
                }));
             } else {
                this.orgUsers = [];
             }
          } catch (err) {
             console.error('Error fetching org users:', err);
             this.orgUsers = [];
          } finally {
             this.isLoadingUsers = false;
          }
       },
       async fetchClientCommunications(orgUuid) {
          if (!orgUuid) {
             this.clientCommunications = [];
             return;
          }
          try {
             const response = await this.$axios.get(`/organisations/${orgUuid}/client-communications`);
             if (response.data && response.data.client_communications) {
                this.clientCommunications = response.data.client_communications;
             } else {
                this.clientCommunications = [];
             }
          } catch (err) {
             console.error('Error fetching client communications:', err);
             this.clientCommunications = [];
          }
       },
       getUserName(userId) {
          const user = this.orgUsers.find(u => u.id === userId);
          return user ? user.name : `User ID: ${userId}`;
       },
       getUserEmail(userId) {
          const user = this.orgUsers.find(u => u.id === userId);
          return user ? user.email : '';
       },
        editCommPrefs(comm) {
          this.editingCommId = comm.id;
          if (comm.is_linked) {
             const user = this.orgUsers.find(u => u.id === comm.user_id);
             this.selectedUsers = user ? [user] : [{ id: comm.user_id, name: comm.name || `User ID: ${comm.user_id}` }];
             this.commForm = {
                name: '',
                clientPace: this.normalizePace(comm.client_pace || ''),
                costSensitivity: this.normalizeCost(comm.cost_sensitivity || ''),
                technicality: this.normalizeTech(comm.technicality || ''),
                decisionStyle: comm.decision_style || '',
                communicationStyle: comm.communication_style || ''
             };
          } else {
             this.selectedUsers = [];
             this.commForm = {
                name: comm.name || '',
                clientPace: this.normalizePace(comm.client_pace || ''),
                costSensitivity: this.normalizeCost(comm.cost_sensitivity || ''),
                technicality: this.normalizeTech(comm.technicality || ''),
                decisionStyle: comm.decision_style || '',
                communicationStyle: comm.communication_style || ''
             };
          }
       },
       cancelEditComm() {
         this.editingCommId = null;
         this.commModalMode = 'form';
         this.commModalContact = null;
         this.selectedUsers = [];
         this.commForm = {
            name: '',
            clientPace: '',
            costSensitivity: '',
            technicality: '',
            decisionStyle: '',
            communicationStyle: ''
         };
      },
      openCreateCommModal() {
         this.cancelEditComm();
         this.commModalMode = 'form';
         this.showCommModal = true;
      },
      closeCommModal() {
         this.cancelEditComm();
         this.showCommModal = false;
      },
      getContactKey(contact) {
         return contact.id || contact.uuid || contact.email;
      },
      getContactFullName(contact) {
         if (contact.isVirtual) {
            return (contact.first_name + ' ' + contact.last_name).trim() || contact.email;
         }
         return `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
      },
      getContactCommPref(contact) {
         if (!this.clientCommunications || !this.clientCommunications.length) return null;
         return this.clientCommunications.find(c => {
            if (contact.auth_user_id && c.is_linked && Number(contact.auth_user_id) === Number(c.user_id)) {
               return true;
            }
            if (c.is_linked && c.user_id) {
               const u = this.orgUsers.find(user => Number(user.id) === Number(c.user_id));
               if (u) {
                  const cEmail = (contact.email || '').toLowerCase();
                  const cName = this.getContactFullName(contact).toLowerCase();
                  if ((cEmail && cEmail === u.email.toLowerCase()) || (cName && cName === u.name.toLowerCase())) {
                     return true;
                  }
               }
            }
            if (c.name) {
               const commName = c.name.trim().toLowerCase();
               const cName = this.getContactFullName(contact).toLowerCase();
               const cEmail = (contact.email || '').toLowerCase();
               return cName === commName || cEmail === commName;
            }
            return false;
         });
      },
      populateCommModalFromContact(contact, pref) {
         this.commModalContact = contact;
         this.editingCommId = pref ? pref.id : null;

         let matchedUser = null;
         if (contact.auth_user_id && this.orgUsers) {
            matchedUser = this.orgUsers.find(u => Number(u.id) === Number(contact.auth_user_id));
         }
         if (!matchedUser && this.orgUsers && contact.email) {
            const cEmail = contact.email.toLowerCase();
            matchedUser = this.orgUsers.find(u => u.email && u.email.toLowerCase() === cEmail);
         }
         this.selectedUsers = matchedUser ? [matchedUser] : [];

         this.commForm = {
            name: this.getContactFullName(contact) || contact.email || '',
            clientPace: pref ? this.normalizePace(pref.client_pace || '') : '',
            costSensitivity: pref ? this.normalizeCost(pref.cost_sensitivity || '') : '',
            technicality: pref ? this.normalizeTech(pref.technicality || '') : '',
            decisionStyle: pref ? (pref.decision_style || '') : '',
            communicationStyle: pref ? (pref.communication_style || '') : ''
         };
      },
      openContactCommDetailsModal(contact) {
         const pref = this.getContactCommPref(contact);
         if (!pref) {
            this.openContactCommModalForContact(contact);
            return;
         }
         this.populateCommModalFromContact(contact, pref);
         this.commModalMode = 'view';
         this.showCommModal = true;
      },
      openContactCommModalForContact(contact) {
         const pref = this.getContactCommPref(contact);
         this.populateCommModalFromContact(contact, pref);
         this.commModalMode = 'form';
         this.showCommModal = true;
      },
      switchCommModalToEdit() {
         this.commModalMode = 'form';
      },
      async deleteCommPrefFromModal() {
         if (!this.editingCommId) return;
         await this.deleteCommPref({ id: this.editingCommId });
         this.closeCommModal();
      },
       async saveCommPrefs() {
          let isLinked = this.profileData.hasAuthOrganisation == 1;
          let users = [];

          if (this.commModalContact) {
             let matchedAuthUserId = null;
             if (this.commModalContact.auth_user_id) {
                matchedAuthUserId = Number(this.commModalContact.auth_user_id);
             } else if (this.selectedUsers.length > 0 && this.selectedUsers[0].id) {
                matchedAuthUserId = Number(this.selectedUsers[0].id);
             }
             isLinked = !!(this.profileData.hasAuthOrganisation == 1 && matchedAuthUserId);
             const contactName = this.getContactFullName(this.commModalContact) || this.commModalContact.email || this.commForm.name || 'Contact';
             users = [{ id: matchedAuthUserId || 0, name: contactName }];
          } else if (isLinked) {
             if (this.selectedUsers.length === 0) return;
             users = this.selectedUsers.map(u => ({ id: u.id, name: u.name }));
          } else {
             if (!this.commForm.name || !this.commForm.name.trim()) return;
             users = [{ id: 0, name: this.commForm.name.trim() }];
          }

          const payload = {
             id: this.editingCommId || 0,
             is_linked: isLinked,
             users: users,
             client_pace: this.commForm.clientPace,
             cost_sensitivity: this.commForm.costSensitivity,
             technicality: this.commForm.technicality,
             decision_style: this.commForm.decisionStyle,
             communication_style: this.commForm.communicationStyle
          };

          this.isLoading = true;
          try {
             const orgUuid = this.profileData.uuid || this.profileData.id;
             const response = await this.$axios.post(`/organisations/${orgUuid}/client-communications`, payload);
             if (response.data.success) {
                this.$toast.success(response.data.message);
                this.cancelEditComm();
                this.showCommModal = false;
                await this.fetchClientCommunications(orgUuid);
                await this.fetchCombinedTimeline(orgUuid);
             } else {
                this.$toast.error(response.data.message);
             }
          } catch (err) {
             console.error('Error saving comm preferences:', err);
             const msg = err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Error saving preferences';
             this.$toast.error(msg);
          } finally {
             this.isLoading = false;
          }
       },
       async deleteCommPref(comm) {
          if (!confirm('Are you sure you want to delete this communication preference?')) return;
          
          this.isLoading = true;
          try {
             const orgUuid = this.profileData.uuid || this.profileData.id;
             const response = await this.$axios.delete(`/organisations/${orgUuid}/client-communications/${comm.id}`);
             if (response.data.success) {
                this.$toast.success(response.data.message);
                await this.fetchClientCommunications(orgUuid);
                await this.fetchCombinedTimeline(orgUuid);
                if (this.editingCommId === comm.id) {
                   this.cancelEditComm();
                }
             } else {
                this.$toast.error(response.data.message);
             }
          } catch (err) {
             console.error('Error deleting comm preference:', err);
             const msg = err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Error deleting preference';
             this.$toast.error(msg);
          } finally {
             this.isLoading = false;
          }
       },
      isDirty(fieldKey) {
         if (!this.initialProfileData || !this.profileData) return false;
         if (Array.isArray(this.profileData[fieldKey])) {
            const a = this.profileData[fieldKey] || [];
            const b = this.initialProfileData[fieldKey] || [];
            if (a.length !== b.length) return true;
            return [...a].sort().join(',') !== [...b].sort().join(',');
         }
         return this.profileData[fieldKey] !== this.initialProfileData[fieldKey];
      },
      async saveField(fieldName, fieldKey) {
         const value = this.profileData[fieldKey];

         if (fieldKey === 'code_of_conduct_accepted_date' && value && moment(value, 'YYYY-MM-DD').isAfter(moment(), 'day')) {
            this.$toast.error('Supplier Code of Conduct acceptance date cannot be in the future.');
            this.profileData.code_of_conduct_accepted_date = (this.initialProfileData && this.initialProfileData.code_of_conduct_accepted_date) || '';
            throw new Error('Supplier Code of Conduct acceptance date cannot be in the future.');
         }
         
         const classes = this.profileData.classifications || [];
         const classificationsPayload = classes.map(c => ({ classification: c }));

         // 1. If it is the leadName field, we update the Lead record
         if (fieldKey === 'leadName') {
            if (!this.profileData.leadUuid) {
               this.$toast.error('No contact lead associated with this organisation to update.');
               return;
            }
            const parts = value.trim().split(/\s+/);
            const firstName = parts[0] || 'Admin';
            const lastName = parts.slice(1).join(' ') || 'User';

            const leadPayload = {
               id: this.profileData.leadUuid,
               uuid: this.profileData.leadUuid,
               lead_type_id: this.profileData.leadTypeId,
               lead_status_id: this.profileData.leadStatusId || '801ee182-87c7-42d0-ad92-b87a9078a838',
               lead_priority_id: this.profileData.leadPriorityId || 'b73d7ad2-b0a5-49d3-ba51-e7ff0006133e',
               organisation_name: this.profileData.name,
               abn: this.profileData.abn,
               first_name: firstName,
               last_name: lastName,
               email: this.profileData.org_lead_email || this.profileData.email,
               phone_number: this.profileData.phone,
               is_charity: this.profileData.isCharity,
               country_code: this.profileData.country || 'Australia',
               classification: classes.join(', '),
               is_test_data: this.profileData.isTestData
            };

            try {
               await this.$axios.put(`/leads/${this.profileData.leadUuid}`, leadPayload);
               this.profileData.firstName = firstName;
               this.profileData.lastName = lastName;
               this.initialProfileData[fieldKey] = value;
               this.$toast.success(`${fieldName} saved successfully!`);
               await this.fetchCombinedTimeline(this.profileData.uuid);
            } catch (err) {
               console.error('Error saving lead name field:', err);
               this.$toast.error(`Failed to save ${fieldName}.`);
            }
            return;
         }

         // 2. For all other fields, we update the Organisation record
         const orgUuid = this.profileData.uuid || this.profileData.id || this.profileData.organisationUuid || this.selectedLeadId;
         const orgPayload = {
            id: orgUuid,
            uuid: orgUuid,
            name: this.profileData.name,
            abn: this.profileData.abn,
            email: this.profileData.email,
            org_lead_email: this.profileData.org_lead_email,
            phone_number: this.profileData.phone,
            address: this.profileData.address,
            address2: this.profileData.address2,
            city: this.profileData.city,
            state: this.profileData.state,
            country: this.profileData.country,
            postcode: this.profileData.postcode,

            billing_address: this.profileData.billing_address,
            billing_address2: this.profileData.billing_address2,
            billing_city: this.profileData.billing_city,
            billing_state: this.profileData.billing_state,
            billing_country: this.profileData.billing_country,
            billing_postcode: this.profileData.billing_postcode,
            billing_email: this.profileData.billing_email,

            delivery_address: this.profileData.delivery_address,
            delivery_address2: this.profileData.delivery_address2,
            delivery_city: this.profileData.delivery_city,
            delivery_state: this.profileData.delivery_state,
            delivery_country: this.profileData.delivery_country,
            delivery_postcode: this.profileData.delivery_postcode,

            service_address: this.profileData.service_address,
            service_address2: this.profileData.service_address2,
            service_city: this.profileData.service_city,
            service_state: this.profileData.service_state,
            service_country: this.profileData.service_country,
            service_postcode: this.profileData.service_postcode,
            service_email: this.profileData.service_email,

            background: this.profileData.background,
            is_charity: this.profileData.isCharity,
            is_test_data: this.profileData.isTestData,
            currency: this.profileData.currency,
            assigned_user_id: this.profileData.assigned_user_id ? parseInt(this.profileData.assigned_user_id) : 0,
            vertex_org_manager_id: this.profileData.vertex_org_manager_id ? parseInt(this.profileData.vertex_org_manager_id) : 0,
            is_msp_managed: this.profileData.isMspManaged,
            msp_uuid: this.profileData.mspUuid,
            is_partner_referred: this.profileData.isPartnerReferred,
            partner_uuid: this.profileData.partnerUuid,
            is_ecosystem: this.profileData.isEcosystem,
            ecosystem_uuid: this.profileData.ecosystemUuid,
            classifications: classificationsPayload,
            diversity_form_completed: this.profileData.diversity_form_completed,
            code_of_conduct_accepted_date: this.profileData.code_of_conduct_accepted_date && moment(this.profileData.code_of_conduct_accepted_date).isValid()
               ? moment(this.profileData.code_of_conduct_accepted_date).toISOString()
               : null,
            security_risk_rating: this.profileData.security_risk_rating,
            esg_practices_rating: this.profileData.esg_practices_rating,
            is_independent_private_business: this.profileData.is_independent_private_business,
            formal_written_policies: this.profileData.formal_written_policies || [],
            positive_practices: this.profileData.positive_practices || [],
            certifications: this.profileData.certifications || [],
            certifications_other: this.profileData.certifications_other || '',
            organisation_type: this.profileData.organisation_type,
            purpose_driven_type: this.profileData.purpose_driven_type,
            underserved_beneficiaries: this.profileData.underserved_beneficiaries,
            underserved_types: this.profileData.underserved_types || [],
            organisational_purpose: this.profileData.organisational_purpose || [],
            population_served: this.profileData.population_served || [],
            is_pbi: this.profileData.is_pbi,
            is_hpc: this.profileData.is_hpc,
            reporting_current: this.profileData.reporting_current,
            acnc_town_city: this.profileData.acnc_town_city || '',
            acnc_state: this.profileData.acnc_state || '',
            acnc_postcode: this.profileData.acnc_postcode || '',
            is_local_supplier: this.profileData.is_local_supplier,
            is_women_owned: this.profileData.is_women_owned,
            is_minority_owned: this.profileData.is_minority_owned,
            is_indigenous_owned: this.profileData.is_indigenous_owned,
            industry_uuid: this.profileData.industry_uuid,
            industry: this.profileData.industry,
            is_disclosure_industry: this.profileData.is_disclosure_industry,
            technology_stack: this.profileData.technology_stack || []
         };

         try {
            await this.$axios.put(`/organisations/${orgUuid}`, orgPayload);
            if (Array.isArray(value)) {
               this.initialProfileData[fieldKey] = [...value];
            } else {
               this.initialProfileData[fieldKey] = value;
            }
            if (fieldKey === 'postcode') {
               const activePostcode = this.profileData.purpose_driven_type === 'Registered Charity'
                  ? (this.profileData.acnc_postcode || this.profileData.postcode)
                  : this.profileData.postcode;
               this.updateLocalSupplierFromPostcode(activePostcode);
            }
            this.$toast.success(`${fieldName} saved successfully!`);
            await this.fetchCombinedTimeline(orgUuid);
         } catch (err) {
            console.error(`Error saving organisation field ${fieldName}:`, err, err?.response?.data);
            const msg = err?.response?.data?.message || `Failed to save ${fieldName}.`;
            this.$toast.error(msg);
            throw err;
         }
      },
      openCompanyBackgroundModal() {
         this.showCompanyBackgroundModal = true;
         this.$nextTick(() => {
            if (this.$refs.companyBackgroundEditor) {
               this.$refs.companyBackgroundEditor.innerHTML = this.formatBackgroundToHtml(this.profileData.background || '');
               this.$refs.companyBackgroundEditor.focus();
            }
         });
      },
      closeCompanyBackgroundModal() {
         this.showCompanyBackgroundModal = false;
      },
      saveCompanyBackgroundModal() {
         const editor = this.$refs.companyBackgroundEditor;
         const cleaned = editor ? this.sanitizeBackgroundHtml(editor.innerHTML) : '';
         this.profileData.background = cleaned;
         this.closeCompanyBackgroundModal();
         this.saveField('Company Background', 'background');
      },
      formatBackgroundToHtml(text) {
         if (!text) return '';
         if (/<[a-z][\s\S]*>/i.test(text)) {
            return this.sanitizeBackgroundHtml(text);
         }
         const escaped = String(text)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
         return escaped.replace(/\r?\n/g, '<br>');
      },
      sanitizeBackgroundHtml(rawHtml) {
         if (!rawHtml || typeof rawHtml !== 'string') return '';

         const parser = new DOMParser();
         const doc = parser.parseFromString(rawHtml, 'text/html');

         const forbiddenElements = doc.querySelectorAll('script, style, iframe, object, embed, meta, link');
         forbiddenElements.forEach(el => el.remove());

         const forbiddenTags = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'META', 'LINK']);
         const allowedTags = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'BR', 'P', 'DIV', 'UL', 'OL', 'LI']);
         const headingTags = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6']);

         const cleanNode = (node) => {
            if (node.nodeType === Node.TEXT_NODE) {
               return;
            }
            if (node.nodeType !== Node.ELEMENT_NODE) {
               if (node.parentNode) node.parentNode.removeChild(node);
               return;
            }

            const tag = node.tagName.toUpperCase();
            if (forbiddenTags.has(tag)) {
               if (node.parentNode) node.parentNode.removeChild(node);
               return;
            }

            let isBold = false;
            let isItalic = false;
            let isUnderline = false;

            if (node.style) {
               const fw = node.style.fontWeight;
               if ((fw === 'bold' || fw === 'bolder' || parseInt(fw, 10) >= 600) && tag !== 'B' && tag !== 'STRONG') {
                  isBold = true;
               }
               if ((node.style.fontStyle === 'italic' || node.style.fontStyle === 'oblique') && tag !== 'I' && tag !== 'EM') {
                  isItalic = true;
               }
               const td = node.style.textDecoration || node.style.textDecorationLine || '';
               if (td.includes('underline') && tag !== 'U') {
                  isUnderline = true;
               }
            }

            const children = Array.from(node.childNodes);
            children.forEach(child => cleanNode(child));

            if (isBold || isItalic || isUnderline) {
               const frag = doc.createDocumentFragment();
               while (node.firstChild) {
                  frag.appendChild(node.firstChild);
               }
               let wrapper = frag;
               if (isBold) {
                  const strong = doc.createElement('strong');
                  strong.appendChild(wrapper);
                  wrapper = strong;
               }
               if (isItalic) {
                  const em = doc.createElement('em');
                  em.appendChild(wrapper);
                  wrapper = em;
               }
               if (isUnderline) {
                  const u = doc.createElement('u');
                  u.appendChild(wrapper);
                  wrapper = u;
               }
               node.appendChild(wrapper);
            }

            if (headingTags.has(tag)) {
               const p = doc.createElement('p');
               const strong = doc.createElement('strong');
               while (node.firstChild) {
                  strong.appendChild(node.firstChild);
               }
               p.appendChild(strong);
               if (node.parentNode) {
                  node.parentNode.replaceChild(p, node);
               }
               return;
            }

            if (allowedTags.has(tag)) {
               while (node.attributes.length > 0) {
                  node.removeAttribute(node.attributes[0].name);
               }
               return;
            }

            const parent = node.parentNode;
            if (parent) {
               while (node.firstChild) {
                  parent.insertBefore(node.firstChild, node);
               }
               parent.removeChild(node);
            }
         };

         const bodyChildren = Array.from(doc.body.childNodes);
         bodyChildren.forEach(child => cleanNode(child));

         const textContent = (doc.body.textContent || '').replace(/\u00a0/g, ' ').trim();
         if (!textContent) {
            return '';
         }

         return doc.body.innerHTML.trim();
      },
      execBackgroundCommand(command) {
         if (this.$refs.companyBackgroundEditor) {
            this.$refs.companyBackgroundEditor.focus();
         }
         document.execCommand(command, false, null);
      },
      onCompanyBackgroundPaste(e) {
         e.preventDefault();
         const clipboard = e.clipboardData || window.clipboardData;
         const htmlData = clipboard ? clipboard.getData('text/html') : '';
         const textData = clipboard ? clipboard.getData('text/plain') : '';

         let cleanHtml = '';
         if (htmlData) {
            cleanHtml = this.sanitizeBackgroundHtml(htmlData);
         }

         if (!cleanHtml && textData) {
            const escapeHtml = (str) => {
               return str
                  .replace(/&/g, '&amp;')
                  .replace(/</g, '&lt;')
                  .replace(/>/g, '&gt;')
                  .replace(/"/g, '&quot;')
                  .replace(/'/g, '&#39;');
            };

            const lines = textData.split(/\r?\n/);
            const output = [];
            let inList = false;

            for (let i = 0; i < lines.length; i++) {
               const line = lines[i];
               const bulletMatch = line.trim().match(/^([•\-*]\s+)(.*)$/);
               if (bulletMatch) {
                  if (!inList) {
                     output.push('<ul>');
                     inList = true;
                  }
                  output.push(`<li>${escapeHtml(bulletMatch[2])}</li>`);
               } else {
                  if (inList) {
                     output.push('</ul>');
                     inList = false;
                  }
                  output.push(escapeHtml(line));
                  if (i < lines.length - 1) {
                     output.push('<br>');
                  }
               }
            }
            if (inList) {
               output.push('</ul>');
            }
            cleanHtml = output.join('');
         }

         if (cleanHtml) {
            document.execCommand('insertHTML', false, cleanHtml);
         }
      },
      async saveClassifications() {
         const classes = this.profileData.classifications || [];
         const classificationsPayload = classes.map(c => ({ classification: c }));

         const orgPayload = {
            id: this.profileData.uuid,
            uuid: this.profileData.uuid,
            name: this.profileData.name,
            abn: this.profileData.abn,
            email: this.profileData.email,
            org_lead_email: this.profileData.org_lead_email,
            phone_number: this.profileData.phone,
            address: this.profileData.address,
            address2: this.profileData.address2,
            city: this.profileData.city,
            state: this.profileData.state,
            country: this.profileData.country,
            postcode: this.profileData.postcode,

            billing_address: this.profileData.billing_address,
            billing_address2: this.profileData.billing_address2,
            billing_city: this.profileData.billing_city,
            billing_state: this.profileData.billing_state,
            billing_country: this.profileData.billing_country,
            billing_postcode: this.profileData.billing_postcode,
            billing_email: this.profileData.billing_email,

            delivery_address: this.profileData.delivery_address,
            delivery_address2: this.profileData.delivery_address2,
            delivery_city: this.profileData.delivery_city,
            delivery_state: this.profileData.delivery_state,
            delivery_country: this.profileData.delivery_country,
            delivery_postcode: this.profileData.delivery_postcode,

            service_address: this.profileData.service_address,
            service_address2: this.profileData.service_address2,
            service_city: this.profileData.service_city,
            service_state: this.profileData.service_state,
            service_country: this.profileData.service_country,
            service_postcode: this.profileData.service_postcode,
            service_email: this.profileData.service_email,

            background: this.profileData.background,
            is_charity: this.profileData.isCharity,
            is_test_data: this.profileData.isTestData,
            currency: this.profileData.currency,
            assigned_user_id: this.profileData.assigned_user_id ? parseInt(this.profileData.assigned_user_id) : 0,
            vertex_org_manager_id: this.profileData.vertex_org_manager_id ? parseInt(this.profileData.vertex_org_manager_id) : 0,
            is_msp_managed: this.profileData.isMspManaged,
            msp_uuid: this.profileData.mspUuid,
            is_partner_referred: this.profileData.isPartnerReferred,
            partner_uuid: this.profileData.partnerUuid,
            is_ecosystem: this.profileData.isEcosystem,
            ecosystem_uuid: this.profileData.ecosystemUuid,
            classifications: classificationsPayload,
            diversity_form_completed: this.profileData.diversity_form_completed,
            code_of_conduct_accepted_date: this.profileData.code_of_conduct_accepted_date && moment(this.profileData.code_of_conduct_accepted_date).isValid()
               ? moment(this.profileData.code_of_conduct_accepted_date).toISOString()
               : null,
            security_risk_rating: this.profileData.security_risk_rating,
            esg_practices_rating: this.profileData.esg_practices_rating,
            is_independent_private_business: this.profileData.is_independent_private_business,
            formal_written_policies: this.profileData.formal_written_policies || [],
            positive_practices: this.profileData.positive_practices || [],
            certifications: this.profileData.certifications || [],
            certifications_other: this.profileData.certifications_other || '',
            organisation_type: this.profileData.organisation_type,
            purpose_driven_type: this.profileData.purpose_driven_type,
            underserved_beneficiaries: this.profileData.underserved_beneficiaries,
            underserved_types: this.profileData.underserved_types || [],
            organisational_purpose: this.profileData.organisational_purpose || [],
            population_served: this.profileData.population_served || [],
            is_pbi: this.profileData.is_pbi,
            is_hpc: this.profileData.is_hpc,
            reporting_current: this.profileData.reporting_current,
            acnc_town_city: this.profileData.acnc_town_city || '',
            acnc_state: this.profileData.acnc_state || '',
            acnc_postcode: this.profileData.acnc_postcode || '',
            is_local_supplier: this.profileData.is_local_supplier,
            is_women_owned: this.profileData.is_women_owned,
            is_minority_owned: this.profileData.is_minority_owned,
            is_indigenous_owned: this.profileData.is_indigenous_owned,
            industry_uuid: this.profileData.industry_uuid,
            industry: this.profileData.industry,
            is_disclosure_industry: this.profileData.is_disclosure_industry,
            technology_stack: this.profileData.technology_stack || []
         };

         try {
            await this.$axios.put(`/organisations/${this.profileData.uuid}`, orgPayload);
            this.initialProfileData.classifications = [...classes];
            this.initialProfileData.isTestData = this.profileData.isTestData;
            const leadItem = this.leads.find(l => l.uuid === this.profileData.uuid);
            if (leadItem) {
               leadItem.classifications = [...classes];
            }
            this.$toast.success('Classifications saved successfully!');
            await this.fetchCombinedTimeline(this.profileData.uuid);
         } catch (err) {
            console.error('Error saving classifications:', err, err?.response?.data);
            const msg = err?.response?.data?.message || 'Failed to save Classifications.';
            this.$toast.error(msg);
         }
      },
      toggleFormalPolicy(policy) {
         if (!this.profileData.formal_written_policies) {
            this.profileData.formal_written_policies = [];
         }
         let current = [...this.profileData.formal_written_policies];
         if (policy === 'None of the above') {
            if (current.includes('None of the above')) {
               current = [];
            } else {
               current = ['None of the above'];
            }
         } else {
            current = current.filter(p => p !== 'None of the above');
            if (current.includes(policy)) {
               current = current.filter(p => p !== policy);
            } else {
               current.push(policy);
            }
         }
         this.profileData.formal_written_policies = current;
         this.debounceSave('Formal Written Policies', 'formal_written_policies');
      },
      togglePositivePractice(practiceKey) {
         if (!this.profileData.positive_practices) {
            this.profileData.positive_practices = [];
         }
         let current = [...this.profileData.positive_practices];
         if (practiceKey === 'None of the above') {
            if (current.includes('None of the above')) {
               current = [];
            } else {
               current = ['None of the above'];
            }
         } else {
            current = current.filter(p => p !== 'None of the above');
            if (current.includes(practiceKey)) {
               current = current.filter(p => p !== practiceKey);
            } else {
               current.push(practiceKey);
            }
         }
         this.profileData.positive_practices = current;
         this.debounceSave('Positive Practices', 'positive_practices');
      },
      toggleCertification(cert) {
         if (!this.profileData.certifications) {
            this.profileData.certifications = [];
         }
         let current = [...this.profileData.certifications];
         if (current.includes(cert)) {
            current = current.filter(c => c !== cert);
         } else {
            current.push(cert);
         }
         this.profileData.certifications = current;
         this.debounceSave('3rd Party Certifications', 'certifications');
      },
      toggleUnderservedType(type) {
         if (!this.profileData.underserved_types) {
            this.profileData.underserved_types = [];
         }
         let current = [...this.profileData.underserved_types];
         if (current.includes(type)) {
            current = current.filter(t => t !== type);
         } else {
            current.push(type);
         }
         this.profileData.underserved_types = current;
         this.debounceSave('Underserved Types', 'underserved_types');
      },
      addPurposeTag(newTag) {
         if (!this.profileData.organisational_purpose) {
            this.profileData.organisational_purpose = [];
         }
         if (!this.profileData.organisational_purpose.includes(newTag)) {
            this.profileData.organisational_purpose.push(newTag);
            this.debounceSave('Organisational Purpose', 'organisational_purpose');
         }
      },
      addPopulationTag(newTag) {
         if (!this.profileData.population_served) {
            this.profileData.population_served = [];
         }
         if (!this.profileData.population_served.includes(newTag)) {
            this.profileData.population_served.push(newTag);
            this.debounceSave('Population Served', 'population_served');
         }
      },
      onIndustrySelect(industry) {
         const indName = (typeof industry === 'object' && industry !== null) ? (industry.display_name || industry.name) : industry;
         this.profileData.industry = indName;
         let matchedObj = null;
         if (typeof industry === 'object' && industry !== null && industry.uuid) {
            this.profileData.industry_uuid = industry.uuid;
            matchedObj = industry;
         } else {
            matchedObj = (this.industryOptions || []).find(o => typeof o === 'object' && (o.name === indName || o.display_name === indName));
            if (matchedObj && matchedObj.uuid) {
               this.profileData.industry_uuid = matchedObj.uuid;
            }
         }
         let isDisclosure = false;
         if (matchedObj && typeof matchedObj.is_disclosure_industry !== 'undefined') {
            isDisclosure = Boolean(matchedObj.is_disclosure_industry);
         } else {
            isDisclosure = this.checkIsDisclosureIndustry(indName);
         }
         this.profileData.is_disclosure_industry = isDisclosure;
         this.debounceSave('Industry', 'industry');
         this.debounceSave('Disclosure Industry', 'is_disclosure_industry');
      },
      checkIsDisclosureIndustry(industryName) {
         if (!industryName) return false;
         const cleanName = industryName.toLowerCase().trim();
         const disclosureList = (this.disclosureIndustryOptions && this.disclosureIndustryOptions.length > 0)
            ? this.disclosureIndustryOptions.map(d => (typeof d === 'object' ? (d.display_name || d.name || '') : d).toLowerCase().trim())
            : [
               'alcohol', 'tobacco', 'gambling', 'firearms / weapons', 'firearms', 'weapons',
               'pornography', 'payday, short term or high interest lending', 'payday lending',
               'fossil fuels', 'mining', 'nuclear power or hazardous materials', 'nuclear power',
               'prisons', 'whole life insurance', 'debt collection services', 'debt collection',
               'volunteer placement to orphanages', 'tax advisory services', 'animal products or services',
               'genetically modified organisms', 'illegal products or subject to phase out',
               'industries at risk of human rights violations'
            ];
         return disclosureList.some(d => d === cleanName || cleanName.includes(d) || d.includes(cleanName));
      },
      onPurposeDrivenTypeChange(val) {
         if (val !== undefined && typeof val === 'string') {
            this.profileData.purpose_driven_type = val;
         }
         this.debounceSave('Purpose Driven Type', 'purpose_driven_type');
         if (this.profileData.purpose_driven_type === 'Registered Charity') {
            const activePostcode = this.profileData.acnc_postcode || this.profileData.postcode;
            this.updateLocalSupplierFromPostcode(activePostcode);
            if (this.profileData.abn) {
               this.validateAcnc();
            }
         } else {
            this.updateLocalSupplierFromPostcode(this.profileData.postcode);
         }
      },
      updateLocalSupplierFromPostcode(postcode) {
         const isLocal = this.isPostcodeWithin80km(postcode);
         this.profileData.is_local_supplier = isLocal;
         this.debounceSave('Local Supplier Proximity', 'is_local_supplier');
      },
      isPostcodeWithin80km(postcode) {
         if (!postcode) return false;
         const num = parseInt(postcode, 10);
         if (isNaN(num)) return false;
         if (num === 2065) return true;
         // Greater Sydney Metro & Northern beaches
         if (num >= 2000 && num <= 2234) return true;
         // Central Coast (Gosford / Wyong)
         if (num >= 2250 && num <= 2263) return true;
         // Wollongong / Northern Illawarra
         if (num >= 2500 && num <= 2519) return true;
         // Campbelltown / Macarthur
         if (num >= 2555 && num <= 2570) return true;
         // Penrith / St Marys
         if (num >= 2745 && num <= 2753) return true;
         // Lower Blue Mountains (Springwood, Katoomba)
         if (num >= 2773 && num <= 2780) return true;
         // Hawkesbury / Richmond / Windsor
         if (num >= 2756 && num <= 2758) return true;
         return false;
      },
      async validateAcnc(options = { silent: false }) {
         const isSilent = options && options.silent === true;
         const abn = (this.profileData.abn || '').replace(/\s+/g, '');
         if (!abn) {
            if (!isSilent) {
               this.$toast.warning('Please enter an ABN first before validating with ACNC.');
            }
            return;
         }
         this.isCheckingAcnc = true;
         this.acncValidationMessage = '';
         try {
            const res = await this.$axios.get(`/organisations/validate-acnc?abn=${abn}`);
            if (res.data && res.data.acnc) {
               const record = res.data.acnc;
               this.profileData.reporting_current = record.reporting_current;
               this.profileData.is_pbi = record.is_pbi;
               this.profileData.is_hpc = record.is_hpc;
               this.profileData.acnc_town_city = record.town_city || '';
               this.profileData.acnc_state = record.state || '';
               this.profileData.acnc_postcode = record.postcode || '';
               this.profileData.is_local_supplier = typeof record.is_local_supplier !== 'undefined'
                  ? record.is_local_supplier
                  : this.isPostcodeWithin80km(record.postcode);
               if (record.organisational_purposes && record.organisational_purposes.length > 0) {
                  this.profileData.organisational_purpose = record.organisational_purposes;
               }
               if (record.population_served && record.population_served.length > 0) {
                  this.profileData.population_served = record.population_served;
               }
               this.acncValidationStatus = 'success';
               this.acncValidationMessage = `Found active ACNC Charity: ${record.charity_legal_name} (${record.town_city}, ${record.state} ${record.postcode})`;
               if (!isSilent) {
                  this.$toast.success('ACNC record validated.');
               }
               this.debounceSave('Charity ACNC Sync', 'purpose_driven_type');
            }
         } catch (err) {
            this.acncValidationStatus = 'error';
            this.acncValidationMessage = (err.response && err.response.data && err.response.data.message) || 'No ACNC Charity record found for this ABN.';
            this.profileData.reporting_current = false;
            if (!isSilent) {
               this.$toast.error(this.acncValidationMessage);
            }
         } finally {
            this.isCheckingAcnc = false;
         }
      },
      formatDate(date) {
         if (!date) return '-';
         return moment(date).format('D-MMM-YYYY');
      },
      formatSyncedDate(date) {
         if (!date) return '';
         return moment(date).format('D-MMM-YYYY h:mm A');
      },
      onCodeOfConductDateChange() {
         const val = this.profileData.code_of_conduct_accepted_date;
         if (val && moment(val, 'YYYY-MM-DD').isAfter(moment(), 'day')) {
            this.$toast.error('Supplier Code of Conduct acceptance date cannot be in the future.');
            if (this.debounceTimeouts.code_of_conduct_accepted_date) {
               clearTimeout(this.debounceTimeouts.code_of_conduct_accepted_date);
               delete this.debounceTimeouts.code_of_conduct_accepted_date;
            }
            this.fieldStates.code_of_conduct_accepted_date = 'idle';
            this.profileData.code_of_conduct_accepted_date = (this.initialProfileData && this.initialProfileData.code_of_conduct_accepted_date) || '';
            return;
         }
         this.debounceSave('Supplier Code of Conduct Acceptance Date', 'code_of_conduct_accepted_date');
      },
      debounceSave(fieldName, fieldKey) {
         const currentUuid = this.profileData.uuid || this.profileData.id || this.profileData.organisationUuid || this.selectedLeadId;
         if (!currentUuid) return;

         this.fieldStates[fieldKey] = 'waiting';

         if (this.debounceTimeouts[fieldKey]) {
            clearTimeout(this.debounceTimeouts[fieldKey]);
         }

         this.debounceTimeouts[fieldKey] = setTimeout(async () => {
            const activeUuid = this.profileData.uuid || this.profileData.id || this.profileData.organisationUuid || this.selectedLeadId;
            if (activeUuid === currentUuid) {
               this.fieldStates[fieldKey] = 'saving';
               try {
                  await this.saveField(fieldName, fieldKey);
                  this.fieldStates[fieldKey] = 'saved';
                  setTimeout(() => {
                     if (this.fieldStates[fieldKey] === 'saved') {
                        this.fieldStates[fieldKey] = 'idle';
                     }
                  }, 2000);
               } catch (err) {
                  this.fieldStates[fieldKey] = 'idle';
               }
            } else {
               this.fieldStates[fieldKey] = 'idle';
            }
            delete this.debounceTimeouts[fieldKey];
         }, 1200);
      },
      togglePastClientClassification(event) {
         if (event && event.target && event.target.checked) {
            this.primaryClassification = 'Past Client';
         } else {
            this.primaryClassification = 'Client';
         }
         this.saveClassificationsImmediate();
      },
      async saveClassificationsImmediate() {
         this.fieldStates.classifications = 'saving';
         try {
            await this.saveClassifications();
            this.fieldStates.classifications = 'saved';
            setTimeout(() => {
               if (this.fieldStates.classifications === 'saved') {
                  this.fieldStates.classifications = 'idle';
               }
            }, 2000);
         } catch (err) {
            this.fieldStates.classifications = 'idle';
         }
      },
      async fetchMergedOrganisations(orgId) {
         const id = orgId || (this.profileData && (this.profileData.id || this.profileData.uuid));
         if (!id) return;
         this.isMergedOrgsLoading = true;
         try {
            const response = await this.$axios.get(`/organisations/${id}/merged-organisations`);
            if (response.data && response.data.merged_organisations) {
               this.mergedOrganisations = response.data.merged_organisations;
            } else {
               this.mergedOrganisations = [];
            }
         } catch (err) {
            console.error('Failed to fetch merged organisations:', err);
            this.mergedOrganisations = [];
         } finally {
            this.isMergedOrgsLoading = false;
         }
      },
      async openMergeModal() {
         this.selectedMergeTarget = null;
         this.mergeReason = '';
         this.mergeConfirmationChecked = false;
         this.showMergeModal = true;
         this.isSearchingMergeTargets = true;
         const currentUuid = this.profileData.uuid || this.profileData.id;
         const isSourceAuthSynced = this.profileData.has_auth_organisation == 1 || this.profileData.is_auth_synced || (this.profileData.auth_organisation_id && this.profileData.auth_organisation_id > 0);

         const filterEligibleTargets = (list) => {
            return list.filter(item => {
               const itemUuid = item.uuid || item.id;
               if (itemUuid === currentUuid || item.is_merged) return false;
               if (isSourceAuthSynced) {
                  const isItemAuth = item.has_auth_organisation == 1 || item.is_auth_synced || (item.auth_organisation_id && item.auth_organisation_id > 0);
                  if (!isItemAuth) return false;
               }
               return true;
            }).map(item => ({
               id: item.uuid || item.id,
               uuid: item.uuid || item.id,
               name: item.name || item.organisation_name || 'Unnamed Organisation',
               abn: item.abn || '',
               email: item.email || '',
               has_auth_organisation: item.has_auth_organisation,
               is_auth_synced: item.is_auth_synced,
               auth_organisation_id: item.auth_organisation_id
            }));
         };

         if (this.leads && this.leads.length > 0) {
            this.mergeSearchResults = filterEligibleTargets(this.leads);
         }

         try {
            const response = await this.$axios.get('/organisations?pageSize=10000&page=1&excludeTestData=false');
            if (response.data && response.data.organisations) {
               this.mergeSearchResults = filterEligibleTargets(response.data.organisations);
            }
         } catch (err) {
            console.error('Failed to load merge targets:', err);
         } finally {
            this.isSearchingMergeTargets = false;
         }
      },
      closeMergeModal() {
         this.showMergeModal = false;
         this.selectedMergeTarget = null;
         this.mergeSearchResults = [];
      },
      async confirmMerge() {
         if (!this.selectedMergeTarget || !this.mergeConfirmationChecked) return;
         const sourceUuid = this.profileData.uuid || this.profileData.id;
         const targetUuid = this.selectedMergeTarget.uuid || this.selectedMergeTarget.id;

         const isSourceAuthSynced = this.profileData.has_auth_organisation == 1 || this.profileData.is_auth_synced || (this.profileData.auth_organisation_id && this.profileData.auth_organisation_id > 0);
         const isTargetAuthSynced = this.selectedMergeTarget.has_auth_organisation == 1 || this.selectedMergeTarget.is_auth_synced || (this.selectedMergeTarget.auth_organisation_id && this.selectedMergeTarget.auth_organisation_id > 0);

         if (isSourceAuthSynced && !isTargetAuthSynced) {
            this.$toast.error('An Auth-synced organisation cannot be merged into an unlinked organisation. Destination must also be Auth-synced.');
            return;
         }

         this.isMerging = true;
         try {
            const response = await this.$axios.post(`/organisations/${sourceUuid}/merge-to`, {
               target_organisation_uuid: targetUuid,
               reason: this.mergeReason
            });
            if (response.data && (response.data.success || response.data.status === 'success')) {
               this.$toast.success(response.data.message || 'Organisation merged successfully');
               this.closeMergeModal();
               // Refresh list and switch view to the destination organisation
               await this.fetchLeads();
               this.selectedLeadId = targetUuid;
            } else {
               this.$toast.error((response.data && response.data.message) || 'Failed to merge organisation');
            }
         } catch (err) {
            console.error('Error merging organisation:', err);
            const msg = err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Error merging organisation';
            this.$toast.error(msg);
         } finally {
            this.isMerging = false;
         }
      },
      openUnmergeModal(org) {
         this.unmergeTargetOrg = org || this.profileData;
         this.showUnmergeModal = true;
      },
      closeUnmergeModal() {
         this.showUnmergeModal = false;
         this.unmergeTargetOrg = null;
      },
      async confirmUnmerge() {
         if (!this.unmergeTargetOrg) return;
         const targetOrgUuid = this.unmergeTargetOrg.uuid || this.unmergeTargetOrg.id;
         this.isUnmerging = true;
         try {
            const response = await this.$axios.post(`/organisations/${targetOrgUuid}/unmerge`);
            if (response.data && (response.data.success || response.data.status === 'success')) {
               this.$toast.success(response.data.message || 'Organisation unmerged successfully');
               this.closeUnmergeModal();
               // Refresh the current organisation view
               const currentUuid = this.profileData.uuid || this.profileData.id;
               await this.fetchOrganisationDetails(currentUuid);
               await this.fetchLeads();
            } else {
               this.$toast.error((response.data && response.data.message) || 'Failed to unmerge organisation');
            }
         } catch (err) {
            console.error('Error unmerging organisation:', err);
            const msg = err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Error unmerging organisation';
            this.$toast.error(msg);
         } finally {
            this.isUnmerging = false;
         }
      }
   }
};
</script>

<style scoped>
.action-button-container {
   display: inline-flex;
   align-items: center;
   justify-content: flex-end;
   gap: 8px;
   flex-wrap: nowrap;
}

.btn-svg-icon {
   position: relative;
   width: 36px;
   height: 36px;
   border-radius: 50% !important;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   background-color: #e6f7fa;
   color: #00A0C1;
   border: none;
   padding: 0;
   cursor: pointer;
   transition: transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease, color 0.15s ease;
}

.icon-svg {
   width: 20px;
   height: 20px;
   transition: filter 0.15s ease;
}

.icon-svg-fa {
   font-size: 15px;
   color: inherit;
   transition: color 0.15s ease;
}

.btn-svg-icon:hover:not(:disabled) {
   background-color: #00A0C1;
   color: #fff;
   transform: translateY(-1px);
   box-shadow: 0 4px 12px rgba(0, 160, 193, 0.25);
}

.btn-svg-icon:active:not(:disabled) {
   transform: translateY(0);
}

.btn-svg-icon:hover:not(:disabled) .icon-svg {
   filter: brightness(0) invert(1);
}

.btn-svg-icon:hover:not(:disabled) .icon-svg-fa {
   color: #fff !important;
}

.client-dashboard-page {
   padding: 10px 0;
}

.breadcrumb-link {
   transition: color 0.2s ease;
}
.breadcrumb-link:hover {
   color: #00A0C1 !important;
}

.back-to-all-btn {
   transition: all 0.2s ease;
}
.back-to-all-btn:hover {
   background-color: #00A0C1 !important;
   color: #fff !important;
   border-color: #00A0C1 !important;
   box-shadow: 0 4px 12px rgba(0, 160, 193, 0.25);
}

/* Page Header */
.ms-page-header {
   margin-bottom: 24px;
   padding: 20px 24px;
   background: linear-gradient(135deg, #ffffff 0%, #f4f9fb 100%);
   border: 1px solid rgba(0, 130, 152, 0.15);
   border-left: 5px solid #008298;
   border-radius: 12px;
   box-shadow: 0 4px 18px rgba(0, 78, 90, 0.05);
}

.ms-page-header__content {
   display: flex;
   align-items: center;
   gap: 16px;
}

.ms-page-header__icon {
   width: 44px;
   height: 44px;
   border-radius: 10px;
   background: rgba(0, 130, 152, 0.08);
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 1.15rem;
   color: #008298;
}

.ms-page-header__title {
   font-size: 1.45rem;
   font-weight: 800;
   color: #00363f;
   margin: 0;
   letter-spacing: -0.3px;
}

.org-switcher-box,
.org-switcher-section {
   display: flex;
   flex-direction: column;
   align-items: flex-start;
}

.org-switcher-title,
.switcher-label {
   font-size: 0.72rem;
   color: #557d84;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   margin-bottom: 4px;
}

.header-search-container {
   position: relative;
   width: 320px;
   margin: 8px 0;
}

.header-search-input-wrapper {
   position: relative;
   display: flex;
   align-items: center;
}

.header-search-input-wrapper .search-icon {
   position: absolute;
   left: 12px;
   color: #94a3b8;
   font-size: 0.9rem;
   pointer-events: none;
}

.header-search-input {
   width: 100%;
   padding: 8px 36px 8px 36px;
   font-size: 0.85rem;
   border: 1.5px solid #cbd5e1;
   border-radius: 8px;
   color: #334155;
   background-color: #fff;
   transition: all 0.2s ease;
   height: 38px;
   outline: none;
}

.header-search-input:focus {
   border-color: #00A0C1;
   box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}

.clear-search-btn {
   position: absolute;
   right: 12px;
   background: none;
   border: none;
   color: #94a3b8;
   cursor: pointer;
   padding: 4px;
   display: flex;
   align-items: center;
   justify-content: center;
   border-radius: 50%;
   transition: all 0.2s ease;
}

.clear-search-btn:hover {
   color: #64748b;
   background-color: #f1f5f9;
}

/* Dropdown overlay */
.header-search-dropdown {
   position: absolute;
   top: 100%;
   left: 0;
   right: 0;
   background-color: #fff;
   border: 1.5px solid #e2e8f0;
   border-radius: 8px;
   box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
   z-index: 1000;
   max-height: 250px;
   overflow-y: auto;
   margin-top: 6px;
}

.header-search-dropdown-item {
   padding: 10px 14px;
   cursor: pointer;
   transition: background-color 0.15s ease;
}

.header-search-dropdown-item:hover {
   background-color: #f1f5f9;
}

.org-name-text {
   font-weight: 600;
   color: #1e293b;
   font-size: 0.85rem;
}

.org-type-badge {
   font-size: 0.7rem;
   font-weight: 700;
   padding: 3px 8px;
   border-radius: 6px;
   background-color: #f8fafc;
   color: #64748b;
   border: 1px solid #e2e8f0;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   display: inline-flex;
   align-items: center;
   white-space: nowrap;
}

.org-type-badge--lead {
   background-color: #dcfce7;
   color: #15803d;
   border-color: #bbf7d0;
}

.org-type-badge--client,
.org-type-badge--organisation {
   background-color: #ffedd5;
   color: #c2410c;
   border-color: #fed7aa;
}

.org-type-badge--past-client {
   background-color: #f1f5f9;
   color: #475569;
   border-color: #cbd5e1;
}

.org-pill-badge {
   font-size: 0.72rem;
   font-weight: 600;
   padding: 3px 10px;
   border-radius: 999px;
   border: 1px solid transparent;
}

.org-pill-badge--client {
   background-color: #ffedd5;
   color: #c2410c;
   border-color: #fed7aa;
}

.org-pill-badge--past-client {
   background-color: #f1f5f9;
   color: #475569;
   border-color: #cbd5e1;
}

.org-pill-badge--prospect {
   background-color: #fef3c7;
   color: #b45309;
   border-color: #fde68a;
}

.org-pill-badge--lead {
   background-color: #dcfce7;
   color: #15803d;
   border-color: #bbf7d0;
}

.org-pill-badge--partner {
   background-color: #ede9fe;
   color: #6d28d9;
   border-color: #ddd6fe;
}

.org-pill-badge--supplier {
   background-color: #e0f2fe;
   color: #0369a1;
   border-color: #bae6fd;
}

.org-pill-badge--test {
   background-color: #fee2e2;
   color: #b91c1c;
   border-color: #fecaca;
}

.org-pill-badge--synced-deleted {
   background-color: #fee2e2;
   color: #dc2626;
   border: 1px solid #fecaca;
}

.org-pill-badge--synced-deleted i {
   color: #dc2626;
}

.org-pill-badge--synced-inactive {
   background-color: #fffbeb;
   color: #f59e0b;
   border: 1px solid #fde68a;
}

.org-pill-badge--synced-inactive i {
   color: #f59e0b;
}

.org-type-badge--prospect {
   background-color: #fef3c7;
   color: #b45309;
   border-color: #fde68a;
}

.org-type-badge--partner {
   background-color: #ede9fe;
   color: #6d28d9;
   border-color: #ddd6fe;
}

.org-type-badge--supplier {
   background-color: #e0f2fe;
   color: #0369a1;
   border-color: #bae6fd;
}

.org-type-badge--merged {
   background-color: #fffbeb;
   color: #92400e;
   border-color: #fde68a;
}

.org-type-badge--deactivated {
   background-color: #fee2e2;
   color: #dc2626;
   border-color: #fecaca;
}

.org-switcher-multiselect {
   min-width: 260px;
   max-width: 380px;
   cursor: pointer;
}

/* VueMultiselect styles moved to unscoped block at bottom of file */

/* Organisation Header Card */
.org-header-card {
   background: #ffffff;
   border-radius: 12px !important;
   border: 1px solid #e2e8f0 !important;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05) !important;
}

.org-avatar-box {
   width: 48px;
   height: 48px;
   border-radius: 10px;
   background: #e0f2fe;
   color: #0284c7;
   font-size: 1.4rem;
   flex-shrink: 0;
}

.org-header-title {
   font-size: 1.35rem;
   font-weight: 700;
   color: #0f172a;
   letter-spacing: -0.01em;
}

.org-header-subtitle {
   color: #64748b;
   font-size: 0.82rem;
   font-weight: 500;
}

.org-switcher-box {
   display: flex;
   flex-direction: column;
   align-items: flex-end;
}

.org-switcher-title {
   font-size: 0.72rem;
   font-weight: 600;
   color: #64748b;
   margin-bottom: 4px;
}

/* Cards (Matching All Organisations) */
.ms-card {
   background: #fff;
   border-radius: 12px;
   border: 1px solid #e2e8f0;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
   overflow: hidden;
}

/* Premium Tab Navigation (Matching All Organisations) */
.org-tabs-container {
   background-color: #f8fafc;
   border-bottom: 1.5px solid #e2e8f0;
   padding: 0 24px;
   display: flex;
   justify-content: space-between;
   align-items: center;
   flex-wrap: wrap;
   gap: 16px;
}

.org-tabs-nav {
   display: flex;
   gap: 4px;
   flex-wrap: wrap;
}

.org-tab-btn {
   background: none;
   border: none;
   padding: 16px 20px;
   font-weight: 600;
   color: #64748b;
   cursor: pointer;
   position: relative;
   transition: all 0.2s ease;
   font-size: 0.9rem;
   outline: none;
   display: inline-flex;
   align-items: center;
   text-decoration: none;
   user-select: none;
}

.org-tab-btn:hover {
   color: #00768a;
}

.org-tab-btn.active {
   color: #00768a;
   font-weight: 700;
}

.org-tab-btn.active::after {
   content: '';
   position: absolute;
   bottom: 0;
   left: 0;
   right: 0;
   height: 3px;
   background-color: #00768a;
   border-radius: 3px 3px 0 0;
}

.org-tab-counter-badge {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   min-width: 20px;
   height: 20px;
   padding: 0 6px;
   border-radius: 10px;
   font-size: 0.72rem;
   font-weight: 700;
   line-height: 1;
   margin-left: 6px;
   background-color: #e2e8f0;
   color: #475569;
   transition: all 0.2s ease;
}

.org-tab-btn.active .org-tab-counter-badge {
   background-color: #00768a;
   color: #ffffff;
}

/* Accordion Section Cards */
.accordion-card {
   background: #ffffff;
   border-radius: 12px !important;
   border: 1px solid #e2e8f0 !important;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
   overflow: hidden;
}

.accordion-header {
   cursor: pointer;
   user-select: none;
   background: #ffffff;
   transition: background-color 0.15s ease;
}

.accordion-header:hover {
   background-color: #fafbfd;
}

.accordion-title {
   font-size: 1rem;
   font-weight: 700;
   color: #1e293b;
}

.btn-accordion-chevron {
   width: 28px;
   height: 28px;
   border-radius: 6px;
   border: 1px solid #e2e8f0;
   background: #ffffff;
   color: #64748b;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 0.75rem;
   transition: all 0.2s ease;
}

.btn-accordion-chevron:hover {
   background-color: #f1f5f9;
   color: #1e293b;
}


/* Platform Action Icons in Accordion Header */
.btn-accordion-icon {
   width: 30px;
   height: 30px;
   border-radius: 6px;
   border: 1px solid #e2e8f0;
   background: #ffffff;
   color: #94a3b8;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 0.8rem;
   transition: all 0.2s ease;
   cursor: pointer;
}

.btn-accordion-icon.is-linked {
   color: #00A0C1;
   border-color: #cbd5e1;
}

.btn-accordion-icon.btn-acc-xero.is-linked {
   color: #16a34a;
}

.btn-accordion-icon.btn-acc-sync.is-linked {
   color: #0d9488;
}

.btn-accordion-icon:hover:not(:disabled) {
   transform: translateY(-1px);
   box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.btn-accordion-icon.btn-acc-auth.is-linked:hover {
   background-color: #00A0C1;
   color: #ffffff;
   border-color: #00A0C1;
}

.btn-accordion-icon.btn-acc-xero.is-linked:hover {
   background-color: #16a34a;
   color: #ffffff;
   border-color: #16a34a;
}

.btn-accordion-icon.btn-acc-sync.is-linked:hover:not(:disabled) {
   background-color: #0d9488;
   color: #ffffff;
   border-color: #0d9488;
}

.btn-accordion-icon:disabled {
   opacity: 0.45;
   cursor: not-allowed;
   pointer-events: auto;
}

.btn-accordion-icon.is-syncing {
   cursor: wait;
   color: #0d9488;
   background-color: #f0fdfa;
   border-color: #99f6e4;
}

/* Relationship Header Badges */
.rel-header-badge {
   padding: 4px 10px;
   border-radius: 6px;
   font-size: 0.72rem;
   font-weight: 600;
   display: inline-flex;
   align-items: center;
}

.rel-badge-msp {
   background-color: #eff6ff;
   color: #1d4ed8;
   border: 1px solid #bfdbfe;
}

.rel-badge-partner {
   background-color: #f5f3ff;
   color: #6d28d9;
   border: 1px solid #ddd6fe;
}

.rel-badge-ecosystem {
   background-color: #f0fdfa;
   color: #0f766e;
   border: 1px solid #99f6e4;
}

/* Forms */
.form-section-title {
   font-size: 1.1rem;
   font-weight: 700;
   color: #334155;
   margin-bottom: 20px;
   padding-bottom: 8px;
   border-bottom: 1px dashed #e2e8f0;
}

.form-label {
   font-weight: 600;
   color: #475569;
   font-size: 0.85rem;
   margin-bottom: 6px;
}

.form-control, .form-select {
   border-radius: 10px;
   border: 1.5px solid #cbd5e1;
   padding: 10px 14px;
   font-size: 0.9rem;
   color: #334155;
   transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus, .form-select:focus {
   border-color: #00A0C1;
   box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}

.relation-list {
   background: #f8fafc;
   border: 1.5px solid #e2e8f0;
   border-radius: 10px;
   padding: 10px;
   display: flex;
   flex-direction: column;
   gap: 6px;
}

.relation-item {
   padding: 8px 12px;
   background: #fff;
   border-radius: 8px;
   border: 1px solid #e2e8f0;
   font-size: 0.85rem;
   font-weight: 500;
}

.partner-tag {
   display: inline-flex;
   align-items: center;
   background-color: #f1f5f9;
   color: #334155;
   padding: 6px 12px;
   border-radius: 8px;
   font-weight: 500;
   font-size: 0.85rem;
   border: 1px solid #cbd5e1;
}

.partner-tag i:hover {
   color: #dc2626;
}

.btn-save {
   border-radius: 10px !important;
   height: 40px;
}


/* Vertical Timeline */
.vertical-timeline {
   position: relative;
   padding-left: 30px;
}

.vertical-timeline::before {
   content: '';
   position: absolute;
   top: 0;
   bottom: 0;
   left: 9px;
   width: 2px;
   background-color: #e2e8f0;
}

.timeline-event {
   position: relative;
   margin-bottom: 24px;
}

.timeline-event:last-child {
   margin-bottom: 0;
}

.timeline-icon {
   position: absolute;
   left: -33px;
   width: 26px;
   height: 26px;
   background-color: #fff;
   border: 2px solid #cbd5e1;
   border-radius: 50%;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 0.75rem;
   z-index: 1;
}

.timeline-body {
   background: #f8fafc;
   border: 1px solid #e2e8f0;
   border-radius: 10px;
   padding: 14px 16px;
}

.event-title {
   font-size: 0.9rem;
}

.event-description {
   font-size: 0.85rem;
}

/* Individual Save Buttons - Contextual Overlay Design */
.field-save-wrapper {
   position: relative;
   display: flex;
   align-items: center;
   width: 100%;
}

.field-save-wrapper .form-control {
   padding-right: 48px !important;
}

.field-save-wrapper .multiselect {
   width: 100%;
}

.field-save-wrapper .multiselect .multiselect__tags {
   padding-right: 48px !important;
}

.field-save-wrapper .multiselect ~ .field-status-indicator {
   right: 36px;
}

/* Field Status Indicators */
.field-status-indicator {
   position: absolute;
   right: 12px;
   display: flex;
   align-items: center;
   justify-content: center;
   height: 100%;
   z-index: 5;
   pointer-events: none;
}

.field-status-indicator--textarea {
   bottom: 22px;
   right: 12px;
   height: auto;
}

.field-status-indicator-static {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   height: 28px;
   width: 28px;
}

.status-text {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 0.95rem;
   animation: fadeIn 0.3s ease;
}

.status-text.waiting {
   font-size: 0.85rem;
}

.status-text.saving {
   font-size: 0.85rem;
}

.status-text.saved {
   font-size: 1.05rem;
}

/* ── Linked status badges ── */
.lead-linked-badge {
   display: inline-flex;
   align-items: center;
   gap: 5px;
   padding: 4px 10px;
   background: #ecfdf5;
   color: #059669;
   border-radius: 16px;
   font-size: 0.78rem;
   font-weight: 600;
   white-space: nowrap;
}

.lead-linked-badge i {
   font-size: 0.7rem;
}

.lead-unlinked-badge {
   display: inline-flex;
   align-items: center;
   gap: 5px;
   padding: 4px 10px;
   background: #fef2f2;
   color: #dc2626;
   border-radius: 16px;
   font-size: 0.78rem;
   font-weight: 600;
   white-space: nowrap;
}

.lead-unlinked-badge i {
   font-size: 0.7rem;
}

/* Modal Overlay & Card custom styles for link modal */
.lead-modal-overlay {
   position: fixed;
   top: 0;
   left: 0;
   right: 0;
   bottom: 0;
   background-color: rgba(15, 23, 42, 0.4);
   display: flex;
   align-items: center;
   justify-content: center;
   z-index: 1050;
   backdrop-filter: blur(4px);
}

.lead-modal {
   width: 90%;
   max-width: 500px;
   background: #fff;
   border-radius: 16px;
   box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
   overflow: hidden;
}

.lead-modal__header {
   padding: 16px 20px;
   display: flex;
   justify-content: space-between;
   align-items: center;
   background: #ffffff;
}

.lead-modal__title {
   margin: 0;
   font-size: 1.1rem;
   font-weight: 600;
   color: #1e293b;
   display: flex;
   align-items: center;
   gap: 8px;
}

.lead-modal__close {
   background: transparent;
   border: none;
   font-size: 1.5rem;
   color: #64748b;
   cursor: pointer;
   line-height: 1;
   padding: 0;
   transition: color 0.15s ease;
}

.lead-modal__close:hover {
   color: #ef4444;
}

.lead-modal__divider {
   height: 1px;
   background: #e2e8f0;
}

.lead-modal__body {
   padding: 20px;
}

.lead-form-label {
   display: block;
   font-weight: 500;
   font-size: 0.875rem;
   color: #475569;
   margin-bottom: 8px;
}

.lead-modal__actions {
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: 24px;
}

/* Premium Styled Checkbox Custom CSS */
.checkbox-label {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  font-weight: 500;
  color: #333 !important;
  user-select: none;
}

.checkbox-label.align-items-start {
  align-items: flex-start !important;
}

.styled-checkbox {
  display: none;
}

.checkbox-custom {
  width: 18px;
  height: 18px;
  border: 2px solid #00bcd4;
  border-radius: 4px;
  margin-right: 10px;
  display: inline-block;
  position: relative;
  flex-shrink: 0;
  transition: background-color 0.2s, border-color 0.2s, box-shadow 0.2s;
}

.checkbox-label.align-items-start .checkbox-custom {
  margin-top: 2px;
}

.checkbox-label:hover .checkbox-custom {
  border-color: #0097a7;
}

.checkbox-custom::after {
  content: '';
  position: absolute;
  display: none;
  left: 5px;
  top: 1.5px;
  width: 5px;
  height: 9.5px;
  border: solid white;
  border-width: 0 2.5px 2.5px 0;
  transform: rotate(45deg);
}

.styled-checkbox:checked+.checkbox-custom {
  background-color: #00bcd4;
  border-color: #00bcd4;
}

.styled-checkbox:checked+.checkbox-custom::after {
  display: block;
}

.checkbox-text {
  font-size: 0.875rem;
  line-height: 1.4;
  color: #333;
}

/* Premium Custom Toggle Switches */
.switch-toggle {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

.switch-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.switch-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .3s;
  border-radius: 24px;
}

.switch-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .3s;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}

input:checked + .switch-slider {
  background-color: #00A0C1;
}

input:checked + .switch-slider:before {
  transform: translateX(20px);
}

.form-switch .form-check-input:checked,
.form-check-input:checked {
  background-color: #00A0C1 !important;
  border-color: #00A0C1 !important;
}

.form-switch .form-check-input:focus,
.form-check-input:focus {
  border-color: #00A0C1 !important;
  box-shadow: 0 0 0 0.2rem rgba(0, 160, 193, 0.25) !important;
}

/* Custom Styles for Client Communication Cards and Badges */
.comm-preference-card {
   background-color: #ffffff;
   border: 1px solid #e2e8f0 !important;
   border-radius: 12px;
   box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03) !important;
   transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.comm-preference-card:hover {
   transform: translateY(-4px);
   box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04) !important;
}
.comm-badge {
   padding: 6px 12px;
   border-radius: 20px;
   font-size: 0.72rem;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   gap: 6px;
   box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
   transition: all 0.2s ease;
   cursor: help;
}
.comm-badge:hover {
   transform: translateY(-1px);
   box-shadow: 0 3px 6px rgba(0, 0, 0, 0.08);
}
/* Uniform Intensity Badges for Client Communication Module */
.comm-badge--intensity-low {
   background-color: #f0fdf4 !important;
   color: #166534 !important;
   border: 1px solid #bbf7d0 !important;
}
.comm-badge--intensity-medium {
   background-color: #eff6ff !important;
   color: #1e40af !important;
   border: 1px solid #bfdbfe !important;
}
.comm-badge--intensity-high {
   background-color: #fffbeb !important;
   color: #9a3412 !important;
   border: 1px solid #fde68a !important;
}
.comm-badge--intensity-critical {
   background-color: #fef2f2 !important;
   color: #991b1b !important;
   border: 1px solid #fecaca !important;
}
.btn-icon-only {
   width: 32px;
   height: 32px;
   border-radius: 50% !important;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   padding: 0 !important;
   transition: all 0.2s ease;
}
.comm-card-actions .btn-outline-secondary {
   border-color: #cbd5e1;
   color: #64748b;
}
.comm-card-actions .btn-outline-secondary:hover {
   background-color: #f1f5f9;
   color: #334155;
   border-color: #94a3b8;
}
.comm-card-actions .btn-outline-danger {
   border-color: #fca5a5;
   color: #ef4444;
}
.comm-card-actions .btn-outline-danger:hover {
   background-color: #fef2f2;
   color: #dc2626;
   border-color: #f87171;
}

/* Custom BDM-style Modal Styles */
.modal-backdrop-custom {
   position: fixed;
   top: 0;
   left: 0;
   width: 100vw;
   height: 100vh;
   background-color: rgba(15, 23, 42, 0.4);
   display: flex;
   justify-content: center;
   align-items: center;
   z-index: 1050;
   backdrop-filter: blur(4px);
}
.modal-content-custom {
   position: relative;
   background-color: white;
   padding: 2.5rem 2rem 2rem 2rem;
   border-radius: 16px;
   width: 100%;
   box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}
.modal-title-custom {
   font-weight: bold;
   font-size: 1.25rem;
   text-align: left;
   color: #435666;
   margin-bottom: 0;
}
.modal-content-custom hr {
   margin-top: 1rem;
   margin-bottom: 1.5rem;
   border: 0;
   border-top: 1px solid #e2e8f0;
   opacity: 1;
}
.close-button-custom {
   position: absolute;
   top: 1rem;
   right: 1rem;
   font-size: 2rem;
   font-weight: bold;
   line-height: 1;
   color: #888;
   background: none;
   border: none;
   cursor: pointer;
   transition: color 0.2s ease;
}
.close-button-custom:hover {
   color: #000;
}
.add-button-custom {
   font-size: 1rem;
   font-weight: bold;
   border-radius: 8px;
}

/* Vertex Leads-style Action Buttons & Circular Buttons */
.vertex-action-btn {
   width: 36px;
   height: 36px;
   min-width: 36px;
   min-height: 36px;
   border-radius: 50% !important;
   font-size: 0.88rem;
   background-color: #e6f7fa;
   color: #00A0C1;
   border: none;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   cursor: pointer;
   transition: all 0.2s ease-in-out;
   box-shadow: 0 1px 3px rgba(0, 160, 193, 0.12);
}
.vertex-action-btn:hover:not(:disabled) {
   background-color: #00A0C1;
   color: #ffffff;
   transform: translateY(-1px);
   box-shadow: 0 4px 12px rgba(0, 160, 193, 0.25);
}
.vertex-action-btn:disabled {
   background-color: #f1f5f9;
   color: #94a3b8;
   cursor: not-allowed;
   box-shadow: none;
}

.contact-comm-pill-strip {
   display: flex;
   flex-wrap: wrap;
   align-items: center;
   gap: 10px !important;
   margin-top: 14px;
   margin-bottom: 16px;
   padding: 6px 8px;
   border-radius: 8px;
   cursor: pointer;
   transition: background-color 0.2s ease, transform 0.15s ease;
}
.contact-comm-pill-strip:hover {
   background-color: #f0fdfa;
}
.comm-badge--saved {
   background-color: #f0fdfa !important;
   color: #00A0C1 !important;
   border: 1px solid rgba(0, 160, 193, 0.3) !important;
}
.comm-badge--sm {
   font-size: 0.72rem !important;
   padding: 5px 12px !important;
   border-radius: 999px !important;
   font-weight: 700 !important;
   letter-spacing: 0.03em;
   display: inline-flex !important;
   align-items: center;
   gap: 6px !important;
   line-height: 1.2;
   transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.comm-modal-content {
   max-width: 860px !important;
   width: 92vw !important;
   padding: 28px 34px !important;
   border-radius: 16px !important;
}
.comm-modal-content .modal-header-custom {
   padding-bottom: 18px !important;
   margin-bottom: 20px !important;
   border-bottom: 1px solid #f1f5f9;
}
.comm-modal-content .modal-body-custom {
   max-height: none !important;
   overflow-y: visible !important;
   padding: 4px 2px !important;
}
.comm-modal-content .modal-footer-custom {
   padding-top: 20px !important;
   margin-top: 22px !important;
   border-top: 1px solid #f1f5f9;
   gap: 10px !important;
}

.action-btn-circle {
   position: relative;
   width: 34px;
   height: 34px;
   min-width: 34px;
   min-height: 34px;
   padding: 0;
   border-radius: 50% !important;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 0.85rem;
   border: none;
   cursor: pointer;
   transition: all 0.2s ease-in-out;
   text-decoration: none !important;
}
.action-btn-dot {
   position: absolute;
   top: -1px;
   right: -1px;
   width: 9px;
   height: 9px;
   border-radius: 50%;
   background-color: #10b981;
   border: 2px solid #ffffff;
}
.action-btn-circle--vertex {
   background-color: #e6f7fa;
   color: #00A0C1;
}
.action-btn-circle--vertex:hover:not(:disabled) {
   background-color: #00A0C1;
   color: #ffffff;
   transform: translateY(-1px);
   box-shadow: 0 4px 12px rgba(0, 160, 193, 0.25);
}
.action-btn-circle--vertex:disabled {
   opacity: 0.5;
   cursor: not-allowed;
   transform: none;
   box-shadow: none;
}
.action-btn-circle--chevron {
   background-color: #f8fafc;
   border: 1px solid #e2e8f0;
   color: #64748b;
}
.action-btn-circle--chevron:hover:not(:disabled),
.action-btn-circle--chevron.is-expanded {
   background-color: #e6f7fa;
   color: #00A0C1;
   border-color: #00A0C1;
   transform: translateY(-1px);
}
.action-btn-circle--delete {
   background-color: #fef2f2;
   color: #ef4444;
}
.action-btn-circle--delete:hover:not(:disabled) {
   background-color: #ef4444;
   color: #ffffff;
   transform: translateY(-1px);
   box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
}
.action-btn-circle--close {
   background-color: #f1f5f9;
   color: #64748b;
}
.action-btn-circle--close:hover:not(:disabled) {
   background-color: #fee2e2;
   color: #ef4444;
   transform: translateY(-1px);
}

.lead-btn {
   border-radius: 8px;
   padding: 8px 18px;
   font-size: 0.85rem;
   font-weight: 600;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   gap: 6px;
   cursor: pointer;
   transition: all 0.2s ease;
}
.lead-btn--primary {
   background: linear-gradient(135deg, #00A0C1, #33b5d1);
   background-color: #00A0C1;
   color: #ffffff;
   border: none;
   box-shadow: 0 2px 6px rgba(0, 160, 193, 0.2);
}
.lead-btn--primary:hover:not(:disabled) {
   background: linear-gradient(135deg, #008da8, #00A0C1);
   color: #ffffff;
   box-shadow: 0 4px 12px rgba(0, 160, 193, 0.3);
   transform: translateY(-1px);
}
.lead-btn--primary:disabled {
   opacity: 0.6;
   cursor: not-allowed;
}
.lead-btn--ghost {
   background: #ffffff;
   color: #64748b;
   border: 1px solid #cbd5e1;
   padding: 8px 16px;
}
.lead-btn--ghost:hover:not(:disabled) {
   background: #f8fafc;
   color: #334155;
   border-color: #94a3b8;
}

/* Section 7 - Technology Stack & Scanner Styles */
.tech-scanner-card {
   background: #ffffff;
   border: 1px solid #e2e8f0;
   transition: box-shadow 0.2s ease;
}
.tech-scanner-icon {
   width: 36px;
   height: 36px;
   border-radius: 8px;
   background: rgba(0, 160, 193, 0.12);
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 1rem;
   color: #00A0C1;
}
.tech-scan-results-container {
   border: 1px solid #e2e8f0;
   background-color: #f8fafc;
}
.tech-scan-results-container table th {
   font-size: 0.75rem;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   color: #64748b;
   padding: 8px 10px;
}
.tech-scan-results-container table td {
   padding: 8px 10px;
   font-size: 0.85rem;
   vertical-align: middle;
}
.tech-active-section {
   background-color: #f8fafc;
   border: 1px solid #e2e8f0;
}
.tech-active-pill {
   background: #ffffff;
   border: 1px solid #cbd5e1;
   font-size: 0.82rem;
   transition: all 0.15s ease;
   box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.tech-active-pill:hover {
   border-color: #94a3b8;
   box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}
.tech-active-pill .btn-close-xs {
   width: 0.5rem;
   height: 0.5rem;
   padding: 0.25rem;
   opacity: 0.6;
   cursor: pointer;
}
.tech-active-pill .btn-close-xs:hover {
   opacity: 1;
   color: #ef4444;
}
.tech-category-group .tech-grid {
   display: grid;
   grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
   gap: 10px;
}
.tech-tile {
   background: #ffffff;
   border: 1.5px solid #e2e8f0;
   cursor: pointer;
   user-select: none;
   transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.tech-tile:hover {
   border-color: #00A0C1;
   transform: translateY(-1px);
   box-shadow: 0 4px 6px -1px rgba(0, 160, 193, 0.1), 0 2px 4px -2px rgba(0, 160, 193, 0.06);
}
.tech-tile.is-active {
   border-color: #00A0C1;
   background: rgba(0, 160, 193, 0.06);
   box-shadow: 0 0 0 1px #00A0C1;
}
.tech-tile-icon {
   width: 28px;
   height: 28px;
   font-size: 1.15rem;
   flex-shrink: 0;
}
.tech-tile-label {
   min-width: 0;
}
.tech-tile-check {
   font-size: 0.9rem;
   flex-shrink: 0;
}
.vertex-tech-check-icon,
.tech-tile-check .fa-check-circle {
   color: #00A0C1 !important;
}
.vertex-tech-checkbox:checked {
   background-color: #00A0C1 !important;
   border-color: #00A0C1 !important;
}
.vertex-tech-checkbox:focus {
   border-color: #00A0C1 !important;
   box-shadow: 0 0 0 0.2rem rgba(0, 160, 193, 0.25) !important;
}
.vertex-tech-btn-primary {
   background-color: #00A0C1 !important;
   border-color: #00A0C1 !important;
   color: #ffffff !important;
}
.vertex-tech-btn-primary:hover:not(:disabled) {
   background-color: #008298 !important;
   border-color: #008298 !important;
   color: #ffffff !important;
}
.vertex-tech-btn-primary:disabled {
   opacity: 0.65;
   cursor: not-allowed;
}
.vertex-tech-btn-outline {
   background-color: transparent !important;
   border: 1.5px solid #00A0C1 !important;
   color: #00A0C1 !important;
}
.vertex-tech-btn-outline:hover:not(:disabled) {
   background-color: #e6f7fa !important;
   border-color: #008298 !important;
   color: #008298 !important;
}
.vertex-tech-btn-outline:disabled {
   opacity: 0.5;
   cursor: not-allowed;
}
.tech-scan-item-card-selected {
   background-color: rgba(0, 160, 193, 0.08) !important;
}
.tech-monogram-badge {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   width: 24px;
   height: 24px;
   border-radius: 6px;
   font-size: 0.7rem;
   font-weight: 800;
   letter-spacing: -0.3px;
   line-height: 1;
}
.tech-monogram-badge-sm {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   width: 20px;
   height: 20px;
   border-radius: 4px;
   font-size: 0.62rem;
   font-weight: 800;
   letter-spacing: -0.3px;
   line-height: 1;
}
.tech-monogram-badge-xs {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   width: 16px;
   height: 16px;
   border-radius: 3px;
   font-size: 0.55rem;
   font-weight: 800;
   letter-spacing: -0.2px;
   line-height: 1;
}
</style>

<style>
/* Unscoped overrides for Vertex Communication Preferences and Form VueMultiselect */
.vertex-comm-multiselect .multiselect__tags,
.field-save-wrapper .multiselect .multiselect__tags {
   background: #ffffff !important;
   border: 1.5px solid #cbd5e1 !important;
   border-radius: 10px !important;
   min-height: 38px !important;
   padding: 6px 36px 0 12px !important;
   transition: border-color 0.2s, box-shadow 0.2s;
}
.vertex-comm-multiselect.multiselect--active .multiselect__tags,
.field-save-wrapper .multiselect.multiselect--active .multiselect__tags {
   border-color: #00A0C1 !important;
   box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15) !important;
}
.vertex-comm-multiselect .multiselect__single,
.field-save-wrapper .multiselect .multiselect__single {
   background: transparent !important;
   color: #1e293b !important;
   font-size: 0.85rem !important;
   font-weight: 500 !important;
   margin-bottom: 0 !important;
   padding-left: 0 !important;
}
.vertex-comm-multiselect .multiselect__placeholder,
.field-save-wrapper .multiselect .multiselect__placeholder {
   color: #94a3b8 !important;
   font-size: 0.85rem !important;
   margin-bottom: 0 !important;
   padding-left: 0 !important;
}
.vertex-comm-multiselect .multiselect__select,
.field-save-wrapper .multiselect .multiselect__select {
   height: 36px !important;
   width: 34px !important;
}
.vertex-comm-multiselect .multiselect__select::before,
.field-save-wrapper .multiselect .multiselect__select::before {
   border-color: #00A0C1 transparent transparent !important;
}
.vertex-comm-multiselect .multiselect__content-wrapper,
.field-save-wrapper .multiselect .multiselect__content-wrapper {
   border-radius: 10px !important;
   border: 1px solid rgba(0, 160, 193, 0.25) !important;
   box-shadow: 0 8px 20px rgba(0, 78, 90, 0.12) !important;
   background: #ffffff !important;
   margin-top: 4px !important;
   overflow-x: hidden !important;
}
.vertex-comm-multiselect .multiselect__option,
.field-save-wrapper .multiselect .multiselect__option {
   background: #ffffff !important;
   color: #1e293b !important;
   font-size: 0.85rem !important;
   font-weight: 500 !important;
   padding: 10px 14px !important;
   min-height: auto !important;
}
.vertex-comm-multiselect .multiselect__option--highlight,
.field-save-wrapper .multiselect .multiselect__option--highlight {
   background: #00A0C1 !important;
   color: #ffffff !important;
}
.vertex-comm-multiselect .multiselect__option--selected,
.field-save-wrapper .multiselect .multiselect__option--selected {
   background: #e6f7fa !important;
   color: #008298 !important;
   font-weight: 700 !important;
}
.vertex-comm-multiselect .multiselect__option--selected.multiselect__option--highlight,
.field-save-wrapper .multiselect .multiselect__option--selected.multiselect__option--highlight {
   background: #00A0C1 !important;
   color: #ffffff !important;
}
.vertex-comm-multiselect .multiselect__tag,
.field-save-wrapper .multiselect .multiselect__tag {
   background: #e6f7fa !important;
   color: #008298 !important;
   font-weight: 600 !important;
   border-radius: 6px !important;
}
.vertex-comm-multiselect .multiselect__tag-icon::after,
.field-save-wrapper .multiselect .multiselect__tag-icon::after {
   color: #008298 !important;
}
.vertex-comm-multiselect .multiselect__tag-icon:hover,
.field-save-wrapper .multiselect .multiselect__tag-icon:hover {
   background: #00A0C1 !important;
}
.vertex-comm-multiselect .multiselect__tag-icon:hover::after,
.field-save-wrapper .multiselect .multiselect__tag-icon:hover::after {
   color: #ffffff !important;
}
.vertex-comm-multiselect.multiselect,
.field-save-wrapper .multiselect {
   background-color: transparent !important;
   background-image: none !important;
}

/* Unscoped overrides for Organisation Switcher VueMultiselect */
.org-switcher-multiselect .multiselect__tags {
   background: #ffffff !important;
   border: 1.5px solid rgba(0, 130, 152, 0.15) !important;
   border-radius: 10px !important;
   height: 38px !important;
   min-height: 38px !important;
   padding: 6px 40px 0 12px !important;
}

.org-switcher-multiselect .multiselect__single {
   background: transparent !important;
   color: #00363f !important;
   font-size: 0.9rem !important;
   font-weight: 600 !important;
   margin-bottom: 0 !important;
   padding-left: 0 !important;
}

.org-switcher-multiselect .multiselect__input {
   background: transparent !important;
   color: #00363f !important;
   font-size: 0.9rem !important;
   font-weight: 600 !important;
   margin-bottom: 0 !important;
   padding-left: 0 !important;
}

.org-switcher-multiselect .multiselect__input::placeholder {
   color: #94a3b8 !important;
}

.org-switcher-multiselect .multiselect__select {
   height: 36px !important;
   width: 38px !important;
}

.org-switcher-multiselect .multiselect__select::before {
   border-color: #008298 transparent transparent !important;
}

.org-switcher-multiselect .multiselect__placeholder {
   color: #94a3b8 !important;
   font-size: 0.9rem !important;
   font-weight: 600 !important;
   margin-bottom: 0 !important;
   padding-left: 0 !important;
}

.org-switcher-multiselect .multiselect__content-wrapper {
   border-radius: 10px !important;
   border: 1px solid rgba(0, 130, 152, 0.15) !important;
   box-shadow: 0 4px 12px rgba(0, 78, 90, 0.08) !important;
   background: #ffffff !important;
}

.org-switcher-multiselect .multiselect__option {
   background: #ffffff !important;
   color: #1e293b !important;
   font-weight: 500 !important;
}

.org-switcher-multiselect .multiselect__option--highlight {
   background: #008298 !important;
   color: white !important;
}

.org-switcher-multiselect .multiselect__option--selected {
   background: #e6f4f6 !important;
   color: #008298 !important;
   font-weight: 700 !important;
}

.org-switcher-multiselect.multiselect {
   background-color: transparent !important;
   background-image: none !important;
}

/* Merge Target VueMultiselect Styling */
.merge-target-multiselect .multiselect__option {
   background: #ffffff !important;
   color: #1e293b !important;
   padding: 8px 14px !important;
   min-height: auto !important;
}

.merge-target-multiselect .multiselect__option::after {
   display: none !important;
}

.merge-target-multiselect .merge-option-name {
   color: #1e293b !important;
   font-weight: 600;
   transition: color 0.15s ease;
}

.merge-target-multiselect .merge-option-sub {
   color: #64748b !important;
   transition: color 0.15s ease;
}

.merge-target-multiselect .merge-auth-badge {
   transition: all 0.15s ease;
}

/* Highlighted / Hovered / Keyboard Active Option */
.merge-target-multiselect .multiselect__option--highlight {
   background: #00A0C1 !important;
   color: #ffffff !important;
}

.merge-target-multiselect .multiselect__option--highlight::after {
   background: #00A0C1 !important;
   color: #ffffff !important;
}

.merge-target-multiselect .multiselect__option--highlight *,
.merge-target-multiselect .multiselect__option--highlight .merge-option-name {
   color: #ffffff !important;
}

.merge-target-multiselect .multiselect__option--highlight .merge-option-sub {
   color: rgba(255, 255, 255, 0.92) !important;
}

.merge-target-multiselect .multiselect__option--highlight .merge-auth-badge,
.merge-target-multiselect .multiselect__option--highlight .badge {
   background-color: rgba(255, 255, 255, 0.25) !important;
   color: #ffffff !important;
   border-color: rgba(255, 255, 255, 0.5) !important;
}

.merge-target-multiselect .multiselect__option--highlight .merge-auth-badge i,
.merge-target-multiselect .multiselect__option--highlight .badge i {
   color: #ffffff !important;
}

/* Selected Option (Normal state) */
.merge-target-multiselect .multiselect__option--selected {
   background: #e6f4f6 !important;
   color: #00768a !important;
   font-weight: 700 !important;
}

.merge-target-multiselect .multiselect__option--selected::after {
   background: #e6f4f6 !important;
   color: #00768a !important;
}

.merge-target-multiselect .multiselect__option--selected .merge-option-name {
   color: #00768a !important;
}

.merge-target-multiselect .multiselect__option--selected .merge-option-sub {
   color: #00768a !important;
}

.merge-target-multiselect .multiselect__option--selected .merge-auth-badge,
.merge-target-multiselect .multiselect__option--selected .badge {
   background-color: rgba(0, 118, 138, 0.12) !important;
   color: #00768a !important;
   border-color: rgba(0, 118, 138, 0.25) !important;
}

/* Selected AND Highlighted Option */
.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight {
   background: #008fae !important;
   color: #ffffff !important;
}

.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight::after {
   background: #008fae !important;
   color: #ffffff !important;
}

.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight *,
.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight .merge-option-name {
   color: #ffffff !important;
}

.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight .merge-option-sub {
   color: rgba(255, 255, 255, 0.92) !important;
}

.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight .merge-auth-badge,
.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight .badge {
   background-color: rgba(255, 255, 255, 0.25) !important;
   color: #ffffff !important;
   border-color: rgba(255, 255, 255, 0.5) !important;
}

.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight .merge-auth-badge i,
.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight .badge i {
   color: #ffffff !important;
}

/* Platform Action Buttons styling */
.platform-actions-toolbar {
   background: #f8fafc;
   border: 1px solid #e2e8f0;
   border-radius: 8px;
   padding: 3px;
   display: inline-flex;
   align-items: center;
}

.btn-platform-icon {
   width: 34px;
   height: 34px;
   padding: 0 !important;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   border-radius: 6px !important;
   font-size: 0.875rem;
   border: 1px solid transparent;
   background-color: transparent;
   color: #94a3b8;
   cursor: not-allowed;
   transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-platform-icon:disabled {
   opacity: 0.45;
   cursor: not-allowed;
}

/* Auth Action Icon Button */
.btn-platform-auth.is-linked {
   color: #00A0C1;
   background-color: #ffffff;
   border-color: #cbd5e1;
   cursor: pointer;
}
.btn-platform-auth.is-linked:hover {
   background-color: #00A0C1 !important;
   border-color: #00A0C1 !important;
   color: #ffffff !important;
   transform: translateY(-1px);
   box-shadow: 0 2px 5px rgba(0, 160, 193, 0.25);
}

/* Xero Action Icon Button */
.btn-platform-xero.is-linked {
   color: #16a34a;
   background-color: #ffffff;
   border-color: #cbd5e1;
   cursor: pointer;
}
.btn-platform-xero.is-linked:hover {
   background-color: #16a34a !important;
   border-color: #16a34a !important;
   color: #ffffff !important;
   transform: translateY(-1px);
   box-shadow: 0 2px 5px rgba(22, 163, 74, 0.25);
}

/* Sync Action Icon Button */
.btn-platform-sync.is-linked {
   color: #0d9488;
   background-color: #ffffff;
   border-color: #cbd5e1;
   cursor: pointer;
}
.btn-platform-sync.is-linked:hover:not(:disabled) {
   background-color: #0d9488 !important;
   border-color: #0d9488 !important;
   color: #ffffff !important;
   transform: translateY(-1px);
   box-shadow: 0 2px 5px rgba(13, 148, 136, 0.25);
}
.btn-platform-sync.is-syncing {
   color: #0d9488;
   background-color: #f0fdfa;
   border-color: #99f6e4;
   cursor: wait;
}

.lead-former-badge {
   display: inline-flex;
   align-items: center;
   gap: 5px;
   padding: 2px 8px;
   background: #fef3c7;
   color: #92400e;
   border: 1px solid #fde68a;
   border-radius: 12px;
   font-size: 0.72rem;
   font-weight: 600;
   white-space: nowrap;
}

.lead-former-badge i {
   font-size: 0.68rem;
   color: #b45309;
}

/* ========================================================
   Contacts Tab & Key Stakeholders Styling
   ======================================================== */


/* Contact Avatars */
.contact-mini-avatar {
   width: 32px;
   height: 32px;
   border-radius: 50%;
   background: #e2e8f0;
   color: #334155;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 11.5px;
   font-weight: 700;
   flex-shrink: 0;
   text-transform: uppercase;
}

.contact-card-avatar {
   width: 44px;
   height: 44px;
   border-radius: 50%;
   background: #e2e8f0;
   color: #334155;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 15px;
   font-weight: 700;
   flex-shrink: 0;
   text-transform: uppercase;
}

.avatar-primary {
   background: linear-gradient(135deg, #fef3c7, #fde68a);
   color: #92400e;
   box-shadow: 0 2px 4px rgba(245, 158, 11, 0.15);
}

.avatar-technical {
   background: linear-gradient(135deg, #e0f2fe, #bae6fd);
   color: #0369a1;
   box-shadow: 0 2px 4px rgba(2, 132, 199, 0.15);
}

.avatar-billing {
   background: linear-gradient(135deg, #dcfce7, #bbf7d0);
   color: #15803d;
   box-shadow: 0 2px 4px rgba(34, 197, 94, 0.15);
}

.avatar-signer {
   background: linear-gradient(135deg, #f3e8ff, #e9d5ff);
   color: #7e22ce;
   box-shadow: 0 2px 4px rgba(168, 85, 247, 0.15);
}

.avatar-user {
   background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
   color: #475569;
}

.avatar-lead {
   background: linear-gradient(135deg, #ffedd5, #fed7aa);
   color: #c2410c;
}

.avatar-default {
   background: #f1f5f9;
   color: #64748b;
}

/* Contact Badges (aligned with VertexProposal) */
.contact-badge-item {
   letter-spacing: 0.3px;
   box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.contact-badge-item + .contact-badge-item {
   margin-left: 6px !important;
}

.contact-type-badge-primary {
   background-color: #fef3c7 !important;
   color: #92400e !important;
   border: 1px solid #fde68a !important;
}

.contact-type-badge-technical {
   background-color: #e0f2fe !important;
   color: #0369a1 !important;
   border: 1px solid #bae6fd !important;
}

.contact-type-badge-billing {
   background-color: #dcfce7 !important;
   color: #15803d !important;
   border: 1px solid #bbf7d0 !important;
}

.contact-type-badge-signer {
   background-color: #f3e8ff !important;
   color: #7e22ce !important;
   border: 1px solid #e9d5ff !important;
}

.contact-type-badge-user {
   background-color: #f1f5f9 !important;
   color: #475569 !important;
   border: 1px solid #cbd5e1 !important;
}

.contact-type-badge-lead {
   background-color: #ffedd5 !important;
   color: #c2410c !important;
   border: 1px solid #fed7aa !important;
}

.contact-type-badge-default {
   background-color: #f1f5f9 !important;
   color: #64748b !important;
   border: 1px solid #e2e8f0 !important;
}

/* Cards & Mini-cards */
.contact-preview-mini-card {
   transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}
.contact-preview-mini-card:hover {
   transform: translateY(-2px);
   box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
   border-color: #cbd5e1 !important;
}

.contact-directory-card,
.contact-card {
   border-radius: 12px !important;
   border: 1px solid #e2e8f0 !important;
   background: #ffffff;
   box-shadow: 0 2px 6px rgba(15, 23, 42, 0.03) !important;
   transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}
.contact-directory-card:hover,
.contact-card:hover {
   transform: translateY(-2px);
   box-shadow: 0 6px 16px rgba(0, 160, 193, 0.08) !important;
   border-color: rgba(0, 160, 193, 0.45) !important;
}
.contact-directory-card .card-body {
   padding: 22px 24px !important;
}



/* Copy email button */
.copy-email-btn {
   opacity: 0.6;
   transition: opacity 0.15s ease, transform 0.15s ease;
}
.copy-email-btn:hover {
   opacity: 1;
   transform: scale(1.15);
}

/* Vertex Theme Buttons */
.btn-vertex-primary {
   background-color: #00A0C1 !important;
   border: 1px solid #00A0C1 !important;
   color: #ffffff !important;
   font-weight: 600 !important;
   border-radius: 6px !important;
   transition: all 0.2s ease-in-out !important;
}

.btn-vertex-primary:hover,
.btn-vertex-primary:focus {
   background-color: #008298 !important;
   border-color: #008298 !important;
   color: #ffffff !important;
   box-shadow: 0 2px 8px rgba(0, 160, 193, 0.35) !important;
   transform: translateY(-1px);
}

.btn-vertex-primary:disabled {
   background-color: #a0d8e6 !important;
   border-color: #a0d8e6 !important;
   color: #ffffff !important;
   opacity: 0.65 !important;
   transform: none !important;
   box-shadow: none !important;
   cursor: not-allowed !important;
}

.btn-vertex-outline {
   background-color: #ffffff !important;
   border: 1px solid #00A0C1 !important;
   color: #00A0C1 !important;
   font-weight: 600 !important;
   border-radius: 6px !important;
   transition: all 0.2s ease-in-out !important;
}

.btn-vertex-outline:hover,
.btn-vertex-outline:focus {
   background-color: #00A0C1 !important;
   border-color: #00A0C1 !important;
   color: #ffffff !important;
   box-shadow: 0 2px 6px rgba(0, 160, 193, 0.2) !important;
   transform: translateY(-1px);
}

.btn-vertex-outline:disabled {
   border-color: #cbd5e1 !important;
   color: #94a3b8 !important;
   opacity: 0.65 !important;
   transform: none !important;
   box-shadow: none !important;
   cursor: not-allowed !important;
}

.lead-modal .form-select:focus {
   border-color: #00A0C1 !important;
   box-shadow: 0 0 0 0.2rem rgba(0, 160, 193, 0.25) !important;
}

/* Vertex Checkbox Component */
.vertex-checkbox-card {
   background: #f8fafc;
   border: 1px solid #e2e8f0;
   cursor: pointer;
   transition: all 0.2s ease-in-out;
}

.vertex-checkbox-card:hover {
   background: #f1f5f9;
   border-color: #cbd5e1;
}

.vertex-checkbox-card--checked {
   background: rgba(0, 160, 193, 0.08) !important;
   border-color: rgba(0, 160, 193, 0.5) !important;
   box-shadow: 0 1px 3px rgba(0, 160, 193, 0.1);
}

.vertex-checkbox-input {
   width: 18px !important;
   height: 18px !important;
   margin-top: 0 !important;
   cursor: pointer !important;
   border: 1.5px solid #94a3b8 !important;
   border-radius: 4px !important;
   accent-color: #00A0C1 !important;
   transition: all 0.15s ease-in-out !important;
}

.vertex-checkbox-input:checked {
   background-color: #00A0C1 !important;
   border-color: #00A0C1 !important;
}

.vertex-checkbox-input:focus {
   border-color: #00A0C1 !important;
   box-shadow: 0 0 0 0.2rem rgba(0, 160, 193, 0.25) !important;
}

/* Contacts Filter Dropdown & Checkboxes (Matches Leads/Contracts Filter Style) */
.contacts-filter-dropdown-wrapper {
   position: relative;
   display: inline-block;
   flex-shrink: 0;
}

.custom-filter-dropdown-btn {
   background-color: #fff !important;
   border: 1px solid #dee2e6 !important;
   color: #495057 !important;
   transition: all 0.2s ease;
}

.custom-filter-dropdown-btn:hover,
.custom-filter-dropdown-btn:focus,
.custom-filter-dropdown-btn[aria-expanded="true"] {
   border-color: #00A0C1 !important;
   background-color: #f8f9fa !important;
   color: #00A0C1 !important;
}

.custom-filter-dropdown-btn--active {
   border-color: #00A0C1 !important;
   background-color: #fff !important;
   color: #00A0C1 !important;
}

/* Premium Custom Checkboxes */
.custom-checkbox-input {
   position: absolute;
   opacity: 0;
   width: 0;
   height: 0;
   margin: 0;
   pointer-events: none;
}

.custom-checkbox {
   width: 20px;
   height: 20px;
   border: 2px solid #ced4da;
   border-radius: 4px;
   cursor: pointer;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   background-color: #fff;
   transition: all 0.2s ease;
   position: relative;
   flex-shrink: 0;
}

.custom-checkbox-input:checked + .custom-checkbox {
   background-color: #00A0C1;
   border-color: #00A0C1;
}

.custom-checkbox-input:checked + .custom-checkbox::after {
   content: '';
   position: absolute;
   width: 5px;
   height: 10px;
   border: solid white;
   border-width: 0 2px 2px 0;
   transform: rotate(45deg);
   top: 2px;
   left: 6px;
}

.custom-checkbox:hover {
   border-color: #00A0C1;
}

/* Scrollbar for Filter Dropdown */
.contacts-filter-scroll-list::-webkit-scrollbar {
   width: 6px;
}

.contacts-filter-scroll-list::-webkit-scrollbar-track {
   background: #f1f5f9;
   border-radius: 4px;
}

.contacts-filter-scroll-list::-webkit-scrollbar-thumb {
   background: #00A0C1;
   border-radius: 4px;
}

.contacts-filter-scroll-list::-webkit-scrollbar-thumb:hover {
   background: #008299;
}

/* ═══════════════════════════════════════════════════════════
   ORGANISATION DASHBOARD — ACTIVE & STAGED JOBS STYLES
   ═══════════════════════════════════════════════════════════ */
.aj-board-header-icon {
   width: 38px;
   height: 38px;
   border-radius: 10px;
   background-color: #e6f7fa;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 1.1rem;
   flex-shrink: 0;
}

.org-jobs-btn-refresh {
   height: 38px;
   padding: 0 14px;
   background-color: #ffffff;
   border: 1px solid #e2e8f0;
   color: #475569;
   font-weight: 600;
   font-size: 0.85rem;
   border-radius: 8px !important;
   box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
   transition: all 0.15s ease;
}

.org-jobs-btn-refresh:hover:not(:disabled) {
   background-color: #f8fafc;
   color: #0f172a;
   border-color: #cbd5e1;
   box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
}

.org-jobs-btn-refresh:disabled {
   opacity: 0.6;
   cursor: not-allowed;
}

.org-jobs-btn-primary {
   background-color: #00A0C1 !important;
   border-color: #00A0C1 !important;
   color: #ffffff !important;
   border-radius: 8px !important;
   font-weight: 600;
   padding: 0 16px;
   height: 38px;
   font-size: 0.85rem;
   box-shadow: 0 2px 4px rgba(0, 160, 193, 0.18);
   transition: all 0.15s ease;
   text-decoration: none;
}

.org-jobs-btn-primary:hover {
   background-color: #008298 !important;
   border-color: #008298 !important;
   color: #ffffff !important;
   transform: translateY(-1px);
   box-shadow: 0 4px 8px rgba(0, 160, 193, 0.25);
}

.org-job-card {
   background: #ffffff;
   border: 1px solid #e2e8f0;
   border-radius: 16px;
   padding: 22px 24px !important;
   box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
   cursor: pointer;
   display: flex;
   flex-direction: column;
   transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.2s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.2s ease;
   position: relative;
   overflow: hidden;
}

.org-job-card:hover {
   transform: translateY(-3px);
   border-color: #cbd5e1;
   box-shadow: 0 12px 28px rgba(0, 160, 193, 0.12);
}

.org-job-card__icon-box {
   width: 42px;
   height: 42px;
   border-radius: 10px;
   background: #e6f7fa;
   color: #00A0C1;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 1.05rem;
   flex-shrink: 0;
}

.org-job-card__title {
   font-size: 1.02rem;
   font-weight: 700;
   color: #1e293b;
   margin-bottom: 4px;
   line-height: 1.35;
   letter-spacing: -0.01em;
}

.org-job-card__progress-box {
   background: #f8fafc;
   border: 1px solid #eef2f6;
   border-radius: 10px;
   padding: 12px 14px;
   margin-top: auto;
}

.org-job-card__cta {
   transition: color 0.2s ease;
}

.org-job-card__cta-arrow {
   transition: transform 0.2s ease;
}

.org-job-card:hover .org-job-card__cta {
   color: #008298 !important;
}

.org-job-card:hover .org-job-card__cta-arrow {
   transform: translateX(3px);
}

.aj-blueprint-badge {
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 3px 8px;
   background: #f8fafc;
   border: 1px solid #e2e8f0;
   border-radius: 6px;
   font-size: 0.8rem;
}

.aj-code-tag {
   display: inline-flex;
   align-items: center;
   padding: 2px 8px;
   font-size: 0.72rem;
   font-weight: 600;
   font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
   color: #475569;
   background: #f1f5f9;
   border: 1px solid #cbd5e1;
   border-radius: 4px;
   letter-spacing: 0.3px;
}

.aj-badge-status {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   gap: 5px;
   padding: 3px 10px;
   border-radius: 50px;
   font-size: 0.75rem;
   font-weight: 600;
   white-space: nowrap;
   border: 1px solid transparent;
   line-height: 1.3;
}

.aj-badge-dot {
   width: 6px;
   height: 6px;
   border-radius: 50%;
   background-color: currentColor;
   display: inline-block;
}

.aj-badge-status--pending {
   background: #FEF3C7;
   color: #D97706;
   border-color: #FDE68A;
}

.aj-badge-status--inprogress,
.aj-badge-status--in_progress {
   background: #E0F2FE;
   color: #0284C7;
   border-color: #BAE6FD;
}

.aj-badge-status--completed {
   background: #D1FAE5;
   color: #059669;
   border-color: #A7F3D0;
}

.aj-badge-status--onhold,
.aj-badge-status--on_hold {
   background: #FEF3C7;
   color: #D97706;
   border-color: #FDE68A;
}

.aj-badge-status--cancelled {
   background: #FEE2E2;
   color: #DC2626;
   border-color: #FECACA;
}

.aj-badge-health {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   padding: 3px 10px;
   border-radius: 50px;
   font-size: 0.75rem;
   font-weight: 600;
   white-space: nowrap;
   border: 1px solid transparent;
   line-height: 1.3;
}

.aj-badge-health--ontrack,
.aj-badge-health--on_track {
   background: #D1FAE5;
   color: #059669;
   border-color: #A7F3D0;
}

.aj-badge-health--behindplan,
.aj-badge-health--behind_plan,
.aj-badge-health--behind {
   background: #FEF3C7;
   color: #D97706;
   border-color: #FDE68A;
}

.aj-badge-health--overdue,
.aj-badge-health--atrisk,
.aj-badge-health--at_risk {
   background: #FEE2E2;
   color: #DC2626;
   border-color: #FECACA;
}

.aj-trigger-badge {
   display: inline-flex;
   align-items: center;
   padding: 3px 8px;
   border-radius: 6px;
   font-weight: 600;
   font-size: 0.72rem;
   border: 1px solid transparent;
}

.aj-trigger-badge--lead {
   background: #E0F2FE;
   color: #0284C7;
   border-color: #BAE6FD;
}

.aj-trigger-badge--contract {
   background: #D1FAE5;
   color: #059669;
   border-color: #A7F3D0;
}

.aj-trigger-badge--manual {
   background: #F8FAFC;
   color: #64748B;
   border-color: #E2E8F0;
}

.aj-progress-pill {
   font-size: 0.72rem;
   font-weight: 700;
   color: #008298;
   background: #e6f7fa;
   padding: 1px 8px;
   border-radius: 12px;
}

.aj-progress-track {
   height: 6px;
   background: #f1f5f9;
   border-radius: 4px;
   overflow: hidden;
}

.aj-progress-bar {
   height: 100%;
   background: #00A0C1;
   border-radius: 4px;
   transition: width 0.3s ease;
}

.metric-icon-circle {
   width: 42px;
   height: 42px;
   border-radius: 10px;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 1.15rem;
   flex-shrink: 0;
}

.org-job-filter-pill {
   background: #f1f5f9;
   color: #475569;
   border: 1px solid #e2e8f0;
   border-radius: 20px;
   padding: 4px 12px;
   font-size: 0.8rem;
   font-weight: 600;
   transition: all 0.15s ease;
}

.org-job-filter-pill:hover {
   background: #e2e8f0;
   color: #0f172a;
}

.org-job-filter-pill.active {
   background: #00A0C1 !important;
   color: #ffffff !important;
   border-color: #00A0C1 !important;
}

.org-job-filter-pill.active .badge {
   background: rgba(255, 255, 255, 0.25) !important;
   color: #ffffff !important;
}

.org-job-filter-pill--risk.active {
   background: #e11d48 !important;
   border-color: #e11d48 !important;
}

.shadow-xs {
   box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

/* Segmented Capsule Control Bar */
.aj-segmented-bar {
   background: #f8fafc;
   border: 1px solid #e2e8f0;
   border-radius: 12px;
}

.aj-segmented-label {
   font-size: 0.78rem;
   font-weight: 700;
   color: #475569;
   text-transform: uppercase;
   letter-spacing: 0.5px;
}

.aj-segmented-capsule {
   display: inline-flex;
   background: #f1f5f9;
   border-radius: 50px;
   padding: 3px;
   gap: 2px;
   border: 1px solid #e2e8f0;
}

.aj-segmented-pill {
   background: transparent;
   border: none;
   border-radius: 50px;
   padding: 4px 12px;
   font-size: 0.78rem;
   font-weight: 600;
   color: #64748b;
   display: inline-flex;
   align-items: center;
   gap: 5px;
   cursor: pointer;
   transition: all 0.15s ease-in-out;
}

.aj-segmented-pill:hover:not(.active) {
   color: #1e293b;
   background: rgba(255, 255, 255, 0.6);
}

.aj-pill-dot {
   width: 6px;
   height: 6px;
   border-radius: 50%;
   background: currentColor;
   opacity: 0.5;
}

.aj-segmented-pill.active .aj-pill-dot {
   opacity: 1;
}

.aj-segmented-pill.active {
   background: #ffffff;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.aj-segmented-pill.active--pending {
   color: #d97706;
   background: #fef3c7;
}

.aj-segmented-pill.active--in-progress {
   color: #0284c7;
   background: #e0f2fe;
}

.aj-segmented-pill.active--completed {
   color: #059669;
   background: #d1fae5;
}

.aj-segmented-pill.active--on-track {
   color: #059669;
   background: #d1fae5;
}

.aj-segmented-pill.active--behind {
   color: #d97706;
   background: #fef3c7;
}

.aj-segmented-pill.active--at-risk {
   color: #dc2626;
   background: #fee2e2;
}

/* Modal Summary Cards */
.aj-summary-card {
   border: 1px solid #e2e8f0 !important;
   border-radius: 12px !important;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
   transition: box-shadow 0.15s ease;
}

.aj-summary-card:hover {
   box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.aj-summary-icon {
   width: 28px;
   height: 28px;
   border-radius: 8px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 0.82rem;
   flex-shrink: 0;
}

.aj-summary-label {
   font-size: 0.72rem;
   font-weight: 700;
   color: #64748b;
   text-transform: uppercase;
   letter-spacing: 0.5px;
}

/* Operational Tasks Plan Item Styles */
.org-task-item {
   background: #ffffff;
   border: 1px solid #e2e8f0;
   border-radius: 12px !important;
   transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.org-task-item:hover {
   border-color: #cbd5e1;
   box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.org-task-item--completed {
   border-color: #bbf7d0 !important;
   background-color: #fcfdfc;
}

.aj-task-check-btn {
   width: 26px;
   height: 26px;
   min-width: 26px;
   border-radius: 50%;
   border: 2px solid #cbd5e1;
   background: #ffffff;
   color: #cbd5e1;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 0.75rem;
   cursor: pointer;
   transition: all 0.2s ease;
   padding: 0;
}

.aj-task-check-btn:hover:not(:disabled) {
   border-color: #00A0C1;
   color: #00A0C1;
   background: #e6f7fa;
}

.aj-task-check-btn.is-completed {
   border-color: #10b981;
   background: #10b981;
   color: #ffffff;
}

.aj-task-check-btn.is-in-progress {
   border-color: #0284c7;
   color: #0284c7;
   background: #e0f2fe;
}

.aj-task-check-btn--sub {
   width: 22px;
   height: 22px;
   min-width: 22px;
   font-size: 0.65rem;
}

.aj-task-num-pill {
   font-size: 0.72rem;
   font-weight: 700;
   font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
   color: #475569;
   background: #f1f5f9;
   border: 1px solid #e2e8f0;
   border-radius: 6px;
   padding: 2px 7px;
}

.aj-task-num-pill--sub {
   background: #e6f7fa;
   color: #008298;
   border-color: #bae6fd;
}

.aj-task-title {
   font-size: 0.92rem;
   font-weight: 600;
   color: #1e293b;
}

.aj-meta-pill {
   display: inline-flex;
   align-items: center;
   padding: 2px 8px;
   font-size: 0.72rem;
   font-weight: 600;
   background: #f8fafc;
   color: #475569;
   border: 1px solid #e2e8f0;
   border-radius: 6px;
}

.aj-meta-pill--role {
   background: #eff6ff;
   color: #1d4ed8;
   border-color: #bfdbfe;
}

.aj-meta-pill--gate {
   background: #fffbeb;
   color: #b45309;
   border-color: #fde68a;
}

.aj-meta-pill--target {
   background: #f0fdfa;
   color: #0f766e;
   border-color: #99f6e4;
}

.aj-task-status-select {
   min-width: 135px;
   width: auto;
   height: auto !important;
   line-height: 1.35 !important;
   padding: 6px 32px 6px 14px !important;
   font-size: 0.8rem !important;
   font-weight: 600;
   border-radius: 20px !important;
   border: 1px solid #cbd5e1;
   background-position: right 10px center;
   cursor: pointer;
   transition: all 0.15s ease-in-out;
}

.aj-task-status-select:focus {
   box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
   border-color: #00A0C1;
}

.aj-task-status-select--planned {
   background-color: #f8fafc;
   color: #475569;
   border-color: #e2e8f0;
}

.aj-task-status-select--inprogress,
.aj-task-status-select--in_progress {
   background-color: #e0f2fe;
   color: #0284c7;
   border-color: #bae6fd;
}

.aj-task-status-select--completed {
   background-color: #d1fae5;
   color: #059669;
   border-color: #a7f3d0;
}

.aj-task-status-select--blocked {
   background-color: #fee2e2;
   color: #dc2626;
   border-color: #fecaca;
}

.aj-task-status-select--skipped {
   background-color: #f1f5f9;
   color: #94a3b8;
   border-color: #cbd5e1;
}

.aj-task-status-select--sub {
   min-width: 125px;
   width: auto;
   height: auto !important;
   line-height: 1.35 !important;
   padding: 4px 28px 4px 10px !important;
   font-size: 0.76rem !important;
}

.aj-subtask-item {
   background: #f8fafc;
   border: 1px solid #e2e8f0;
   border-radius: 8px !important;
   transition: all 0.15s ease;
}

.aj-subtask-item:hover {
   background: #ffffff;
   border-color: #cbd5e1;
}

.aj-subtask-item--completed {
   border-color: #bbf7d0 !important;
   background-color: #fafffc;
}

/* Company Background Display & Modal */
.company-background-display {
   min-height: 220px;
   height: auto !important;
   max-height: none !important;
   overflow-y: visible;
   white-space: pre-wrap;
   word-break: break-word;
   line-height: 1.55;
   padding: 12px 14px;
   cursor: pointer;
   background: #ffffff;
   border: 1px solid #dee2e6;
   border-radius: 6px;
   transition: border-color 0.2s, box-shadow 0.2s;
}

.company-background-display:hover,
.company-background-display:focus {
   border-color: #00A0C1;
   box-shadow: 0 0 0 2px rgba(0, 160, 193, 0.15);
   outline: none;
}

.company-background-display ul,
.company-background-display ol {
   padding-left: 1.4rem;
   margin-bottom: 0.5rem;
   list-style-type: disc;
}

.company-background-display p {
   margin-bottom: 0.5rem;
}

.cb-modal-icon-badge {
   width: 34px;
   height: 34px;
   border-radius: 8px;
   background-color: #DAF3F9;
   color: #00A0C1;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 0.95rem;
   flex-shrink: 0;
}

.cb-modal-close-btn {
   width: 32px;
   height: 32px;
   border-radius: 50%;
   background: #f1f5f9;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 1.25rem;
   color: #64748b;
   transition: all 0.15s ease;
}

.cb-modal-close-btn:hover {
   background: #e2e8f0;
   color: #0f172a;
}

.cb-editor-shell {
   border: 1px solid #cbd5e1;
   border-radius: 10px;
   overflow: hidden;
   background: #ffffff;
   transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.cb-editor-shell:focus-within {
   border-color: #00A0C1;
   box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}

.cb-modern-toolbar {
   display: flex;
   align-items: center;
   gap: 6px;
   padding: 8px 12px;
   background: #f8fafc;
   border-bottom: 1px solid #e2e8f0;
}

.cb-modern-toolbar-btn {
   width: 32px;
   height: 32px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   background: #ffffff;
   border: 1px solid #e2e8f0;
   border-radius: 6px;
   color: #475569;
   font-size: 0.85rem;
   cursor: pointer;
   transition: all 0.15s ease;
}

.cb-modern-toolbar-btn:hover {
   background: #f0fdfa;
   border-color: #00A0C1;
   color: #008298;
}

.cb-toolbar-divider {
   width: 1px;
   height: 20px;
   background: #e2e8f0;
   margin: 0 2px;
}

.cb-modern-editor {
   padding: 14px 16px;
   min-height: 280px;
   max-height: 55vh;
   overflow-y: auto;
   font-size: 0.95rem;
   line-height: 1.6;
   color: #1e293b;
   outline: none;
   background: #ffffff;
   text-align: left;
   white-space: pre-wrap;
   word-break: break-word;
}

.cb-modern-editor ul,
.cb-modern-editor ol {
   margin-top: 0;
   margin-bottom: 8px;
   padding-left: 1.4rem;
   list-style-type: disc !important;
}

.cb-modern-editor li {
   margin-bottom: 4px;
}

.cb-modern-editor p {
   margin-bottom: 0.5rem;
}

@keyframes fadeIn {
   from { opacity: 0; transform: scale(0.95); }
   to { opacity: 1; transform: scale(1); }
}
</style>